/**
 * Call Center UI strings — English (en), Arabic (ar), and Urdu (ur).
 * Translation sheets: locales/call-center-translation-sheet.csv (ar),
 * locales/call-center-urdu-translation-sheet.csv (ur).
 */
const callCenterTranslations = {
  en: {
    // Navigation
    ccMenuTickets: "Tickets",
    ccMenuAccountPlans: "Payment Plans",
    ccMenuAccountPlanTypes: "Plan Templates",
    ccMenuAgentWorkspace: "Ticket Entry",
    ccMenuTicketBulkUpload: "Bulk Upload",
    ccMenuTicketUploadHistory: "Upload History",
    ccMenuTicketColumns: "Column Settings",
    ccMenuTicketSettingsMerged: "Ticket Settings",
    ccTicketSettingsPageTitle: "Ticket Settings",
    ccTicketSettingsPageSubtitle: "Configure ticket columns and status workflow for your organization.",
    ccTicketSettingsTabColumns: "Columns",
    ccTicketSettingsTabStatuses: "Statuses",
    ccTicketSettingsNoTabAccess: "You do not have permission to view ticket settings.",
    ccAccountPlansPageTitle: "Payment Plans",
    ccAccountPlansPageSubtitle: "Schedule installment collections and automated payment reminder tickets per account.",
    ccAccountPlansManageTypes: "Manage plan templates",
    ccAccountPlanType: "Plan template",
    ccAccountPlanSelectType: "Select a plan template",
    ccAccountPlanTypeRequired: "Plan template is required",
    ccAccountPlanTypeSelectHint: "Schedule, assignment, reminders, and ticket defaults come from the selected template.",
    ccAccountPlanTypesPageTitle: "Plan Templates",
    ccAccountPlanTypesPageSubtitle: "Define reusable schedule templates for payment plans.",
    ccAccountPlanTypesBackToPlans: "Back to payment plans",
    ccAccountPlanTypeCreate: "Create template",
    ccAccountPlanTypeCreateTitle: "Create plan template",
    ccAccountPlanTypeEditTitle: "Edit plan template",
    ccAccountPlanTypeFormHint: "Template settings apply to every payment plan created from this template.",
    ccAccountPlanTypeName: "Template name",
    ccAccountPlanTypeDescription: "Description",
    ccAccountPlanTypeNameRequired: "Template name is required",
    ccAccountPlanTypeDefaultAgent: "Default call agent",
    ccAccountPlanTypeDefaultAgentOptional: "Choose at plan creation",
    ccAccountPlanTypeDefaultAgentHint: "Optional. Leave empty to pick the agent when creating each payment plan.",
    ccAccountPlanTypeActive: "Active (available for new payment plans)",
    ccAccountPlanTypesEmptyTitle: "No plan templates yet",
    ccAccountPlanTypesEmptyHint: "Create a template to standardize installment schedules before adding payment plans.",
    ccAccountPlanTypeCreated: "Plan template created",
    ccAccountPlanTypeUpdated: "Plan template updated",
    ccAccountPlanTypeLoadFailed: "Failed to load plan templates",
    ccAccountPlanTypeSaveFailed: "Failed to save plan template",
    ccAccountPlanTypeInfoTitle: "How plan templates work",
    ccAccountPlanTypeInfoBody:
      "Templates define installment frequency, reminders, and ticket defaults. Each payment plan picks a template and adds account-specific amount and dates.",
    ccAccountPlanTypeKpiTotal: "Total templates",
    ccAccountPlanTypeKpiActive: "Active templates",
    ccAccountPlanTypeKpiInUse: "In use",
    ccAccountPlanTypeUsage: "Usage",
    ccAccountPlanTypeUsageHint: "Active / total payment plans",
    ccAccountPlanTypeSearchPlaceholder: "Search by template name…",
    ccAccountPlanTypeDetailSubtitle: "Template configuration and usage",
    ccAccountPlanTypeDeactivateBlocked: "Cannot deactivate while active payment plans use this template.",
    ccAccountPlanTypeUsageSummary: "Used by {count} payment plan(s).",
    ccAccountPlanTypeResultsLabel: "templates",
    ccAccountPlanTypeScheduleSummary: "{frequency} · {count} installments",
    ccAccountPlanTypeScheduleColumn: "Schedule",
    ccAccountPlanTypeEmptyFilteredTitle: "No templates match your filters",
    ccAccountPlanTypeEmptyFilteredHint: "Try adjusting search or status filters.",
    ccAccountPlanTypeKpiClickHint: "Click a card to filter the list.",
    ccAccountPlanTypeDetailScheduleSection: "Installment schedule",
    ccAccountPlanTypeDetailTicketSection: "Ticket defaults",
    ccAccountPlanTypeDetailUsageSection: "Usage",
    ccAccountPlanTypeUsageEmpty: "Not used by any payment plans yet.",
    ccAccountPlanTypeInUseWarning:
      "This template is used by {count} payment plan(s). Changes apply to future installments only.",
    ccAccountPlanTypeBreadcrumbPaymentPlans: "Payment Plans",
    ccAccountPlanTypeFormSectionBasic: "Basic information",
    ccAccountPlanTypeFormSectionSchedule: "Installment schedule",
    ccAccountPlanTypeFormSectionTickets: "Ticket defaults",
    ccAccountPlanTypeFormPreviewTitle: "Template summary",
    ccAccountPlanTypeFormPreviewBody: "Reminder {reminder} · {assignment} · priority {priority}",
    ccAccountPlanCreate: "Create payment plan",
    ccAccountPlanCreateTitle: "Create payment plan",
    ccAccountPlanEditTitle: "Edit payment plan",
    ccAccountPlanDetailTitle: "Payment plan details",
    ccAccountPlanAccount: "Account number",
    ccAccountPlanMobileNumbers: "Mobile number(s)",
    ccAccountPlanAmount: "Installment amount (OMR)",
    ccAccountPlanTotalAmount: "Total amount (OMR)",
    ccAccountPlanAmountColumn: "Amount",
    ccAccountPlanTotalAmountRequired: "Total amount is required",
    ccAccountPlanPerInstallmentHint: "Approximately {amount} OMR per installment before rounding",
    ccAccountPlanPreviewSchedule: "Preview schedule",
    ccAccountPlanPreviewTitle: "Installment preview",
    ccAccountPlanPreviewEmpty: "Enter total amount, installment count, and start date to preview the schedule.",
    ccAccountPlanPreviewTotal: "Total: {amount} OMR",
    ccAccountPlanInstallmentCount: "Number of installments",
    ccAccountPlanFrequency: "Frequency",
    ccAccountPlanFrequency_ONE_TIME: "One-time payment",
    ccAccountPlanOneTimeNote: "One-time plans create a single payment ticket on the due date.",
    ccAccountPlanStartDate: "Start date (first due date)",
    ccAccountPlanAssignmentMode: "Agent assignment",
    ccAccountPlanAssignedAgent: "Assigned call agent",
    ccAccountPlanAutoAssign: "Auto-assign",
    ccAccountPlanSelectAgent: "Select call agent",
    ccAccountPlanReminderOffset: "Payment reminder",
    ccAccountPlanTicketPriority: "Ticket priority",
    ccAccountPlanRemarksTemplate: "Default ticket remarks",
    ccAccountPlanAmountRequired: "Installment amount is required",
    ccAccountPlanAmountMin: "Installment amount must be greater than zero",
    ccAccountPlanFrequencyRequired: "Frequency is required",
    ccAccountPlanInstallmentCountRequired: "Number of installments is required",
    ccAccountPlanInstallmentCountMax: "Number of installments cannot exceed 120",
    ccAccountPlanStartDateRequired: "Start date is required",
    ccAccountPlanAssignedAgentRequired: "Assigned call agent is required",
    ccAccountPlanProgress: "Progress",
    ccAccountPlanNextDue: "Next due",
    ccAccountPlanStatus: "Status",
    ccAccountPlanActions: "Actions",
    ccAccountPlanView: "View",
    ccAccountPlanEdit: "Edit",
    ccAccountPlanCancel: "Cancel plan",
    ccAccountPlanCancelConfirm: "Cancel this payment plan and any pending installments?",
    ccAccountPlanCancelFailed: "Failed to cancel payment plan",
    ccAccountPlanInstallmentSchedule: "Installment schedule",
    ccAccountPlanDueDate: "Due date",
    ccAccountPlanReminderDate: "Reminder date",
    ccAccountPlanTicket: "Ticket",
    ccAccountPlanOpenTicket: "Open ticket",
    ccAccountPlansEmpty: "No payment plans found.",
    ccAccountPlansLoadFailed: "Failed to load payment plans",
    ccAccountPlansSaveFailed: "Failed to save payment plan",
    ccAccountPlanFilterStatus: "Status",
    ccAccountPlanFilterAll: "All",
    ccAccountPlanFiltersTitle: "Filters",
    ccAccountPlanResultsLabel: "payment plans",
    ccAccountPlanInstallmentsLabel: "installments",
    ccAccountPlansEmptyTitle: "No payment plans yet",
    ccAccountPlansEmptyHint: "Create a payment plan to schedule recurring collection tickets and reminders for an account.",
    ccClearFilters: "Clear filters",
    ccListFiltersTitle: "Filters",
    ccAlertsResultsLabel: "alerts",
    ccAlertsEmptyTitle: "No alerts found",
    ccAlertsEmptyHint: "Try adjusting your filters or check back later for new SLA warnings.",
    ccNotificationsResultsLabel: "notifications",
    ccNotificationsEmptyTitle: "No notifications yet",
    ccNotificationsUnreadOnly: "Unread only",
    ccNotificationsColumnTitle: "Title",
    ccNotificationsColumnMessage: "Message",
    ccNotificationsColumnDate: "Date",
    ccRead: "Read",
    ccUnread: "Unread",
    ccOpen: "Open",
    ccAccountPlanStatus_ACTIVE: "Active",
    ccAccountPlanStatus_COMPLETED: "Completed",
    ccAccountPlanStatus_CANCELLED: "Cancelled",
    ccAccountPlanStatus_SCHEDULED: "Scheduled",
    ccAccountPlanStatus_TICKET_CREATED: "Ticket created",
    ccAccountPlanStatus_SKIPPED: "Skipped",
    ccMenuInbox: "Inbox",
    ccMenuBrowserPhone: "Browser Phone",
    ccMenuCampaigns: "Campaigns",
    ccMenuAnalytics: "Dashboard",
    ccMenuSettings: "Settings",
    ccMenuTicketSettings: "Status Settings",
    ccMenuWhatsAppSettings: "WhatsApp Settings",
    ccMenuPaymentConfirmationSettings: "Payment Confirmation",
    ccMenuOrganizations: "Organizations",
    ccMenuOrganizationsTooltip: "Manage platform organizations",
    ccDisabled: "Disabled",
    ccOrgPageTitle: "Organizations",
    ccOrgTableOrganization: "Organization",
    ccOrgStatTotal: "Total organizations",
    ccOrgStatTotalHint: "Across all pages matching your filters",
    ccOrgStatActive: "Active on page",
    ccOrgStatActiveHint: "Of {count} rows shown",
    ccOrgStatCalling: "Calling enabled",
    ccOrgStatCallingHint: "On this page ({count} rows)",
    ccOrgPageSubtitle: "Create and manage tenant organizations, features, and integration settings.",
    ccOrgCreate: "New organization",
    ccOrgInfoTitle: "Platform organizations",
    ccOrgInfoBody:
      "Each organization is an isolated tenant with its own users, tickets, and integrations. Use the action icons to configure payment proof templates, payment confirmation, and WhatsApp per organization.",
    ccOrgLoadFailed: "Failed to load organizations",
    ccOrgResultsLabel: "organizations",
    ccOrgSearchPlaceholder: "Search by code or name...",
    ccOrgSortBy: "Sort by",
    ccOrgSortDirection: "Direction",
    ccOrgSortAsc: "Ascending",
    ccOrgSortDesc: "Descending",
    ccOrgSortCode: "Code",
    ccOrgSortName: "Name",
    ccOrgSortCreated: "Created date",
    ccOrgIncludeInactive: "Include inactive",
    ccOrgPerPage: "per page",
    ccOrgEmptyTitle: "No organizations yet",
    ccOrgEmptyHint: "Create the first organization to onboard a new tenant.",
    ccOrgEmptyFilteredTitle: "No organizations match your filters",
    ccOrgEmptyFilteredHint: "Try a different search term or include inactive organizations.",
    ccOrgCode: "Code",
    ccOrgName: "Name",
    ccOrgCalling: "Calling",
    ccOrgAiCalling: "AI calling",
    ccOrgCreatedAt: "Created",
    ccOrgPaymentProofTemplate: "Payment proof template",
    ccOrgPaymentConfirmation: "Payment confirmation",
    ccOrgWhatsApp: "WhatsApp settings",
    ccOrgTelephony: "Telephony provider",
    ccTelephonyProvider: "Telephony provider",
    ccTelephonyProviderD2c: "D2C Cloud PBX",
    ccTelephonyProviderGrandstream: "Grandstream UCM",
    ccTelephonyD2cSection: "Cloud PBX overrides",
    ccTelephonyD2cHint: "Leave blank to use server environment defaults.",
    ccTelephonyD2cBaseUrl: "PBX base URL",
    ccTelephonyD2cAccessId: "Access ID",
    ccTelephonyD2cAccessKey: "Access key",
    ccTelephonyGrandstreamSection: "Grandstream UCM connection",
    ccTelephonyGrandstreamDesktopHint:
      "Agent calling uses the Emmad desktop app with native SIP (UDP/TCP to UCM), like Wave Desktop. Set SIP domain to the UCM realm; LAN IP from base URL is used for registration when present.",
    ccTelephonyGsBaseUrl: "UCM API base URL",
    ccTelephonyGsUsername: "API username",
    ccTelephonyGsPassword: "API password",
    ccTelephonyGsRecordingBaseUrl: "Recording base URL",
    ccTelephonyGsSipDomain: "SIP domain",
    ccTelephonyGsSipWssUrl: "SIP WSS URL",
    ccTelephonyGsSipWsUrl: "SIP WS URL (optional)",
    ccTelephonyGsCdrEndpoint: "CDR endpoint path",
    ccTelephonyGsCdrSyncEnabled: "Enable scheduled CDR sync",
    ccTelephonyGsWebrtcEnabled: "Enable browser WebRTC",
    ccTelephonyGsWebhookSecret: "Event webhook secret",
    ccTelephonyGsSipDomainRequired: "SIP domain is required for Grandstream",
    ccTelephonyGsSipWssRequired: "SIP WSS or WS URL is required for Grandstream",
    ccTelephonySecretConfigured: "A value is already saved; leave blank to keep it.",
    ccTelephonyLoadFailed: "Failed to load telephony configuration",
    ccTelephonySaveSuccess: "Telephony configuration saved",
    ccTelephonySaveFailed: "Failed to save telephony configuration",
    ccOrgDeactivate: "Deactivate",
    ccOrgDeactivateTitle: "Deactivate organization",
    ccOrgDeactivateConfirm: "Deactivate \"{name}\"? Users will no longer be able to sign in under this organization.",
    ccOrgAccessDenied: "Only platform super-admins can manage organizations.",
    ccOrgLogoUploaded: "Organization logo uploaded",
    ccOrgLogoUploadFailed: "Failed to upload organization logo",
    ccOrgNameRequired: "Organization name is required",
    ccOrgCodeRequired: "Organization code is required",
    ccOrgCodeInvalid: "Code must use uppercase letters, numbers, underscore, or hyphen",
    ccOrgCodePlaceholder: "ACME",
    ccOrgNamePlaceholder: "Acme Collections",
    ccOrgCreateSuccess: "Organization created",
    ccOrgUpdated: "Organization updated",
    ccOrgSaveFailed: "Failed to save organization",
    ccOrgDeactivated: "Organization deactivated",
    ccOrgDeactivateFailed: "Failed to deactivate organization",
    ccOrgTemplateLoadFailed: "Failed to load payment proof template",
    ccOrgTemplateSaved: "Payment proof template saved",
    ccOrgTemplateSaveFailed: "Failed to save payment proof template",
    ccOrgCreateTitle: "Create organization",
    ccOrgEditTitle: "Edit organization",
    ccOrgCreateFormHint: "Choose a unique tenant code. It cannot be changed after creation.",
    ccOrgEditFormHint: "Update organization details, calling features, and logo.",
    ccOrgCodeHint: "Uppercase letters, numbers, underscore, or hyphen (e.g. ACME_CO)",
    ccOrgPageSize: "Page size",
    ccOrgRetiredCode: "Retired code",
    ccOrgDefaultProtected: "Default platform organization cannot be deactivated",
    ccOrgActionsLegend: "Row actions: payment proof template, payment confirmation, WhatsApp, edit, deactivate",
    ccOrgActive: "Active",
    ccOrgCallingFeatures: "Calling features",
    ccOrgSectionIdentity: "Organization details",
    ccOrgSectionIdentityHint: "Code is permanent after create; name and status can be updated anytime.",
    ccOrgSectionBranding: "Branding",
    ccOrgCallingChannelsHint: "Control where agents can place calls. Telephony PBX settings are configured separately.",
    ccOrgCallingEnabled: "Enable manual calling",
    ccOrgCallingMasterHint: "Master switch for call logs, campaigns, recordings, and agent dialers.",
    ccOrgWebCallingEnabled: "Web calling",
    ccOrgWebCallingHint: "Browser phone and WebRTC (SIP over WSS) in the web app.",
    ccOrgDesktopCallingEnabled: "Desktop calling",
    ccOrgDesktopCallingHint: "WASL desktop app with native SIP (UDP/TCP), like Wave Desktop.",
    ccOrgAiCallingEnabled: "AI automated calling",
    ccOrgAiCallingHint: "Outbound AI voice campaigns for this organization.",
    ccOrgCallingMasterOffNote: "Turn on manual calling above to enable web, desktop, or AI channels.",
    ccOrgActiveHint: "Inactive organizations cannot be selected by agents.",
    ccOrgLogoFormatsHint: "PNG, JPG, GIF, or WebP. Shown on payment proof documents.",
    ccOrgTelephonyProviderUnset: "Telephony not configured",
    ccOrgChannelsWeb: "Web",
    ccOrgChannelsDesktop: "Desktop",
    ccOrgLogo: "Logo",
    ccOrgLogoHint: "Shown on payment proof documents. PNG or JPG recommended.",
    ccOrgNoLogo: "No logo uploaded",
    ccOrgLogoUploading: "Uploading...",
    ccOrgLogoUpload: "Upload logo",
    ccPaymentConfirmationSettingsTitle: "Payment Confirmation",
    ccPaymentConfirmationSettingsSubtitle: "Configure how your organization notifies the call center when customers pay (webhook, REST poll, or message queue).",
    ccPaymentConfirmationEnabled: "Enable payment confirmation for this organization",
    ccPaymentConfirmationProvider: "Integration mode",
    ccPaymentConfirmationProviderWebhook: "Webhook (org pushes payment events)",
    ccPaymentConfirmationProviderRestPoll: "REST API poll (we check org API by account no)",
    ccPaymentConfirmationProviderMessageQueue: "Message queue (org-provided broker)",
    ccPaymentConfirmationWebhookSecret: "Webhook secret",
    ccPaymentConfirmationWebhookUrlLabel: "Register this webhook URL with your payment system",
    ccPaymentConfirmationWebhookHint: "POST signed JSON with account number and payment details when a customer pays.",
    ccPaymentConfirmationPollBaseUrl: "Poll API base URL",
    ccPaymentConfirmationPollPathTemplate: "Poll path template",
    ccPaymentConfirmationPollAuthType: "Poll auth type",
    ccPaymentConfirmationPollAuthSecret: "Poll auth secret",
    ccPaymentConfirmationPollInterval: "Poll interval (minutes)",
    ccPaymentConfirmationMqBrokerType: "Message broker type",
    ccPaymentConfirmationMqConfigJson: "MQ connection JSON",
    ccPaymentConfirmationMqConfigHint: "Provide host, port, username, password, virtualHost, and queue as JSON.",
    ccPaymentConfirmationFieldMapping: "Payload field mapping",
    ccPaymentConfirmationSave: "Save payment confirmation settings",
    ccPaymentConfirmationSaveSuccess: "Payment confirmation settings saved",
    ccPaymentConfirmationSaveFailed: "Failed to save payment confirmation settings",
    ccPaymentConfirmationLoadFailed: "Failed to load payment confirmation settings",
    ccPaymentConfirmationTestConnection: "Test connection",
    ccPaymentConfirmationTestSuccess: "Connection test passed",
    ccPaymentConfirmationTestFailed: "Connection test failed",
    ccPaymentConfirmationInfoTitle: "Payment confirmed",
    ccPaymentConfirmationInfoBadge: "Confirmed",
    ccPaymentConfirmationInfoSource: "Source",
    ccPaymentConfirmationInfoReference: "Reference",
    ccPaymentConfirmationInfoAmount: "Amount",
    ccPaymentConfirmationInfoConfirmedAt: "Confirmed at",
    ccPaymentConfirmationSourceWebhook: "Webhook",
    ccPaymentConfirmationSourceRestPoll: "REST poll",
    ccPaymentConfirmationSourceMessageQueue: "Message queue",
    ccSecretConfiguredHint: "Already configured — leave blank to keep current value",
    ccPaymentConfirmationDisableConfirm:
      "Disabling payment confirmation stops automatic ticket updates. Continue?",
    ccPaymentConfirmationProviderChangeConfirm:
      "Changing integration mode may require updating your external system. Continue?",
    ccPaymentConfirmationInvalidMqJson: "Invalid MQ connection JSON",
    ccPaymentConfirmationPollBaseUrlRequired: "Poll base URL is required when enabled",
    ccPaymentConfirmationPollBaseUrlInvalid: "Enter a valid poll base URL (https://...)",
    ccPaymentConfirmationPollPathRequired: "Poll path template is required when enabled",
    ccPaymentConfirmationPollIntervalInvalid: "Poll interval must be between 1 and 1440 minutes",
    ccPaymentConfirmationWebhookSecretRequired: "Webhook secret is required when enabled",
    ccPaymentConfirmationMqConfigRequired: "MQ connection JSON is required when enabled",
    ccPaymentConfirmationFieldAccountNo: "Account number field",
    ccPaymentConfirmationFieldAmount: "Amount field",
    ccPaymentConfirmationFieldReference: "Reference field",
    ccPaymentConfirmationFieldPaidAt: "Paid-at field",
    ccPaymentConfirmationFieldIsPaid: "Is-paid field",
    ccPaymentConfirmationFieldMappingHint:
      "Map JSON keys from your payment payload to the fields the call center expects.",
    ccPaymentConfirmationPayloadExampleTitle: "Example webhook payload",
    ccPaymentConfirmationPayloadExampleHint:
      "Your system should POST JSON like this. Field names can differ if mapped above.",
    ccPaymentConfirmationCopyUrl: "Copy URL",
    ccPaymentConfirmationUrlCopied: "URL copied to clipboard",
    ccPaymentConfirmationCopyUrlFailed: "Could not copy URL",
    ccPaymentConfirmationEventLogTitle: "Recent integration events",
    ccPaymentConfirmationEventLogSubtitle:
      "Audit trail of payment confirmation attempts — useful for debugging failed or unmatched payments.",
    ccPaymentConfirmationEventLogEmpty: "No payment confirmation events yet",
    ccPaymentConfirmationEventLogEmptyHint:
      "Events appear here when your system sends payment notifications or when polling runs.",
    ccPaymentConfirmationEventLogLoadFailed: "Failed to load payment confirmation events",
    ccPaymentConfirmationEventProcessed: "Applied",
    ccPaymentConfirmationEventFailed: "Failed",
    ccPaymentConfirmationEventSkipped: "Skipped",
    ccPaymentConfirmationEventAccountNo: "Account no.",
    ccPaymentConfirmationEventReference: "Reference",
    ccPaymentConfirmationEventProvider: "Provider",
    ccPaymentConfirmationEventCreatedAt: "Received at",
    ccPaymentConfirmationEventError: "Details",
    ccPaymentConfirmationEventResultsLabel: "events",
    ccMenuNotifications: "Notifications",
    ccMenuPaymentReminders: "Payment reminders",
    ccMenuPaymentConfirmations: "Payment confirmations",

    // Notifications
    ccNotificationsTitle: "Notifications",
    ccNotificationsSubtitleOne: "1 unread notification",
    ccNotificationsSubtitleMany: "{count} unread notifications",
    ccNotificationsEmpty: "No notifications yet.",
    ccNotificationsMarkAllRead: "Mark all as read",
    ccNotificationsMarkAllReadSuccess: "All notifications marked as read",
    ccNotificationsMarkAllReadFailed: "Failed to mark all notifications as read",
    ccNotificationsMarkReadFailed: "Failed to mark notification as read",
    ccNotificationsLoadFailed: "Failed to load notifications",
    ccNotificationsPaymentAlertTitle: "Payment due today",
    ccNotificationsPaymentConfirmedTitle: "Payment confirmed",
    ccNotificationsRemindersSection: "Payment reminders",
    ccNotificationsConfirmationsSection: "Payment confirmations",
    ccNotificationsRemindersEmpty: "No payment reminders yet.",
    ccNotificationsConfirmationsEmpty: "No payment confirmations yet.",
    ccNotificationsSectionUnreadOne: "1 unread",
    ccNotificationsSectionUnreadMany: "{count} unread",

    ccWhatsAppSettingsTitle: "WhatsApp Settings",
    ccWhatsAppSettingsSubtitle: "Configure WhatsApp credentials for this organization (Direct Meta or Flowmatical).",
    ccWhatsAppProvider: "Provider",
    ccWhatsAppProviderMetaDirect: "Direct Meta API",
    ccWhatsAppProviderFlowmatical: "Flowmatical",
    ccWhatsAppFlowmaticalBaseUrl: "Flowmatical base URL",
    ccWhatsAppFlowmaticalBaseUrlRequired: "Flowmatical base URL is required when WhatsApp is enabled",
    ccWhatsAppFlowmaticalApiToken: "Flowmatical API token",
    ccWhatsAppFlowmaticalWebhookSecret: "Flowmatical webhook secret",
    ccWhatsAppFlowmaticalWebhookUrlLabel: "Register this inbound webhook URL in Flowmatical",
    ccWhatsAppFlowmaticalWebhookHint: "In Flowmatical go to Settings > Integrations & API > Signed webhooks, paste this URL, enable delivery, and copy the whsec_ signing secret shown once. Do not point Meta callbacks here — Flowmatical manages Meta at platform level.",
    ccWhatsAppFlowmaticalApiTokenHint: "Create a scoped flw_ token with messages:send (and templates:read if needed) under Settings > Integrations & API > API tokens.",
    ccWhatsAppFlowmaticalBaseUrlHint: "API host only (default https://api.flowmatical.com). Do not include /api/v1/integrations/public.",
    ccWhatsAppEnabled: "Enable WhatsApp for this organization",
    ccWhatsAppPhoneNumberId: "Phone number ID",
    ccWhatsAppPhoneNumberIdRequired: "Phone number ID is required when WhatsApp is enabled",
    ccWhatsAppAccessToken: "Access token",
    ccWhatsAppWebhookVerifyToken: "Webhook verify token",
    ccWhatsAppAppSecret: "App secret",
    ccWhatsAppApiVersion: "Graph API version",
    ccWhatsAppApiVersionRequired: "API version is required",
    ccWhatsAppSecretConfiguredHint: "Already configured — leave blank to keep current value",
    ccWhatsAppSettingsHint: "Secrets are stored per organization. Leave secret fields blank to keep existing values.",
    ccWhatsAppSave: "Save WhatsApp settings",
    ccWhatsAppSaveSuccess: "WhatsApp settings saved",
    ccWhatsAppSaveFailed: "Failed to save WhatsApp settings",
    ccWhatsAppLoadFailed: "Failed to load WhatsApp settings",

    // Shared
    ccEmDash: "—",
    ccBack: "← Back",
    ccProfileTitle: "My Profile",
    ccProfileSubtitle: "View and update your account details.",
    ccProfileName: "Full name",
    ccProfileEmail: "Email address",
    ccProfileRole: "Role",
    ccProfileOrganization: "Organization",
    ccProfileAgentExtension: "Agent extension",
    ccProfileSave: "Save profile",
    ccProfileUpdated: "Profile updated",
    ccProfileLoadFailed: "Failed to load profile",
    ccProfileUpdateFailed: "Failed to update profile",
    ccProfileChangePassword: "Change password",
    ccProfileChangePasswordHint: "Use a strong password with upper and lower case letters, a number, and a special character.",
    ccCurrentPassword: "Current password",
    ccNewPassword: "New password",
    ccConfirmPassword: "Confirm new password",
    ccCurrentPasswordRequired: "Current password is required",
    ccNewPasswordRequired: "New password is required",
    ccConfirmPasswordRequired: "Please confirm your new password",
    ccPasswordComplexity: "Password must be at least 8 characters and include upper, lower, number, and special character",
    ccPasswordsMustMatch: "Passwords must match",
    ccPasswordChanged: "Password changed successfully",
    ccPasswordChangeFailed: "Failed to change password",
    ccInvalidEmail: "Enter a valid email address",
    ccEmailRequired: "Email is required",
    ccShowPassword: "Show password",
    ccHidePassword: "Hide password",
    ccClose: "Close",
    ccReset: "Reset",
    ccLoading: "Loading...",
    ccSaving: "Saving...",
    ccDeleting: "Deleting...",
    ccRefreshing: "Refreshing...",
    ccProcessing: "Processing...",
    ccAll: "All",
    ccUnknown: "Unknown",
    ccUserFallback: "User",
    ccAccountNo: "Account No",
    ccMobileNumber: "Mobile Number",
    ccCustomerName: "Customer Name",
    ccAccountNumber: "Account number",
    ccAccountNumberRequired: "Account number is required",
    ccPhoneNumber: "Phone number",
    ccPhoneNumberRequired: "Phone number is required",
    ccStatus: "Status",
    ccPriority: "Priority",
    ccPriorityRequired: "Priority is required",
    ccRemarks: "Remarks",
    ccOutstanding: "Outstanding",
    ccPrevious: "Previous",
    ccNext: "Next",
    ccRows: "Rows",
    ccActions: "Actions",

    // WhatsApp
    ccWhatsApp: "WhatsApp",
    ccWhatsAppChat: "WhatsApp Chat",
    ccWhatsAppOpenChat: "Open WhatsApp chat",
    ccWhatsAppNoPhone: "No mobile number on this ticket",
    ccWhatsAppSend: "Send",
    ccWhatsAppSendFailed: "Failed to send WhatsApp message",
    ccWhatsAppMessageSent: "WhatsApp message sent",
    ccWhatsAppEmpty: "No WhatsApp messages yet. Send a message to start the conversation.",
    ccWhatsAppComposePlaceholder: "Type a WhatsApp message...",
    ccWhatsAppChatWith: "Chat with",
    ccWhatsAppThreadLoadFailed: "Failed to load WhatsApp messages",
    ccWhatsAppStatusSent: "Sent",
    ccWhatsAppStatusDelivered: "Delivered",
    ccWhatsAppStatusRead: "Read",
    ccWhatsAppStatusFailed: "Failed",
    ccWhatsAppStatusPending: "Pending",
    ccWhatsAppSelectNumber: "Select mobile number",
    ccWhatsAppCheckingNumber: "Checking WhatsApp number...",
    ccWhatsAppNumberValid: "Registered on WhatsApp",
    ccWhatsAppNumberInvalidHint: "This number is not registered on WhatsApp. Messages may fail to deliver.",
    ccWhatsAppCheckFailed: "Could not verify WhatsApp number",
    ccWhatsAppAttachFile: "Attach file",
    ccWhatsAppSelectedFile: "Selected file",
    ccWhatsAppAttachment: "Attachment",
    ccWhatsAppDownload: "Download",
    ccWhatsAppDownloadFailed: "Failed to download attachment",
    ccWhatsAppNoPhoneHint: "Add a mobile number to the ticket to start WhatsApp messaging.",
    ccWhatsAppEmptyTitle: "No messages yet",
    ccWhatsAppComposerHint: "Press Enter to send. Shift+Enter for a new line. Max attachment size 16 MB.",
    ccWhatsAppFileTooLarge: "Attachment must be 16 MB or smaller.",
    ccWhatsAppMetaWebhookUrlLabel: "Register this callback URL in Meta Developer Dashboard",
    ccWhatsAppMetaWebhookHint:
      "In Meta Developer Dashboard → WhatsApp → Configuration, set the Callback URL to this address and use the same webhook verify token configured above.",
    ccWhatsAppCopyUrl: "Copy URL",
    ccWhatsAppUrlCopied: "URL copied to clipboard",

    ccTicketAttachments: "Ticket attachments",
    ccTicketAttachFile: "Attach file",
    ccTicketAttachmentsHint: "Upload documents, images, or other files related to this ticket.",
    ccTicketAttachmentsEmpty: "No files attached to this ticket yet.",
    ccTicketAttachmentUploaded: "File attached to ticket",
    ccTicketAttachmentUploadFailed: "Failed to attach file",
    ccTicketAttachmentDownload: "Download",
    ccTicketAttachmentDownloadFailed: "Failed to download file",
    ccTicketAttachmentsLoadFailed: "Failed to load ticket attachments",
    ccConfirmDeleteAttachment: "Delete this attachment? This cannot be undone.",
    ccTicketAttachmentDeleted: "Attachment deleted",
    ccTicketAttachmentDeleteFailed: "Failed to delete attachment",

    ccPaymentProof: "Payment proof",
    ccPaymentProofOpen: "Payment proof",
    ccPaymentProofHint: "Generate a clearance certificate PDF for the customer after payment is verified. Edit the fields, save the PDF to this ticket, then download or send it via WhatsApp.",
    ccPaymentProofLoadFailed: "Failed to load payment proof",
    ccPaymentProofDraftSaved: "Payment proof draft saved",
    ccPaymentProofDraftSaveFailed: "Failed to save payment proof draft",
    ccPaymentProofSaved: "Payment proof saved",
    ccPaymentProofSaveFailed: "Failed to save payment proof",
    ccPaymentProofDownloadFailed: "Failed to download payment proof",
    ccPaymentProofPreview: "Preview PDF",
    ccPaymentProofSaveDraft: "Save draft",
    ccPaymentProofSaveDownload: "Save & download PDF",
    ccPaymentProofDownloadSaved: "Download saved PDF",
    ccPaymentProofSendWhatsApp: "Send via WhatsApp",
    ccPaymentProofWhatsAppSent: "Payment proof sent via WhatsApp",
    ccPaymentProofWhatsAppFailed: "Failed to send payment proof via WhatsApp",
    ccPaymentProofWhatsAppMessage: "WhatsApp message",
    ccPaymentProofWhatsAppPhone: "Customer WhatsApp number",
    ccPaymentProofPhoneRequired: "Select or enter a WhatsApp phone number",
    ccPaymentProofGeneratedAt: "Last generated",
    ccPaymentProofTemplateTitle: "Payment proof template",
    ccPaymentProofTemplatePageHint: "Configure the clearance certificate layout, labels, field mappings, and default WhatsApp message for your organization.",
    ccPaymentProofTemplateSaved: "Payment proof template saved",
    ccPaymentProofTemplateSaveFailed: "Failed to save payment proof template",
    ccPaymentProofTemplateLoadFailed: "Failed to load payment proof template",
    ccPaymentProofCertificateDetails: "Certificate details",
    ccPaymentProofLogoUrl: "Logo URL",
    ccPaymentProofLogoUpload: "Organization logo",
    ccPaymentProofLogoChooseFile: "Upload logo",
    ccPaymentProofLogoEmpty: "No logo uploaded",
    ccPaymentProofLogoHint: "PNG, JPEG, GIF, or WebP up to 5 MB. Shown on the clearance certificate header.",
    ccPaymentProofLogoUploaded: "Logo uploaded",
    ccPaymentProofLogoUploadFailed: "Failed to upload logo",
    ccPaymentProofAssetsSection: "Certificate images",
    ccPaymentProofImageTooLarge: "Image exceeds maximum size of 5 MB",
    ccUploading: "Uploading...",
    ccPaymentProofPrimaryColor: "Primary color",
    ccPaymentProofPrimaryColorInvalid: "Primary color must be a valid hex value (e.g. #E87722)",
    ccPaymentProofCompanyNameEn: "Company name (English)",
    ccPaymentProofCompanyNameAr: "Company name (Arabic)",
    ccPaymentProofTitleEn: "Certificate title (English)",
    ccPaymentProofTitleAr: "Certificate title (Arabic)",
    ccPaymentProofBodyEn: "Certificate body (English)",
    ccPaymentProofBodyAr: "Certificate body (Arabic)",
    ccPaymentProofTelecomPartner: "Telecom partner name",
    ccPaymentProofStampUpload: "Stamp image",
    ccPaymentProofStampChooseFile: "Upload stamp",
    ccPaymentProofStampEmpty: "No stamp uploaded",
    ccPaymentProofStampHint: "PNG, JPEG, GIF, or WebP up to 5 MB. Shown on the certificate footer.",
    ccPaymentProofStampUploaded: "Stamp uploaded",
    ccPaymentProofStampUploadFailed: "Failed to upload stamp",
    ccPaymentProofSignatureUpload: "Signature image",
    ccPaymentProofSignatureChooseFile: "Upload signature",
    ccPaymentProofSignatureEmpty: "No signature uploaded",
    ccPaymentProofSignatureHint: "PNG, JPEG, GIF, or WebP up to 5 MB. Shown on the certificate footer.",
    ccPaymentProofSignatureUploaded: "Signature uploaded",
    ccPaymentProofSignatureUploadFailed: "Failed to upload signature",
    ccPaymentProofWhatsAppTemplate: "Default WhatsApp message template",
    ccPaymentProofFieldMappings: "Field mappings",
    ccPaymentProofMappingNone: "None",
    ccPaymentProofColumnsLoadFailed: "Failed to load ticket columns",
    ccPaymentProofFieldLabels: "Field labels",
    ccPaymentProofTemplateAdmin: "Payment proof template",
    ccSave: "Save",

    // Inbox
    ccInboxTitle: "Call Center Inbox",
    ccInboxSubtitle: "Initiate tracking, place calls externally, and update lifecycle status.",
    ccBrowserPhoneTitle: "Browser Phone",
    ccBrowserPhoneSubtitle: "Register your extension and handle calls directly inside the web app.",
    ccManualCall: "Manual Call",
    ccAiTriggered: "AI Triggered",
    ccPhoneOptionalPlaceholder: "Optional",
    ccPhoneOptionalHint: "Optional. Leave blank to use the mobile number stored on the account.",
    ccManualCallHint:
      "When browser phone is connected, calls are placed in-app via Linkus WebRTC. Existing tickets for the account are linked; a new ticket is not created.",
    ccAiCallHint:
      "AI call context (outstanding balance and last updated) is taken from the account record, not this form. Existing tickets for the account are linked; a new ticket is not created. Call lifecycle is updated manually during provider outage.",
    ccInitiateCallNow: "Initiate Call Now",
    ccLiveStatus: "Live Status",
    ccMockTelephonyHint:
      "FreeSWITCH bridge not configured or telephony mocked — provider id is synthetic (mock-freeswitch-…). Call and ticket are still valid; live events may be limited until the HTTP bridge is wired.",
    ccCallId: "CALL ID",
    ccNotStarted: "Not Started",
    ccTrackingAccount: "TRACKING ACCOUNT",
    ccNotSet: "Not Set",
    ccStream: "STREAM",
    ccDuration: "DURATION",
    ccCallType: "CALL TYPE",
    ccAccountNoUpper: "ACCOUNT NO",
    ccDialed: "DIALED",
    ccCallTicket: "Call ticket",
    ccViewTicketInList: "View ticket in list",
    ccListenRecording: "Listen to Recording",
    ccRecordingProcessing: "Recording processing…",
    ccRecordingPlaybackFailed: "Playback failed. Refresh the ticket or try again shortly.",
    ccCallHistory: "Call history",
    ccNoCallsFound: "No calls found",
    ccUnknownAccount: "Unknown account",
    ccRecordingPending: "Recording pending — available after the call completes.",
    ccManualStatusPanelHint: "Call initiated in system. Please place call manually and update status.",
    ccMarkRinging: "Mark Ringing",
    ccMarkAnswered: "Mark Answered",
    ccMarkFailed: "Mark Failed",
    ccCompleteCallNotes: "Complete call (optional notes)",
    ccSummaryOptional: "Summary (optional)",
    ccRecordingUrlOptional: "Recording URL (optional)",
    ccMarkCompleted: "Mark Completed",
    ccEventHistory: "Event History",
    ccTimelineInitial: "Initial",
    ccTimelineRealtime: "Realtime",
    ccStreamConnecting: "Connecting…",
    ccStreamConnected: "Connected",
    ccStreamReconnecting: "Reconnecting…",
    ccStreamClosed: "Closed",
    ccStreamDisconnected: "Disconnected",
    ccCallTypeAiAutomated: "AI automated",
    ccCallTypeManual: "Manual",
    ccCallTypeOutbound: "Outbound",
    ccToastCallInitiated: "Call started.",
    ccCallingDisabled: "Calling is disabled for this organization.",
    ccBrowserPhoneOrgCallingDisabled: "Calling is disabled for this organization.",
    ccBrowserPhoneWebCallingDisabled:
      "Web calling is disabled for this organization. Use the WASL desktop app or ask an admin to enable web calling.",
    ccBrowserPhoneDesktopCallingDisabled:
      "Desktop calling is disabled for this organization. Use the web phone or ask an admin to enable desktop calling.",
    ccBrowserPhoneNotEnabled:
      "Browser phone is not available. Configure your agent extension, email, and D2C Linkus SDK credentials.",
    ccBrowserPhoneMissingExtension:
      "Browser phone needs your PBX extension. Ask an admin to set agent extension on your user profile.",
    ccBrowserPhoneMissingEmail:
      "Browser phone needs your email to match the D2C PBX extension. Ask an admin to set your user email.",
    ccBrowserPhoneSdkNotConfigured:
      "D2C Linkus SDK credentials are missing on the server. Set D2C_PBX_ACCESS_ID and D2C_PBX_ACCESS_KEY, then restart the backend.",
    ccBrowserPhoneFlagDisabled:
      "Browser phone is turned off. Set TELEPHONY_BROWSER_PHONE_ENABLED=true on the backend.",
    ccBrowserPhoneGrandstreamDesktopOnly:
      "This organization uses Grandstream UCM. Use the browser phone with WebRTC (SIP over WSS) on the same network as the UCM.",
    ccBrowserPhoneGrandstreamWebrtcDisabled:
      "Grandstream WebRTC is disabled for this organization. Enable WebRTC in telephony settings and on the UCM extension.",
    ccBrowserPhoneGrandstreamWssNotConfigured:
      "Grandstream SIP WebSocket (WSS) URL is missing. Set gs SIP WSS URL in organization telephony config (e.g. wss://192.168.0.160:8090/ws).",
    ccBrowserPhoneGrandstreamNotConfigured:
      "Grandstream calling is not fully configured. Ask an admin to complete UCM WebRTC/WSS settings and your agent extension and SIP password.",
    ccBrowserPhonePbxCredentialsRejected:
      "D2C rejected the Linkus SDK Access ID/Key. Regenerate them in PBX → Integrations → Linkus SDK, update D2C_PBX_ACCESS_ID / D2C_PBX_ACCESS_KEY, and restart the backend.",
    ccBrowserPhonePbxIpBlocked:
      "D2C PBX blocked this browser IP for OpenAPI (errcode 70004). Ask D2C to whitelist your public IP.",
    ccBrowserPhonePbxSslError:
      "The browser could not verify the D2C PBX HTTPS certificate. Check network/proxy settings or contact D2C.",
    ccToastCallInitiatedClickToCall:
      "Call initiated — answer your phone when it rings.",
    ccClickToCallHint:
      "Enter a number and tap Call. Your extension will ring first, then the customer.",
    ccInitiatingCall: "Initiating call…",
    ccLinkusSoftphoneTitle: "Answer on Linkus",
    ccLinkusSoftphoneHint:
      "Calls are placed by the server. Keep Linkus Desktop, Mobile, or Web Client signed in to answer click-to-call and inbound calls.",
    ccLinkusExtensionLabel: "Your extension",
    ccOpenLinkusWebClient: "Open Linkus Web Client",
    ccDownloadLinkusDesktop: "Download Linkus Desktop",
    ccDownloadLinkusMobile: "Download Linkus Mobile",
    ccMakeCall: "Call customer",
    ccGlobalDialerTitle: "Phone",
    ccDialerCall: "Call",
    ccGlobalDialerOpen: "Open phone dialer",
    ccGlobalDialerMinimize: "Minimize dialer",
    ccGlobalDialerConnect: "Connect phone",
    ccDisconnectPhone: "Disconnect",
    ccGlobalDialerConnecting: "Connecting…",
    ccGlobalDialerReady: "Ready to call",
    ccGlobalDialerOnCall: "On call",
    ccGlobalDialerIncoming: "Incoming call",
    ccCallMute: "Mute",
    ccCallUnmute: "Unmute",
    ccCallHold: "Hold",
    ccCallResume: "Resume",
    ccCallHangUp: "Hang up",
    ccCallEndCall: "End call",
    ccCallKeypad: "Keypad",
    ccCallContacts: "Contacts",
    ccCallNote: "Note",
    ccActiveOutboundCall: "Active outbound call",
    ccActiveInboundCall: "Active inbound call",
    ccCallOnHold: "On hold",
    ccCallOutbound: "Outbound",
    ccCallInbound: "Inbound",
    ccIncomingCallAccept: "Accept",
    ccIncomingCallReject: "Reject",
    ccIncomingCallAccount: "Account",
    ccUnknownCaller: "Unknown caller",
    ccGlobalDialerTapToConnect: "Tap to connect",
    ccGlobalDialerEnterToCall: "Enter a number and tap Call",
    ccDialerEnterNumber: "Enter number",
    ccPhoneStatusReady: "Ready",
    ccPhoneStatusUnavailable: "Unavailable",
    ccPhoneStatusEnded: "Call ended",
    ccPhoneStatusOffline: "Offline",
    ccPhoneAdvancedSettings: "Audio & call history",
    ccBrowserPhonePageHint: "Dial numbers below. On other screens, use the phone button at the bottom-right.",
    ccGlobalDialerNavigateHint: "You can navigate to any screen while on a call.",
    ccGlobalDialerOpenBrowserPhone: "Open browser phone",
    ccGlobalDialerResetPhone: "Reset phone",
    ccToastBrowserPhoneConnecting: "Browser phone is connecting. Try again in a moment.",
    ccToastBrowserPhoneNotReady:
      "Browser phone is not registered. Check extension/SIP password or open the phone dialer to connect.",
    ccToastBrowserPhoneOfficeNetworkRequired:
      "Browser calling works only on the office network. Connect to office Wi-Fi, then try again.",
    ccToastNativeSipRegistrationFailed:
      "Desktop phone could not register with the PBX. Use office LAN or VPN, confirm extension and SIP password, and close Wave Desktop for this extension.",
    ccBrowserPhoneLinkusConnectionFailed:
      "Could not connect to D2C Cloud PBX. Check your internet connection, refresh the page, and try again.",
    ccToastBrowserPhoneAuthFailed:
      "Could not register your phone extension. Verify agent extension and SIP password in User Admin.",
    ccBrowserPhoneExtensionMissing:
      "No phone extension is set on your account. Ask an admin to add your extension and email in User Admin.",
    ccBrowserPhoneExtensionMismatch:
      "Your phone extension could not be found on the PBX. Ask an admin to verify your extension.",
    ccBrowserPhoneUnexpectedError:
      "Phone setup failed. Please try again or contact your administrator if the problem continues.",
    ccToastUnableInitiateCall: "Unable to initiate call",
    ccToastInitiateCallFirst: "Initiate a call first to resolve the account before updating status.",
    ccToastCallStatusUpdated: "Call status updated to",
    ccToastLinkedTicketNotFound: "Linked ticket was not found. It may have been deleted.",
    ccToastCallStatusUpdateFailed: "Failed to update status to",

    // Tickets list
    ccTicketsTitle: "Support Tickets",
    ccTicketsSubtitle: "Manage and track recovery tickets across the {org} workspace.",
    ccAddNewTicket: "Add New Ticket",
    ccNewTicket: "New Ticket",
    ccOutstandingRemarks: "Outstanding Remarks",
    ccShowingTicketsCount: "Showing {from}-{to} of {total} tickets",
    ccTicketFilters: "Ticket Filters",
    ccShowingPage: "Showing page",
    ccOf: "of",
    ccTotalTicket: "total ticket",
    ccTotalTickets: "total tickets",
    ccHideAdvancedFilters: "Hide advanced filters",
    ccShowAdvancedFilters: "Show advanced filters",
    ccSearchAccountPlaceholder: "Search account number...",
    ccGlobalSearchPlaceholder: "Search tickets, agents, debtors...",
    ccCalled: "Called",
    ccCalledOnly: "Called only",
    ccNotCalled: "Not called",
    ccAssignee: "Assignee",
    ccUnassigned: "Unassigned",
    ccAssignTicket: "Assign ticket",
    ccReassignTicket: "Reassign ticket",
    ccAssignSelectedTickets: "Assign selected",
    ccTicketAssignModalTitle: "Assign ticket",
    ccTicketReassignModalTitle: "Reassign ticket",
    ccTicketBulkAssignModalTitle: "Assign selected tickets",
    ccTicketAssignModalDescription: "Choose a call agent for this ticket.",
    ccTicketBulkAssignModalDescription: "Choose a call agent for all selected tickets.",
    ccTicketAssignSelectAgent: "Call agent",
    ccTicketAssignSelectAgentPlaceholder: "Select an agent",
    ccTicketAssignConfirm: "Save assignment",
    ccTicketUnassign: "Unassign",
    ccTicketAssignSuccess: "Ticket assignment updated.",
    ccTicketUnassignSuccess: "Ticket unassigned.",
    ccTicketAssignFailed: "Could not update ticket assignment.",
    ccTicketUnassignFailed: "Could not unassign ticket.",
    ccTicketsBulkAssignSuccess: "Selected tickets assigned.",
    ccTicketAssignNoAgents: "No eligible call agents were found for this organization.",
    ccLoadingUsers: "Loading agents…",
    ccAllAssignees: "All assignees",
    ccScopedToCurrentAgent: "Scoped to current agent",
    ccSortBy: "Sort By",
    ccSortOutstandingAsc: "Outstanding: Low to High",
    ccSortOutstandingDesc: "Outstanding: High to Low",
    ccSortPaymentDateAsc: "Payment Date: Oldest First",
    ccSortPaymentDateDesc: "Payment Date: Newest First",
    ccSortCalledAtAsc: "Called At: Oldest First",
    ccSortCalledAtDesc: "Called At: Newest First",
    ccSortRecentlyUpdated: "Recently Updated",
    ccStatusIds: "Status IDs",
    ccStatusFiltersSelected: "status filter(s) selected",
    ccMultiSelectTip: "Tip: hold Ctrl/Cmd for multi-select",
    ccMinOutstanding: "Min Outstanding",
    ccMaxOutstanding: "Max Outstanding",
    ccPaymentDateFrom: "Payment Date From",
    ccPaymentDateTo: "Payment Date To",
    ccApplyFilters: "Apply Filters",
    ccDeleteSelected: "Delete selected",
    ccNoTicketsFound: "No tickets found",
    ccNoTicketsHint: "Try adjusting your filters.",
    ccNoTicketsEmpty: "No tickets yet. Create one from Ticket Entry or bulk upload an Excel file.",
    ccNoTicketsFiltered: "No tickets match the current filters.",
    ccConfirmBulkDeleteTickets: "Delete {count} selected ticket(s)? This cannot be undone.",
    ccTicketsListLoadFailed: "Failed to load tickets",
    ccSelectAllTickets: "Select all tickets",
    ccSelectTicket: "Select ticket",
    ccPage: "Page",
    ccResults: "results",
    ccTicket: "Ticket",
    ccTicketId: "Ticket ID",
    ccTicketOverview: "Ticket Overview",
    ccTargetOutstanding: "Target Outstanding",
    ccPaymentDate: "Payment Date",
    ccUpdateTicket: "Update Ticket",
    ccTargetPaymentDate: "Target Payment Date",
    ccCalledAt: "Called At",
    ccAdditionalComments: "Additional Comments",
    ccAddRemarkNote: "Add remark note",
    ccAddAdditionalComments: "Add additional comments",
    ccSaveStatusUpdate: "Save Status Update",
    ccRefreshLiveOutstanding: "Refresh Live Outstanding",
    ccToastTicketStatusUpdated: "Ticket status updated",
    ccToastTicketNotFound: "Ticket was not found. It may have been deleted.",
    ccToastTicketStatusUpdateFailed: "Failed to update ticket status",
    ccToastOutstandingRefreshedPrefix: "Outstanding refreshed. Previous:",
    ccToastOutstandingRefreshedLive: "Live:",
    ccToastOutstandingRefreshedAt: "At:",
    ccToastRefreshOutstandingFailed: "Failed to refresh outstanding",
    ccToastSelectTicketToDelete: "Select at least one ticket to delete.",
    ccToastDeleteProcessedPrefix: "Delete processed. Deleted:",
    ccToastDeleteAlreadyDeleted: "already deleted:",
    ccToastDeleteNotFound: "not found:",
    ccToastBulkDeleteFailed: "Failed to bulk delete tickets",
    ccToastLoadTicketsFailed: "Failed to load tickets",
    ccToastLoadStatusOptionsFailed: "Failed to load status options",
    ccToastLoadAssigneesFailed: "Failed to load assignee list",

    // Agent workspace
    ccAgentWorkspaceTitle: "Ticket Entry",
    ccAgentWorkspaceSubtitle: "Create a single ticket manually with the configured fields.",
    ccTicketBulkUploadTitle: "Bulk Ticket Upload",
    ccTicketBulkUploadSubtitle: "Upload tickets from Excel or CSV and review past upload results.",
    ccUploadHistoryResultsLabel: "uploads",
    ccUploadHistoryFile: "File",
    ccUploadHistoryDate: "Uploaded",
    ccUploadHistoryEmptyTitle: "No uploads yet",
    ccUploadHistoryNoRowDetails: "No row-level details recorded for this upload.",
    ccBulkUploadFormHint: "Upload a spreadsheet, map columns, then process rows into tickets.",
    ccBulkUploadStepFile: "Select file",
    ccBulkUploadStepMapping: "Map columns",
    ccBulkUploadStepProcess: "Process upload",
    ccBulkUploadParsing: "Reading file headers…",
    ccBulkUploadValidationFailed: "Validation failed",
    ccBulkUploadValidationPending: "Mapping in progress",
    ccBulkUploadFooterHint: "Rows with active payment plans are ignored, not failed.",
    ccBulkUploadClearFile: "Remove file",
    ccBulkUploadClearConfirm: "Remove the selected file and reset column mapping?",
    ccBulkUploadSubmitConfirm: "Process this file and create tickets for all valid rows?",
    ccBulkUploadSuccessTitle: "Upload completed",
    ccBulkUploadRequiredColumns: "Required columns",
    ccUploadHistoryPageTitle: "Upload History",
    ccUploadHistoryPageSubtitle: "Review past Excel uploads, row outcomes, and failure reasons.",
    ccUploadHistoryRecentTitle: "Recent uploads",
    ccUploadHistoryViewAll: "View all history",
    ccUploadHistoryStatUploads: "Total uploads",
    ccUploadHistorySuccessRate: "Success rate",
    ccUploadHistorySearchPlaceholder: "Search by file name…",
    ccUploadHistoryOutcomeFilter: "Upload result",
    ccUploadHistoryOutcome_ALL: "All uploads",
    ccUploadHistoryOutcome_HAS_FAILURES: "With failures",
    ccUploadHistoryOutcome_HAS_IGNORED: "With ignored rows",
    ccUploadHistoryOutcome_CLEAN: "Fully successful",
    ccUploadHistoryEmptyFiltered: "No uploads match the current filters.",
    ccUploadHistoryLoadFailed: "Failed to load upload history",
    ccUploadHistoryDetailTitle: "Upload details",
    ccUploadHistoryTab_ALL: "All rows",
    ccUploadHistoryTab_CREATED: "Created",
    ccUploadHistoryTab_IGNORED: "Ignored",
    ccUploadHistoryTab_FAILED: "Failed",
    ccUploadHistoryTab_REASSIGNED: "Reassigned",
    ccUploadHistoryTab_UNCHANGED: "Unchanged",
    ccUploadRowReassigned: "Reassigned",
    ccUploadRowUnchanged: "Unchanged",
    ccBulkUploadReassignExisting: "Reassign existing open tickets when account already exists",
    ccBulkUploadReassignExistingHint:
      "Map an agent column in the file. Matching accounts update assignee instead of creating duplicate tickets.",
    ccBulkUploadReassignRequiresAgent:
      "Enable agent column mapping when reassigning existing tickets from Excel upload.",
    ccTicketAssignmentHistoryTitle: "Assignment history",
    ccTicketAssignmentHistoryEmpty: "No assignment changes recorded yet.",
    ccTicketAssignmentHistoryLoadFailed: "Failed to load assignment history",
    ccTicketAssignmentEventAssigned: "Assigned to {assignee}",
    ccTicketAssignmentEventUnassigned: "Unassigned from {previous}",
    ccTicketAssignmentEventReassigned: "Reassigned from {previous} to {next}",
    ccTicketAssignmentSourceExcelUpload: "Excel upload",
    ccTicketAssignmentSourceExcelReassign: "Excel reassign",
    ccTicketAssignmentSourceManualAssign: "Manual assign",
    ccTicketAssignmentSourceManualUnassign: "Manual unassign",
    ccTicketAssignmentSourceBulkList: "Bulk list",
    ccTicketAssignmentSourceBulkApi: "Bulk API",
    ccTicketAssignmentSourceTicketCreate: "Ticket create",
    ccTicketAssignmentSourceUnknown: "System",
    ccTicketColumnConfigPageTitle: "Ticket Column Settings",
    ccTicketColumnConfigPageSubtitle: "Manage which ticket columns are required and where they appear.",
    ccManualTicketEntry: "Manual Ticket Entry",
    ccTicketEntryFormHint: "Fill in the required fields below. Fields marked with * are mandatory.",
    ccTicketEntrySectionAccount: "Account details",
    ccTicketEntrySectionAccountHint: "The account number must be unique for active tickets.",
    ccTicketEntrySectionContact: "Contact numbers",
    ccTicketEntrySectionContactHint: "Add one or more mobile numbers for outreach.",
    ccTicketEntrySectionAssignment: "Assignment & priority",
    ccTicketEntrySectionPayment: "Payment details",
    ccTicketEntrySectionNotes: "Remarks",
    ccTicketEntrySectionNotesHint: "Interaction notes appear on the ticket timeline.",
    ccTicketEntrySectionAdditional: "Additional fields",
    ccTicketEntryQuickLinks: "Quick links",
    ccTicketEntryRequiredFields: "Required fields",
    ccTicketEntryRequiredHint: "Required fields are marked with an asterisk.",
    ccTicketEntryViewTickets: "View all tickets",
    ccTicketEntryClearConfirm: "Discard unsaved changes and clear the form?",
    ccTicketEntryAccountPlanTitle: "Payment plans",
    ccTicketEntryAccountPlanNote: "Accounts with an active payment plan cannot receive manual tickets. Manage them under Payment Plans instead.",
    ccAssignedTo: "Assigned To",
    ccLoadingUsers: "Loading users...",
    ccSelectUserOptional: "Select user (optional)",
    ccRemarksInteractionNotes: "Remarks / Interaction Notes",
    ccCallCommentThread: "Call history & comments",
    ccNoCallsOnTicket: "No calls recorded on this ticket yet.",
    ccNoCommentsForCall: "No comments for this call yet.",
    ccAddCallComment: "Add comment",
    ccCallCommentPlaceholder: "Write a note about this call...",
    ccCallDirectionInbound: "Inbound",
    ccCallDirectionOutbound: "Outbound",
    ccCallAgent: "Agent",
    ccCallAt: "Called at",
    ccSavingComment: "Saving...",
    ccCommentAdded: "Comment added",
    ccCommentAddFailed: "Failed to add comment",
    ccCallThreadLoadFailed: "Failed to load call history",
    ccAiGenerated: "AI generated",
    ccCreateTicket: "Create Ticket",
    ccClear: "Clear",
    ccAccountNoExample: "e.g. 112015128",
    ccRemarksPlaceholder: "Add clear interaction notes...",
    ccBulkImport: "Bulk Import",
    ccBulkImportRequiredColumns: "Upload multiple tickets via CSV or Excel. Required columns:",
    ccBulkImportPriorityNote: "Applies to every row in the upload.",
    ccBulkImportAgentNameNote: "Agent Name must exactly match an existing call agent in your organization.",
    ccBulkImportOptional: "Optional:",
    ccBulkImportDynamicHint: "Any columns in your file are captured automatically. Configure mandatory and visible fields below.",
    ccBulkImportMultiMobileNote: "For multiple mobile numbers, map several columns to Mobile Number or put comma-separated numbers in one cell. Local numbers without a country code default to Oman (+968).",
    ccAddMobileNumber: "Add mobile number",
    ccRemoveMobileNumber: "Remove mobile number",
    ccMobileNumberPlaceholder: "Enter mobile number",
    ccMobileNumberHint: "Country code defaults to Oman (+968). Change the flag for other countries. Existing local numbers are treated as Omani.",
    ccMobileNumberInvalid: "Enter a valid mobile number with country code.",
    ccMobileNumberInvalidOman: "Omani mobile numbers must be 7–8 digits after +968.",
    ccMobileNumberDuplicate: "This mobile number is already listed.",
    ccMobileNumberMax: "Maximum {max} mobile numbers allowed.",
    ccMobileNumberRequired: "At least one mobile number is required",
    ccBulkPriority: "Bulk Priority",
    ccUploadHistoryTitle: "Upload history",
    ccUploadHistoryEmpty: "No Excel uploads yet.",
    ccUploadSuccessCount: "Created",
    ccUploadIgnoredCount: "Ignored",
    ccUploadFailedCount: "Failed",
    ccUploadTotalRows: "Total rows",
    ccUploadViewDetails: "View details",
    ccUploadHideDetails: "Hide details",
    ccUploadRowCreated: "Created",
    ccUploadRowIgnored: "Ignored",
    ccUploadRowFailed: "Failed",
    ccUploadSummaryToast: "Upload finished: {success} created, {ignored} ignored, {failed} failed.",
    ccToastAccountPlanTicketBlocked:
      "Cannot create a manual ticket for this account because it has an active payment plan",
    ccDropFileOrBrowse: "Drop file here or click to browse",
    ccFileTypesCsvXls: "CSV, XLS, XLSX",
    ccValidationResult: "Validation Result",
    ccMissingHeaders: "Missing:",
    ccHeadersValidated: "Headers validated successfully",
    ccFoundHeaders: "Found:",
    ccUploadColumnMapping: "Column Mapping",
    ccUploadKeepAsNew: "Keep as new field:",
    ccUploadMappedTo: "Mapped to:",
    ccDuplicateUploadMappingTitle: "These headers are mapped to the same ticket field:",
    ccToastDuplicateUploadMapping: "Each ticket field can only be mapped from one upload column.",
    ccProcessBulkUpload: "Process Bulk Upload",
    ccAgentTipTitle: "Agent Tip",
    ccAgentTipBody:
      "Add clear interaction notes in remarks to keep ticket timelines useful during manual call tracking.",
    ccColumnConfigTitle: "Ticket Column Configuration",
    ccColumnConfigSubtitle: "Mark captured columns as mandatory and choose where they appear.",
    ccSaveColumnConfig: "Save Column Settings",
    ccSaving: "Saving...",
    ccColumnLabel: "Column",
    ccColumnType: "Type",
    ccMandatory: "Mandatory",
    ccShowInList: "List",
    ccShowInForm: "Form",
    ccShowInUpdate: "Update",
    ccShowInFilter: "Filter",
    ccPredefinedColumn: "Predefined",
    ccCustomColumn: "Custom",
    ccInvalidNumber: "Enter a valid number",
    ccToastLoadColumnConfigFailed: "Failed to load ticket column configuration",
    ccToastColumnConfigSaved: "Column configuration saved",
    ccToastColumnConfigSaveFailed: "Failed to save column configuration",
    ccToastTicketCreated: "Ticket created successfully",
    ccToastCreateTicketFailed: "Failed to create ticket",
    ccToastInvalidFileType: "Invalid file type. Please upload CSV/XLS/XLSX",
    ccToastParseFileFailed: "Failed to parse file structure",
    ccToastNoFileSelected: "No file selected",
    ccToastSelectBulkPriority: "Please select a bulk priority",
    ccToastHeaderValidationFailed: "Header validation failed",
    ccToastBulkUploadProcessed: "Bulk upload processed",
    ccToastBulkUploadFailed: "Bulk upload failed",
    ccToastLoadOfficeUsersFailed: "Failed to load office users",

    // Campaigns
    ccCampaignManagerTitle: "Campaign Manager",
    ccCampaignManagerSubtitle: "Automate and monitor outbound call flows",
    ccCreateNewCampaign: "Create New Campaign",
    ccCampaignName: "Campaign Name",
    ccCampaignNamePlaceholder: "E.g. Q4 Debt Recovery",
    ccCampaignNameRequired: "Campaign name is required",
    ccExecutionScheduleCron: "Execution Schedule (Cron)",
    ccCallTypeRequired: "Call type is required",
    ccScheduleCronRequired: "Schedule cron is required",
    ccManualAgent: "Manual Agent",
    ccMaxRetries: "Max Retries",
    ccDelayMin: "Delay (Min)",
    ccTriggerConditions: "Trigger Conditions",
    ccAddRule: "+ Add Rule",
    ccFieldMode: "FIELD",
    ccExprMode: "EXPR",
    ccExpressionPlaceholder: "e.g. balance > 500",
    ccFieldName: "Field Name",
    ccValue: "Value",
    ccSetAsActive: "Set as Active",
    ccCreateCampaign: "Create Campaign",
    ccExistingCampaigns: "Existing Campaigns",
    ccLogicPreview: "Logic Preview",
    ccToastCampaignLaunched: "Campaign launched successfully",
    ccToastCampaignCreateFailed: "Failed to create campaign",
    ccToastLoadCampaignsFailed: "Failed to load campaigns list",
    ccToastCampaignUpdated: "Campaign updated successfully",
    ccToastCampaignDeleted: "Campaign deleted successfully",
    ccToastCampaignDeleteFailed: "Failed to delete campaign",
    ccCampaignTasksLoadFailed: "Failed to load campaign tasks",
    ccCampaignFormSubtitle: "Configure schedule, targeting rules, and retry policy.",
    ccScheduleCronHint: "Use a 6-field Spring cron expression, e.g. 0 */10 * * * *",
    ccCampaignManualTypeHint: "Manual campaigns are saved for reference but are not auto-dialed. Use AI automated for scheduled outbound calls.",
    ccConditionLogic: "Condition logic",
    ccTicketLimit: "Ticket limit per run",
    ccTicketLimitPlaceholder: "Optional",
    ccRemove: "Remove",
    ccSaveChanges: "Save changes",
    ccAnsweredRate: "Answered rate",
    ccSearch: "Search",
    ccCampaignSearchPlaceholder: "Search campaign name...",
    ccCampaignStatusActive: "Active",
    ccCampaignStatusInactive: "Inactive",
    ccCampaignsEmptyTitle: "No campaigns yet",
    ccCampaignsEmptyHint: "Create a payment reminder campaign to AI-call unassigned tickets on a schedule.",
    ccCampaignResultsLabel: "campaigns",
    ccCampaignDeleteConfirm: "Delete campaign \"{name}\"? Queued tasks for this campaign will also be removed.",
    ccCampaignDetailSubtitle: "Schedule, status, and recent dial tasks",
    ccCampaignRecentTasks: "Recent dial tasks",
    ccCampaignTasksEmpty: "No dial tasks queued for this campaign yet.",
    ccCampaignTaskAiOutcome: "AI outcome",
    ccCallTaskStatus_QUEUED: "Queued",
    ccCallTaskStatus_PROCESSING: "Processing",
    ccCallTaskStatus_DIALING: "Dialing",
    ccCallTaskStatus_AWAITING_COMPLETION: "Awaiting completion",
    ccCallTaskStatus_COMPLETED: "Completed",
    ccCallTaskStatus_FAILED: "Failed",
    ccCallTaskStatus_RETRY_WAIT: "Retry scheduled",
    ccAiCallIntent_PAID: "Paid",
    ccAiCallIntent_WILL_PAY: "Will pay",
    ccAiCallIntent_DISPUTE: "Dispute",
    ccAiCallIntent_CALLBACK_REQUIRED: "Callback required",
    ccAiCallIntent_NO_ANSWER: "No answer",
    ccAiCallIntent_UNKNOWN: "Unknown",
    ccEditCampaign: "Edit campaign",
    ccViewDetails: "View details",
    ccUpdatedAt: "Updated at",
    ccPhone: "Phone",
    ccCampaignPromoTitle: "Start a campaign",
    ccCampaignPromoSubtitle: "Launch an outbound recovery run and track it live.",
    ccCampaignPromoAction: "New campaign",
    ccCampaignPaymentReminderSubtitle:
      "Schedule AI calls to unassigned tickets with outstanding balance. Tickets update automatically from call outcomes.",
    ccCampaignPaymentReminderPageSubtitle:
      "AI payment reminder calls for unassigned tickets — outcomes update ticket status automatically.",
    ccCampaignPaymentReminderPageIntro:
      "Each run finds open, unassigned tickets matching your rules, places AI reminder calls, then updates tickets when customers respond (paid, will pay, callback, dispute, or no answer).",
    ccCampaignFlowTitle: "How payment reminder campaigns work",
    ccCampaignFlowHint: "Campaigns always use AI automated calling.",
    ccCampaignFlowStep1: "Find unassigned tickets with outstanding balance that match your trigger rules.",
    ccCampaignFlowStep2: "Place AI reminder calls during the configured schedule and dial window.",
    ccCampaignFlowStep3: "Update each ticket from the AI outcome (status, promise date/amount, summary, recording).",
    ccCampaignUnassignedHint: "Only unassigned tickets are included — this is enforced automatically.",
    ccCampaignResetDefaults: "Reset payment reminder defaults",
    ccCampaignTypeLabel: "Campaign type",
    ccCampaignTypeAiPaymentReminder: "AI payment reminder",
    ccCampaignDetailPaymentReminderSubtitle: "AI dial tasks and automatic ticket updates from customer responses",
    ccCampaignOutcomeTitle: "Ticket updates from AI outcomes",
    ccCampaignOutcomeHint:
      "Paid → PAID · Will pay / No answer → Follow up · Callback → Callback required · Dispute → Failed · Promise date and amount are saved when provided.",
    ccCampaignKpiAiScopeHint: "Metrics below include AI automated payment-reminder calls only — manual agent calls are excluded.",
    ccCampaignKpiAiCalls: "AI calls placed",
    ccCampaignKpiAiAnsweredRate: "AI answered rate",
    ccCampaignKpiAiSuccessRate: "Paid after AI call",
    ccCampaignKpiAiRecovery: "Recovery from AI calls",

    // Dashboard (command center)
    ccAnalyticsTitle: "Dashboard",
    ccAnalyticsSubtitle: "Team targets, campaign health, and shortcuts at a glance.",
    ccDashboardLastUpdated: "Last updated {time} ({timezone})",
    ccOperationalAlerts: "Operational Alerts",
    ccOperationalAlertsSubtitle: "{open} open · {escalated} escalated",
    ccViewAllAlerts: "View all alerts",
    ccAlertAgent: "Agent",
    ccAlertReference: "Reference",
    ccAlertReason: "Reason",
    ccAlertPriority: "Priority",
    ccAlertPending: "Pending",
    ccAlertDuration: "Duration",
    ccAlertOpenFor: "Open for",
    ccAlertResponseTime: "Response time",
    ccAlertStatus: "Status",
    ccAcknowledge: "Acknowledge",
    ccAlertsLoading: "Loading alerts…",
    ccAlertsLoadFailed: "Failed to load alerts",
    ccAlertAcknowledged: "Alert acknowledged",
    ccAlertAckFailed: "Failed to acknowledge alert",
    ccMenuAgentAlerts: "Agent Alerts",
    ccMenuAgentResponseAlerts: "Agent response alerts",
    ccAlertsPageTitle: "Agent Response Alerts",
    ccAlertsPageSubtitle: "Monitor SLA warnings, escalations, and team response times.",
    ccAllStatuses: "All statuses",
    ccAllAgents: "All agents",
    ccSlaSettingsLink: "SLA settings",
    ccMenuSlaSettings: "Response SLA",
    ccSlaSettingsPageTitle: "Response SLA",
    ccSlaSettingsPageSubtitle: "Configure warning and escalation timers for assigned tickets and missed calls.",
    ccAlertsEmpty: "No alerts match the current filters.",
    ccAlertResolved: "Alert resolved",
    ccAlertResolveFailed: "Failed to resolve alert",
    ccResolve: "Resolve",
    ccResolveAlert: "Resolve alert",
    ccResolutionNotePlaceholder: "Resolution note (optional)",
    ccCancel: "Cancel",
    ccConfirmDialogTitle: "Confirm action",
    ccConfirmDialogProceed: "Proceed",
    ccConfirmDialogConfirm: "Confirm",
    ccPrevious: "Previous",
    ccNext: "Next",
    ccPageOf: "Page {page} of {total}",
    ccAlertReportTitle: "Alert audit report",
    ccLoadReport: "Load report",
    ccExportCsv: "Export CSV",
    ccAlertReportFailed: "Failed to load alert report",
    ccAlertReportEmpty: "No report data to export",
    ccReportCreated: "Created",
    ccReportResolved: "Resolved",
    ccReportAutoResolved: "Auto resolved",
    ccReportEscalatedL1: "Escalated L1",
    ccReportEscalatedL2: "Escalated L2",
    ccReportAcknowledged: "Acknowledged",
    ccAlertsAccessDenied: "You do not have access to the alerts monitor.",
    ccAckNotePlaceholder: "Acknowledgement note (optional)",
    ccAcknowledgeAlert: "Acknowledge alert",
    ccReassignToAgent: "Reassign ticket to",
    ccNoReassign: "Do not reassign",
    ccAlertTimeline: "Event timeline",
    ccAlertTimelineEmpty: "No events recorded for this alert.",
    ccAlertNotificationWarningTitle: "Agent response SLA warning",
    ccAlertNotificationWarningBody: "An assigned ticket or missed call needs attention.",
    ccAlertNotificationEscalatedTitle: "Agent alert escalated",
    ccAlertNotificationEscalatedBody: "A response SLA alert was escalated to your team.",
    ccViewTimeline: "Timeline",
    ccHideTimeline: "Hide timeline",
    ccSlaThresholdsTitle: "Response SLA thresholds",
    ccSlaThresholdsSubtitle: "Configure warning and escalation timers per alert type. Times can use minutes, hours, or days.",
    ccSlaTypeColumn: "Type",
    ccSlaWarningMinutes: "Warning (minutes)",
    ccSlaL1Minutes: "Escalation L1 (minutes)",
    ccSlaL2Minutes: "Escalation L2 (minutes)",
    ccSlaAckGraceMinutes: "Ack grace (minutes)",
    ccSlaEnabled: "Enabled",
    ccSlaStatusOn: "On",
    ccSlaStatusOff: "Off",
    ccSlaThresholdColumn: "Threshold",
    ccSlaDescriptionColumn: "Description",
    ccSlaDurationColumn: "Duration",
    ccSlaSaveHint: "Changes apply to all alert types when saved.",
    ccSlaPriorityHint: "Critical and high-priority alerts escalate faster. Disable a type to stop new alerts and escalation for that category.",
    ccSlaEscalationPreview: "Escalation timeline",
    ccSlaDisabledTimeline: "Escalation is paused while this alert type is disabled.",
    ccSlaStepPending: "Assigned",
    ccSlaImmediate: "Immediate",
    ccSlaUnitMinute: "minute",
    ccSlaUnitMinutes: "minutes",
    ccSlaUnitHour: "hour",
    ccSlaUnitHours: "hours",
    ccSlaUnitDay: "day",
    ccSlaUnitDays: "days",
    ccSlaUnitMinuteShort: "m",
    ccSlaUnitHourShort: "h",
    ccSlaUnitDayShort: "d",
    ccSlaAckGraceNote: "+{duration} ack grace before L2",
    ccSlaCopyFrom: "Copy from",
    ccSlaCopyFromPlaceholder: "Select type",
    ccSlaCopiedFrom: "Copied thresholds from {source}",
    ccSlaResetDefaults: "Reset defaults",
    ccSlaResetToDefaults: "Reset to default thresholds",
    ccSlaItemType_TICKET: "Ticket",
    ccSlaItemType_MISSED_CALL: "Missed call",
    ccSlaItemType_BULK_ASSIGNMENT: "Bulk assignment",
    ccSlaItemDesc_TICKET: "Response SLA for newly assigned or callback-required tickets.",
    ccSlaItemDesc_MISSED_CALL: "Callback SLA after a missed inbound call.",
    ccSlaItemDesc_BULK_ASSIGNMENT: "Start-work SLA after a large bulk ticket upload.",
    ccSlaField_warning: "Warning",
    ccSlaField_escalationL1: "Escalation L1",
    ccSlaField_escalationL2: "Escalation L2",
    ccSlaField_acknowledgementGrace: "Ack grace",
    ccSlaFieldDesc_warning: "Time before the alert enters warning state",
    ccSlaFieldDesc_escalationL1: "Escalate to the agent's supervisor",
    ccSlaFieldDesc_escalationL2: "Escalate to call-center administrators",
    ccSlaFieldDesc_acknowledgementGrace: "Grace period after acknowledgement before L2 escalation",
    ccSlaUnitLabel_MINUTES: "Minutes",
    ccSlaUnitLabel_HOURS: "Hours",
    ccSlaUnitLabel_DAYS: "Days",
    ccSupervisorMappingTitle: "Supervisor mapping",
    ccSupervisorMappingSubtitle: "Map each call agent to a supervisor for L1 escalation.",
    ccSupervisor: "Supervisor",
    ccNoSupervisor: "No supervisor",
    ccSlaSettingsLoading: "Loading SLA settings…",
    ccSlaSettingsLoadFailed: "Failed to load SLA settings",
    ccSlaSettingsSaved: "SLA settings saved",
    ccSlaSettingsSaveFailed: "Failed to save SLA settings",
    ccSaveSlaSettings: "Save SLA settings",
    ccSaving: "Saving…",
    ccRefresh: "Refresh",
    ccActions: "Actions",
    ccRefreshAnalytics: "Sync",
    ccExportDashboard: "Export",
    ccLast7Days: "Last 7 days",
    ccLast30Days: "Last 30 days",
    ccLast90Days: "Last 90 days",
    ccDashboardExported: "Dashboard export downloaded",
    ccDashboardQuickActions: "Quick actions",
    ccDashboardLastFetched: "Last updated",
    ccDashboardNotFetchedYet: "Not loaded yet",
    ccDashboardOpen: "Open",
    ccTeamProgress: "Team Progress",
    ccTeamProgressSubtitle: "Ticket target execution by member",
    ccTotalTargets: "Total Targets",
    ccCalledTargets: "Called Targets",
    ccPaidTargets: "Paid Targets",
    ccRecovery: "Recovery",
    ccCalledCoverage: "Called Coverage",
    ccPaidConversion: "Paid Conversion",
    ccMember: "Member",
    ccAssigned: "Assigned",
    ccCalledCol: "Called",
    ccPaidCol: "Paid",
    ccPending: "Pending",
    ccNoTeamProgressData: "No team progress data available.",
    ccCampaignAnalytics: "Campaign Analytics",
    ccCampaignAnalyticsSubtitle: "Campaign throughput and outcome quality",
    ccTotalCalls: "Total Calls",
    ccAnswerRate: "Answer Rate",
    ccSuccessRate: "Success Rate",
    ccCampaignPerformanceSnapshot: "Campaign Performance Snapshot",
    ccAnsweredPercent: "Answered %",
    ccSuccessPercent: "Success %",
    ccNoCampaignAnalyticsData: "No campaign analytics data available.",
    ccToastLoadAnalyticsFailed: "Failed to load analytics",
    ccTotalRecovery: "Total Recovery",
    ccPerfectTiming: "Best time to call",
    ccPerfectTimingSubtitle: "When customers usually answer, by hour of day",
    ccPerfectTimingHelp: "Calls are grouped by the hour the customer answered (or when the call started). Answer rate = answered calls ÷ total calls in that hour. The best hour has the highest answer rate with enough calls to be reliable.",
    ccPerfectTimingHeroTitle: "Recommended calling time",
    ccPerfectTimingHeroStats: "{rate}% answer rate · {answered} answered out of {total} calls",
    ccPerfectTimingRecommended: "Also recommended",
    ccPerfectTimingRecommendedShort: "Good",
    ccPerfectTimingBest: "Best",
    ccPerfectTimingBestHour: "Best time to call: {hour}",
    ccPerfectTimingAnswerRate: "Answer rate",
    ccPerfectTimingAnsweredCalls: "Answered calls",
    ccPerfectTimingPeriodTotalCalls: "Total calls in period",
    ccPerfectTimingOverallRate: "Overall answer rate",
    ccPerfectTimingByHour: "Breakdown by hour",
    ccPerfectTimingShowingHours: "{count} hours with call activity",
    ccPerfectTimingChartHint: "Bar height = answer rate for that hour",
    ccPerfectTimingTableTime: "Time",
    ccPerfectTimingTableCalls: "Total calls",
    ccPerfectTimingTableAnswered: "Answered",
    ccPerfectTimingTableRate: "Answer rate",
    ccPerfectTimingNoData: "Not enough call history for timing insights",
    ccPerfectTimingSample: "Based on {count} calls in selected period",
    ccVsLastPeriodShort: "vs. last period",
    ccDebtorSegments: "Debtor Segments",
    ccIndividuals: "Individuals",
    ccSMEs: "SMEs",
    ccCorporates: "Corporates",
    ccBestPerformingAgents: "Best Performing Agents",
    ccAgentRatingHelp:
      "Star rating (0–5) for the selected period combines: 35% call coverage (called targets ÷ assigned targets), 35% paid conversion (paid targets ÷ called targets), and 30% recovery relative to the top agent on the team. Agents with no activity in this period show 0.0.",
    ccAgentRatingHelpLabel: "How agent rating is calculated",
    ccAgentTableShowing: "Showing {shown} of {total} agents",
    ccAgentTableSearchPlaceholder: "Search agents by name...",
    ccAgentTableShowAll: "Show all agents",
    ccAgentTableShowTop: "Show top {count}",
    ccAgentTableMoreHidden: "Show {count} more agents",
    ccAgentTableNoMatches: "No agents match your search",
    ccAgent: "Agent",
    ccCalls: "Calls",
    ccRating: "Rating",
    ccShowDetails: "Show details",
    ccOnTrackCoverage: "On track for 80% coverage target",
    ccCallOutcomes: "Call outcomes",
    ccOutcomeAns: "Ans",
    ccOutcomeNoAns: "No Ans",
    ccOutcomeBusy: "Busy",
    ccOutcomeCallback: "Callbk",
    ccOutcomePaid: "Paid",
    ccOutcomeWrong: "Wrong",
    ccOutcomeDnc: "DNC",

    // Weekly tracker
    ccWeeklyTrackerTitle: "Weekly Call Tracker",
    ccWeeklyTrackerSubtitle: "Daily call counts by ticket status (call type) for selected agents. Sun–Thu work week.",
    ccWeeklyTrackerWeekOf: "Week of (Sunday)",
    ccWeeklyTrackerAgents: "Call agents",
    ccWeeklyTrackerSearchAgents: "Search and select agents…",
    ccWeeklyTrackerSelectedCount: "{count} of {total} agents selected",
    ccWeeklyTrackerApplyHint: "Select agents, then click Sync dashboard to load data.",
    ccWeeklyTrackerSelectAll: "Select all",
    ccWeeklyTrackerClearAll: "Clear",
    ccWeeklyTrackerSelectAgents: "Select at least one call agent.",
    ccWeeklyTrackerLoadFailed: "Failed to load weekly tracker",
    ccWeeklyTrackerExportFailed: "Failed to download Excel report",
    ccWeeklyTrackerExportSuccess: "Weekly tracker Excel downloaded",
    ccWeeklyTrackerExporting: "Exporting…",
    ccWeeklyTrackerDownloadExcel: "Export Excel",
    ccWeeklyTrackerNeedsAttention: "Needs attention",
    ccWeeklyTrackerCommitments: "Commitments logged",
    ccWeeklyTrackerRecoveredThisWeek: "Recovered this week",
    ccWeeklyTrackerCallsCompleted: "Calls Completed",
    ccWeeklyTrackerFollowupsDone: "Follow-ups Done",
    ccWeeklyTrackerDailyCalls: "Daily Calls",
    ccWeeklyTrackerTargetCallsPlaced: "{done} of {target} target calls placed this week",
    ccWeeklyTrackerCallType: "Call type (status)",
    ccWeeklyTrackerWeeklyTotal: "Weekly total",
    ccWeeklyTrackerWeeklyTarget: "Weekly target",
    ccWeeklyTrackerCombinedCalls: "Combined calls (all agents)",
    ccWeeklyTrackerCallsThisWeek: "Calls this week",
    ccWeeklyTrackerNoData: "No weekly tracker data for the selected filters.",
    ccWeeklyTrackerKpiPromise: "Promise-to-pay achieved",
    ccWeeklyTrackerKpiCollected: "Amount collected (OMR)",
    ccWeeklyTrackerKpiMissed: "Missed follow-ups",
    ccMenuWeeklyTracker: "Weekly Tracker",
    ccWeeklyTrackerQuickDesc: "Agent targets by status with Excel export",

    // Settings
    ccWorkflowSettingsTitle: "Ticket Status Settings",
    ccWorkflowSettingsSubtitle: "Add custom ticket statuses or rename built-in ones for your workspace.",
    ccAddNewStatus: "Add New Status",
    ccStatusKeyLabel: "Status key",
    ccStatusKeyPlaceholder: "e.g. AWAITING_DOCS",
    ccStatusKeyHint: "Unique code used in APIs and filters. Auto-generated from the display name.",
    ccStatusKeyInvalid: "Use letters, numbers, and underscores (start with a letter).",
    ccStatusSettingsViewOnly: "You can view statuses here. Ask an admin with Settings access to add or edit them.",
    ccStatusManageFromTickets: "Use these statuses on the Tickets page after saving.",
    ccConfiguration: "Configuration",
    ccBaseMapping: "Base Mapping",
    ccDisplayName: "Display Name",
    ccDisplayNamePlaceholder: "e.g. Waiting for Customer",
    ccColorTheme: "Color Theme",
    ccLiveUiPreview: "Live UI Preview",
    ccPreviewStatus: "Preview Status",
    ccPreviewTicketLedger: "Will appear in Ticket Ledger",
    ccFinalStatus: "Final Status",
    ccFinalStatusHint: "Closing this ticket stops automation retries.",
    ccEnabled: "Enabled",
    ccSaveWorkflowStatus: "Save Workflow Status",
    ccActiveStatusLibrary: "Active Status Library",
    ccSyncStatuses: "Sync Statuses",
    ccNoCustomStatuses: "No custom statuses configured yet.",
    ccFinal: "Final",
    ccActive: "Active",
    ccInactive: "Inactive",
    ccStatusProTipTitle: "Status Pro-Tip",
    ccStatusProTipBody:
      'Use high-contrast colors (like Red or Orange) for statuses that require urgent Agent attention, and muted tones for final statuses like "Completed."',
    ccStatusKeyRequired: "Status key is required",
    ccNameRequired: "Name is required",
    ccInvalidHexColor: "Invalid Hex color",
    ccToastStatusSaved: "Status configuration saved",
    ccToastStatusSaveFailed: "Failed to save status",
    ccToastLoadStatusesFailed: "Failed to load statuses",
    ccStatusLibrary: "Status Library",
    ccEditStatus: "Edit Status",
    ccUpdateWorkflowStatus: "Update Status",
    ccCancelEdit: "Cancel",
    ccStatusKeyLockedHint: "Status key cannot be changed after creation.",
    ccEdit: "Edit",
    ccDelete: "Delete",
    ccActivate: "Activate",
    ccDeactivate: "Deactivate",
    ccBuiltInStatus: "Built-in",
    ccToastStatusUpdated: "Status updated",
    ccToastStatusUpdateFailed: "Failed to update status",
    ccToastStatusDeleted: "Status deleted",
    ccToastStatusDeleteFailed: "Failed to delete status",
    ccConfirmDeleteStatus: "Delete this custom status? This cannot be undone.",
  },
  ar: {
    ccMenuTickets: "التذاكر",
    ccMenuAccountPlans: "خطط الدفع",
    ccMenuAccountPlanTypes: "قوالب الخطط",
    ccMenuAgentWorkspace: "إدخال التذاكر",
    ccMenuTicketBulkUpload: "رفع جماعي",
    ccMenuTicketUploadHistory: "سجل الرفع",
    ccMenuTicketColumns: "إعدادات الأعمدة",
    ccMenuTicketSettingsMerged: "إعدادات التذاكر",
    ccTicketSettingsPageTitle: "إعدادات التذاكر",
    ccTicketSettingsPageSubtitle: "قم بتكوين أعمدة التذاكر وسير عمل الحالات لمنظمتك.",
    ccTicketSettingsTabColumns: "الأعمدة",
    ccTicketSettingsTabStatuses: "الحالات",
    ccTicketSettingsNoTabAccess: "ليس لديك صلاحية لعرض إعدادات التذاكر.",
    ccAccountPlansPageTitle: "خطط الدفع",
    ccAccountPlansPageSubtitle: "جدولة تحصيل الأقساط وتذاكر تذكير الدفع التلقائية لكل حساب.",
    ccAccountPlansManageTypes: "إدارة قوالب الخطط",
    ccAccountPlanType: "قالب الخطة",
    ccAccountPlanSelectType: "اختر قالب خطة",
    ccAccountPlanTypeRequired: "قالب الخطة مطلوب",
    ccAccountPlanTypeSelectHint: "الجدول والتعيين والتذكيرات وإعدادات التذكرة تأتي من القالب المحدد.",
    ccAccountPlanTypesPageTitle: "قوالب الخطط",
    ccAccountPlanTypesPageSubtitle: "حدد قوالب جداول قابلة لإعادة الاستخدام لخطط الدفع.",
    ccAccountPlanTypesBackToPlans: "العودة إلى خطط الدفع",
    ccAccountPlanTypeCreate: "إنشاء قالب",
    ccAccountPlanTypeCreateTitle: "إنشاء قالب خطة",
    ccAccountPlanTypeEditTitle: "تعديل قالب خطة",
    ccAccountPlanTypeFormHint: "تُطبَّق إعدادات القالب على كل خطة دفع تُنشأ من هذا القالب.",
    ccAccountPlanTypeName: "اسم القالب",
    ccAccountPlanTypeDescription: "الوصف",
    ccAccountPlanTypeNameRequired: "اسم القالب مطلوب",
    ccAccountPlanTypeDefaultAgent: "وكيل الاتصال الافتراضي",
    ccAccountPlanTypeDefaultAgentOptional: "اختر عند إنشاء الخطة",
    ccAccountPlanTypeDefaultAgentHint: "اختياري. اتركه فارغاً لاختيار الوكيل عند إنشاء كل خطة دفع.",
    ccAccountPlanTypeActive: "نشط (متاح لخطط دفع جديدة)",
    ccAccountPlanTypesEmptyTitle: "لا توجد قوالب خطط بعد",
    ccAccountPlanTypesEmptyHint: "أنشئ قالباً لتوحيد جداول الأقساط قبل إضافة خطط الدفع.",
    ccAccountPlanTypeCreated: "تم إنشاء قالب الخطة",
    ccAccountPlanTypeUpdated: "تم تحديث قالب الخطة",
    ccAccountPlanTypeLoadFailed: "فشل تحميل قوالب الخطط",
    ccAccountPlanTypeSaveFailed: "فشل حفظ قالب الخطة",
    ccAccountPlanTypeInfoTitle: "كيف تعمل قوالب الخطط",
    ccAccountPlanTypeInfoBody:
      "تحدد القوالب تكرار الأقساط والتذكيرات وإعدادات التذكرة الافتراضية. كل خطة دفع تختار قالباً وتضيف المبلغ والتواريخ الخاصة بالحساب.",
    ccAccountPlanTypeKpiTotal: "إجمالي القوالب",
    ccAccountPlanTypeKpiActive: "القوالب النشطة",
    ccAccountPlanTypeKpiInUse: "قيد الاستخدام",
    ccAccountPlanTypeUsage: "الاستخدام",
    ccAccountPlanTypeUsageHint: "خطط دفع نشطة / إجمالي",
    ccAccountPlanTypeSearchPlaceholder: "البحث باسم القالب…",
    ccAccountPlanTypeDetailSubtitle: "إعدادات القالب والاستخدام",
    ccAccountPlanTypeDeactivateBlocked: "لا يمكن إلغاء التفعيل بينما تستخدم خطط دفع نشطة هذا القالب.",
    ccAccountPlanTypeUsageSummary: "يُستخدم في {count} خطة/خطط دفع.",
    ccAccountPlanTypeResultsLabel: "قوالب",
    ccAccountPlanTypeScheduleSummary: "{frequency} · {count} أقساط",
    ccAccountPlanTypeScheduleColumn: "الجدول",
    ccAccountPlanTypeEmptyFilteredTitle: "لا توجد قوالب تطابق الفلاتر",
    ccAccountPlanTypeEmptyFilteredHint: "جرّب تعديل البحث أو فلتر الحالة.",
    ccAccountPlanTypeKpiClickHint: "انقر على بطاقة لتصفية القائمة.",
    ccAccountPlanTypeDetailScheduleSection: "جدول الأقساط",
    ccAccountPlanTypeDetailTicketSection: "إعدادات التذكرة الافتراضية",
    ccAccountPlanTypeDetailUsageSection: "الاستخدام",
    ccAccountPlanTypeUsageEmpty: "غير مستخدم في أي خطط دفع بعد.",
    ccAccountPlanTypeInUseWarning:
      "يُستخدم هذا القالب في {count} خطة/خطط دفع. التغييرات تُطبَّق على الأقساط المستقبلية فقط.",
    ccAccountPlanTypeBreadcrumbPaymentPlans: "خطط الدفع",
    ccAccountPlanTypeFormSectionBasic: "المعلومات الأساسية",
    ccAccountPlanTypeFormSectionSchedule: "جدول الأقساط",
    ccAccountPlanTypeFormSectionTickets: "إعدادات التذكرة الافتراضية",
    ccAccountPlanTypeFormPreviewTitle: "ملخص القالب",
    ccAccountPlanTypeFormPreviewBody: "تذكير {reminder} · {assignment} · أولوية {priority}",
    ccAccountPlanCreate: "إنشاء خطة دفع",
    ccAccountPlanCreateTitle: "إنشاء خطة دفع",
    ccAccountPlanEditTitle: "تعديل خطة الدفع",
    ccAccountPlanDetailTitle: "تفاصيل خطة الدفع",
    ccAccountPlanAccount: "رقم الحساب",
    ccAccountPlanMobileNumbers: "رقم/أرقام الجوال",
    ccAccountPlanAmount: "مبلغ القسط (ريال عماني)",
    ccAccountPlanTotalAmount: "المبلغ الإجمالي (ريال عماني)",
    ccAccountPlanAmountColumn: "المبلغ",
    ccAccountPlanTotalAmountRequired: "المبلغ الإجمالي مطلوب",
    ccAccountPlanPerInstallmentHint: "حوالي {amount} ريال عماني لكل قسط قبل التقريب",
    ccAccountPlanPreviewSchedule: "معاينة الجدول",
    ccAccountPlanPreviewTitle: "معاينة الأقساط",
    ccAccountPlanPreviewEmpty: "أدخل المبلغ الإجمالي وعدد الأقساط وتاريخ البدء لمعاينة الجدول.",
    ccAccountPlanPreviewTotal: "الإجمالي: {amount} ريال عماني",
    ccAccountPlanInstallmentCount: "عدد الأقساط",
    ccAccountPlanFrequency: "التكرار",
    ccAccountPlanFrequency_ONE_TIME: "دفعة واحدة",
    ccAccountPlanOneTimeNote: "خطط الدفعة الواحدة تنشئ تذكرة دفع واحدة في تاريخ الاستحقاق.",
    ccAccountPlanStartDate: "تاريخ البدء (أول استحقاق)",
    ccAccountPlanAssignmentMode: "تعيين الوكيل",
    ccAccountPlanAssignedAgent: "وكيل الاتصال المعيّن",
    ccAccountPlanAutoAssign: "تعيين تلقائي",
    ccAccountPlanSelectAgent: "اختر وكيل الاتصال",
    ccAccountPlanReminderOffset: "تذكير الدفع",
    ccAccountPlanTicketPriority: "أولوية التذكرة",
    ccAccountPlanRemarksTemplate: "ملاحظات التذكرة الافتراضية",
    ccAccountPlanAmountRequired: "مبلغ القسط مطلوب",
    ccAccountPlanAmountMin: "يجب أن يكون مبلغ القسط أكبر من صفر",
    ccAccountPlanFrequencyRequired: "التكرار مطلوب",
    ccAccountPlanInstallmentCountRequired: "عدد الأقساط مطلوب",
    ccAccountPlanInstallmentCountMax: "لا يمكن أن يتجاوز عدد الأقساط 120",
    ccAccountPlanStartDateRequired: "تاريخ البدء مطلوب",
    ccAccountPlanAssignedAgentRequired: "وكيل الاتصال المعيّن مطلوب",
    ccAccountPlanProgress: "التقدم",
    ccAccountPlanNextDue: "الاستحقاق التالي",
    ccAccountPlanStatus: "الحالة",
    ccAccountPlanActions: "الإجراءات",
    ccAccountPlanView: "عرض",
    ccAccountPlanEdit: "تعديل",
    ccAccountPlanCancel: "إلغاء الخطة",
    ccAccountPlanCancelConfirm: "إلغاء خطة الدفع هذه وأي أقساط معلقة؟",
    ccAccountPlanCancelFailed: "فشل إلغاء خطة الدفع",
    ccAccountPlanInstallmentSchedule: "جدول الأقساط",
    ccAccountPlanDueDate: "تاريخ الاستحقاق",
    ccAccountPlanReminderDate: "تاريخ التذكير",
    ccAccountPlanTicket: "التذكرة",
    ccAccountPlanOpenTicket: "فتح التذكرة",
    ccAccountPlansEmpty: "لا توجد خطط دفع.",
    ccAccountPlansLoadFailed: "فشل تحميل خطط الدفع",
    ccAccountPlansSaveFailed: "فشل حفظ خطة الدفع",
    ccAccountPlanFilterStatus: "الحالة",
    ccAccountPlanFilterAll: "الكل",
    ccAccountPlanFiltersTitle: "الفلاتر",
    ccAccountPlanResultsLabel: "خطط دفع",
    ccAccountPlanInstallmentsLabel: "أقساط",
    ccAccountPlansEmptyTitle: "لا توجد خطط دفع بعد",
    ccAccountPlansEmptyHint: "أنشئ خطة دفع لجدولة تذاكر التحصيل المتكررة والتذكيرات للحساب.",
    ccClearFilters: "مسح الفلاتر",
    ccListFiltersTitle: "الفلاتر",
    ccAlertsResultsLabel: "تنبيهات",
    ccAlertsEmptyTitle: "لم يتم العثور على تنبيهات",
    ccAlertsEmptyHint: "جرّب تعديل الفلاتر أو تحقق لاحقاً من تحذيرات SLA الجديدة.",
    ccNotificationsResultsLabel: "إشعارات",
    ccNotificationsEmptyTitle: "لا توجد إشعارات بعد",
    ccNotificationsUnreadOnly: "غير المقروءة فقط",
    ccNotificationsColumnTitle: "العنوان",
    ccNotificationsColumnMessage: "الرسالة",
    ccNotificationsColumnDate: "التاريخ",
    ccRead: "مقروء",
    ccUnread: "غير مقروء",
    ccOpen: "فتح",
    ccAccountPlanStatus_ACTIVE: "نشطة",
    ccAccountPlanStatus_COMPLETED: "مكتملة",
    ccAccountPlanStatus_CANCELLED: "ملغاة",
    ccAccountPlanStatus_SCHEDULED: "مجدولة",
    ccAccountPlanStatus_TICKET_CREATED: "تم إنشاء التذكرة",
    ccAccountPlanStatus_SKIPPED: "تم التخطي",
    ccMenuInbox: "صندوق الوارد",
    ccMenuBrowserPhone: "هاتف المتصفح",
    ccMenuCampaigns: "الحملات",
    ccMenuAnalytics: "لوحة المعلومات",
    ccMenuTicketSettings: "إعدادات الحالة",
    ccMenuWhatsAppSettings: "إعدادات واتساب",
    ccMenuNotifications: "الإشعارات",
    ccMenuPaymentReminders: "تذكيرات الدفع",
    ccMenuPaymentConfirmations: "تأكيدات الدفع",
    ccMenuOrganizations: "المنظمات",
    ccMenuOrganizationsTooltip: "إدارة منظمات المنصة",
    ccDisabled: "معطل",
    ccOrgPageTitle: "المنظمات",
    ccOrgTableOrganization: "المنظمة",
    ccOrgStatTotal: "إجمالي المنظمات",
    ccOrgStatTotalHint: "وفق عوامل التصفية الحالية",
    ccOrgStatActive: "نشطة في الصفحة",
    ccOrgStatActiveHint: "من {count} صف",
    ccOrgStatCalling: "المكالمات مفعّلة",
    ccOrgStatCallingHint: "في هذه الصفحة ({count} صف)",
    ccOrgPageSubtitle: "إنشاء وإدارة منظمات المستأجرين والميزات وإعدادات التكامل.",
    ccOrgCreate: "منظمة جديدة",
    ccOrgInfoTitle: "منظمات المنصة",
    ccOrgInfoBody:
      "كل منظمة هي مستأجر معزول بمستخدميه وتذاكره وتكاملاته. استخدم أيقونات الإجراءات لتكوين قوالب إثبات الدفع وتأكيد الدفع وواتساب لكل منظمة.",
    ccOrgLoadFailed: "فشل تحميل المنظمات",
    ccOrgResultsLabel: "منظمات",
    ccOrgSearchPlaceholder: "البحث بالرمز أو الاسم...",
    ccOrgSortBy: "ترتيب حسب",
    ccOrgSortDirection: "الاتجاه",
    ccOrgSortAsc: "تصاعدي",
    ccOrgSortDesc: "تنازلي",
    ccOrgSortCode: "الرمز",
    ccOrgSortName: "الاسم",
    ccOrgSortCreated: "تاريخ الإنشاء",
    ccOrgIncludeInactive: "تضمين غير النشطة",
    ccOrgPerPage: "لكل صفحة",
    ccOrgEmptyTitle: "لا توجد منظمات بعد",
    ccOrgEmptyHint: "أنشئ أول منظمة لإضافة مستأجر جديد.",
    ccOrgEmptyFilteredTitle: "لا توجد منظمات تطابق الفلاتر",
    ccOrgEmptyFilteredHint: "جرّب مصطلح بحث مختلف أو تضمين المنظمات غير النشطة.",
    ccOrgCode: "الرمز",
    ccOrgName: "الاسم",
    ccOrgCalling: "المكالمات",
    ccOrgAiCalling: "مكالمات الذكاء الاصطناعي",
    ccOrgCreatedAt: "تاريخ الإنشاء",
    ccOrgPaymentProofTemplate: "قالب إثبات الدفع",
    ccOrgPaymentConfirmation: "تأكيد الدفع",
    ccOrgWhatsApp: "إعدادات واتساب",
    ccOrgTelephony: "مزود الاتصالات",
    ccTelephonyProvider: "مزود الاتصالات",
    ccTelephonyProviderD2c: "D2C Cloud PBX",
    ccTelephonyProviderGrandstream: "Grandstream UCM",
    ccTelephonyD2cSection: "تجاوزات Cloud PBX",
    ccTelephonyD2cHint: "اتركه فارغًا لاستخدام إعدادات الخادم الافتراضية.",
    ccTelephonyD2cBaseUrl: "عنوان PBX",
    ccTelephonyD2cAccessId: "معرف الوصول",
    ccTelephonyD2cAccessKey: "مفتاح الوصول",
    ccTelephonyGrandstreamSection: "اتصال Grandstream UCM",
    ccTelephonyGsBaseUrl: "عنوان UCM API",
    ccTelephonyGsUsername: "اسم مستخدم API",
    ccTelephonyGsPassword: "كلمة مرور API",
    ccTelephonyGsRecordingBaseUrl: "عنوان التسجيل",
    ccTelephonyGsSipDomain: "نطاق SIP",
    ccTelephonyGsSipWssUrl: "رابط SIP WSS",
    ccTelephonyGsSipWsUrl: "رابط SIP WS (اختياري)",
    ccTelephonyGsCdrEndpoint: "مسار CDR",
    ccTelephonyGsCdrSyncEnabled: "تفعيل مزامنة CDR المجدولة",
    ccTelephonyGsWebrtcEnabled: "تفعيل WebRTC في المتصفح",
    ccTelephonyGsWebhookSecret: "سر webhook للأحداث",
    ccTelephonyGsSipDomainRequired: "نطاق SIP مطلوب لـ Grandstream",
    ccTelephonyGsSipWssRequired: "رابط SIP WSS أو WS مطلوب لـ Grandstream",
    ccTelephonySecretConfigured: "تم حفظ قيمة مسبقًا؛ اتركه فارغًا للإبقاء عليها.",
    ccTelephonyLoadFailed: "فشل تحميل إعدادات الاتصالات",
    ccTelephonySaveSuccess: "تم حفظ إعدادات الاتصالات",
    ccTelephonySaveFailed: "فشل حفظ إعدادات الاتصالات",
    ccOrgDeactivate: "إلغاء التفعيل",
    ccOrgDeactivateTitle: "إلغاء تفعيل المنظمة",
    ccOrgDeactivateConfirm: "إلغاء تفعيل \"{name}\"؟ لن يتمكن المستخدمون من تسجيل الدخول تحت هذه المنظمة.",
    ccOrgAccessDenied: "يمكن لمسؤولي المنصة فقط إدارة المنظمات.",
    ccOrgLogoUploaded: "تم رفع شعار المنظمة",
    ccOrgLogoUploadFailed: "فشل رفع شعار المنظمة",
    ccOrgNameRequired: "اسم المنظمة مطلوب",
    ccOrgCodeRequired: "رمز المنظمة مطلوب",
    ccOrgCodeInvalid: "يجب أن يستخدم الرمز أحرفًا كبيرة أو أرقامًا أو _ أو -",
    ccOrgCodePlaceholder: "ACME",
    ccOrgNamePlaceholder: "Acme Collections",
    ccOrgCreateSuccess: "تم إنشاء المنظمة",
    ccOrgUpdated: "تم تحديث المنظمة",
    ccOrgSaveFailed: "فشل حفظ المنظمة",
    ccOrgDeactivated: "تم إلغاء تفعيل المنظمة",
    ccOrgDeactivateFailed: "فشل إلغاء تفعيل المنظمة",
    ccOrgTemplateLoadFailed: "فشل تحميل قالب إثبات الدفع",
    ccOrgTemplateSaved: "تم حفظ قالب إثبات الدفع",
    ccOrgTemplateSaveFailed: "فشل حفظ قالب إثبات الدفع",
    ccOrgCreateTitle: "إنشاء منظمة",
    ccOrgEditTitle: "تعديل المنظمة",
    ccOrgCreateFormHint: "اختر رمزًا فريدًا للمستأجر. لا يمكن تغييره بعد الإنشاء.",
    ccOrgEditFormHint: "حدّث تفاصيل المنظمة وميزات المكالمات والشعار.",
    ccOrgCodeHint: "أحرف كبيرة وأرقام و _ أو - (مثل ACME_CO)",
    ccOrgPageSize: "حجم الصفحة",
    ccOrgRetiredCode: "رمز مُنهى",
    ccOrgDefaultProtected: "لا يمكن إلغاء تفعيل منظمة المنصة الافتراضية",
    ccOrgActionsLegend: "إجراءات الصف: قالب إثبات الدفع، تأكيد الدفع، واتساب، تعديل، إلغاء التفعيل",
    ccOrgActive: "نشطة",
    ccOrgCallingFeatures: "ميزات المكالمات",
    ccOrgSectionIdentity: "بيانات المنظمة",
    ccOrgSectionIdentityHint: "الرمز ثابت بعد الإنشاء؛ يمكن تحديث الاسم والحالة في أي وقت.",
    ccOrgSectionBranding: "الهوية البصرية",
    ccOrgCallingChannelsHint: "تحكم في أماكن اتصال الوكلاء. إعدادات PBX في قسم الاتصالات.",
    ccOrgCallingEnabled: "تفعيل المكالمات اليدوية",
    ccOrgCallingMasterHint: "المفتاح الرئيسي لسجل المكالمات والحملات والتسجيلات والهاتف.",
    ccOrgWebCallingEnabled: "المكالمات عبر الويب",
    ccOrgWebCallingHint: "هاتف المتصفح و WebRTC في تطبيق الويب.",
    ccOrgDesktopCallingEnabled: "المكالمات عبر سطح المكتب",
    ccOrgDesktopCallingHint: "تطبيق WASL مع SIP الأصلي (UDP/TCP).",
    ccOrgAiCallingEnabled: "مكالمات الذكاء الاصطناعي",
    ccOrgAiCallingHint: "حملات صوتية آلية بالذكاء الاصطناعي.",
    ccOrgCallingMasterOffNote: "فعّل المكالمات اليدوية أعلاه لتفعيل الويب أو سطح المكتب أو الذكاء الاصطناعي.",
    ccOrgActiveHint: "لا يمكن للوكلاء اختيار المنظمات غير النشطة.",
    ccOrgLogoFormatsHint: "PNG أو JPG أو GIF أو WebP.",
    ccOrgTelephonyProviderUnset: "الاتصالات غير مهيأة",
    ccOrgChannelsWeb: "ويب",
    ccOrgChannelsDesktop: "سطح المكتب",
    ccOrgLogo: "الشعار",
    ccOrgLogoHint: "يظهر على مستندات إثبات الدفع. يُفضل PNG أو JPG.",
    ccOrgNoLogo: "لم يتم رفع شعار",
    ccOrgLogoUploading: "جاري الرفع...",
    ccOrgLogoUpload: "رفع الشعار",
    ccNotificationsTitle: "الإشعارات",
    ccNotificationsSubtitleOne: "إشعار واحد غير مقروء",
    ccNotificationsSubtitleMany: "{count} إشعارات غير مقروءة",
    ccNotificationsEmpty: "لا توجد إشعارات بعد.",
    ccNotificationsMarkAllRead: "تعليم الكل كمقروء",
    ccNotificationsMarkAllReadSuccess: "تم تعليم جميع الإشعارات كمقروءة",
    ccNotificationsMarkAllReadFailed: "فشل تعليم جميع الإشعارات كمقروءة",
    ccNotificationsMarkReadFailed: "فشل تعليم الإشعار كمقروء",
    ccNotificationsLoadFailed: "فشل تحميل الإشعارات",
    ccNotificationsPaymentAlertTitle: "الدفع مستحق اليوم",
    ccNotificationsPaymentConfirmedTitle: "تم تأكيد الدفع",
    ccNotificationsRemindersSection: "تذكيرات الدفع",
    ccNotificationsConfirmationsSection: "تأكيدات الدفع",
    ccNotificationsRemindersEmpty: "لا توجد تذكيرات دفع بعد.",
    ccNotificationsConfirmationsEmpty: "لا توجد تأكيدات دفع بعد.",
    ccWhatsAppSettingsTitle: "إعدادات واتساب",
    ccWhatsAppSettingsSubtitle: "قم بتكوين بيانات اعتماد WhatsApp لهذه المنظمة (Meta مباشر أو Flowmatical).",
    ccWhatsAppProvider: "المزود",
    ccWhatsAppProviderMetaDirect: "Meta API مباشر",
    ccWhatsAppProviderFlowmatical: "Flowmatical",
    ccWhatsAppFlowmaticalBaseUrl: "عنوان Flowmatical الأساسي",
    ccWhatsAppFlowmaticalBaseUrlRequired: "عنوان Flowmatical الأساسي مطلوب عند تفعيل واتساب",
    ccWhatsAppFlowmaticalApiToken: "رمز Flowmatical API",
    ccWhatsAppFlowmaticalWebhookSecret: "سر webhook Flowmatical",
    ccWhatsAppFlowmaticalWebhookUrlLabel: "سجّل عنوان webhook الوارد هذا في Flowmatical",
    ccWhatsAppFlowmaticalWebhookHint: "في Flowmatical انتقل إلى Settings > Integrations & API > Signed webhooks، الصق هذا العنوان، فعّل التسليم، وانسخ سر whsec_ المعروض مرة واحدة. لا توجّه callbacks الخاصة بـ Meta إلى هنا — Flowmatical يدير Meta على مستوى المنصة.",
    ccWhatsAppFlowmaticalApiTokenHint: "أنشئ رمز flw_ محدود النطاق مع messages:send (و templates:read عند الحاجة) من Settings > Integrations & API > API tokens.",
    ccWhatsAppFlowmaticalBaseUrlHint: "مضيف API فقط (الافتراضي https://api.flowmatical.com). لا تضف /api/v1/integrations/public.",
    ccWhatsAppEnabled: "تفعيل واتساب لهذه المنظمة",
    ccWhatsAppPhoneNumberId: "معرف رقم الهاتف",
    ccWhatsAppPhoneNumberIdRequired: "معرف رقم الهاتف مطلوب عند تفعيل واتساب",
    ccWhatsAppAccessToken: "رمز الوصول",
    ccWhatsAppWebhookVerifyToken: "رمز التحقق من Webhook",
    ccWhatsAppAppSecret: "سر التطبيق",
    ccWhatsAppApiVersion: "إصدار Graph API",
    ccWhatsAppApiVersionRequired: "إصدار API مطلوب",
    ccWhatsAppSecretConfiguredHint: "تم التكوين — اتركه فارغًا للاحتفاظ بالقيمة الحالية",
    ccWhatsAppSettingsHint: "يتم تخزين الأسرار لكل منظمة. اترك حقول الأسرار فارغة للاحتفاظ بالقيم الحالية.",
    ccWhatsAppSave: "حفظ إعدادات واتساب",
    ccWhatsAppSaveSuccess: "تم حفظ إعدادات واتساب",
    ccWhatsAppSaveFailed: "فشل حفظ إعدادات واتساب",
    ccWhatsAppLoadFailed: "فشل تحميل إعدادات واتساب",
    ccEmDash: "—",
    ccBack: "← رجوع",
    ccProfileTitle: "ملفي الشخصي",
    ccProfileSubtitle: "عرض وتحديث تفاصيل حسابك.",
    ccProfileName: "الاسم الكامل",
    ccProfileEmail: "البريد الإلكتروني",
    ccProfileRole: "الدور",
    ccProfileOrganization: "المنظمة",
    ccProfileAgentExtension: "رقم التحويلة",
    ccProfileSave: "حفظ الملف الشخصي",
    ccProfileUpdated: "تم تحديث الملف الشخصي",
    ccProfileLoadFailed: "فشل تحميل الملف الشخصي",
    ccProfileUpdateFailed: "فشل تحديث الملف الشخصي",
    ccProfileChangePassword: "تغيير كلمة المرور",
    ccProfileChangePasswordHint: "استخدم كلمة مرور قوية تحتوي على أحرف كبيرة وصغيرة ورقم ورمز خاص.",
    ccCurrentPassword: "كلمة المرور الحالية",
    ccNewPassword: "كلمة المرور الجديدة",
    ccConfirmPassword: "تأكيد كلمة المرور الجديدة",
    ccCurrentPasswordRequired: "كلمة المرور الحالية مطلوبة",
    ccNewPasswordRequired: "كلمة المرور الجديدة مطلوبة",
    ccConfirmPasswordRequired: "يرجى تأكيد كلمة المرور الجديدة",
    ccPasswordComplexity: "يجب أن تكون كلمة المرور 8 أحرف على الأقل وتتضمن أحرفًا كبيرة وصغيرة ورقمًا ورمزًا خاصًا",
    ccPasswordsMustMatch: "كلمتا المرور غير متطابقتين",
    ccPasswordChanged: "تم تغيير كلمة المرور بنجاح",
    ccPasswordChangeFailed: "فشل تغيير كلمة المرور",
    ccInvalidEmail: "أدخل بريدًا إلكترونيًا صالحًا",
    ccEmailRequired: "البريد الإلكتروني مطلوب",
    ccShowPassword: "إظهار كلمة المرور",
    ccHidePassword: "إخفاء كلمة المرور",
    ccClose: "إغلاق",
    ccReset: "إعادة تعيين",
    ccLoading: "جاري التحميل...",
    ccSaving: "جاري الحفظ...",
    ccDeleting: "جاري الحذف...",
    ccRefreshing: "جاري التحديث...",
    ccProcessing: "جاري المعالجة...",
    ccAll: "الكل",
    ccUnknown: "مجهول",
    ccUserFallback: "مستخدم",
    ccAccountNo: "رقم الحساب",
    ccMobileNumber: "رقم الجوال",
    ccCustomerName: "اسم العميل",
    ccAccountNumber: "رقم الحساب",
    ccAccountNumberRequired: "رقم الحساب مطلوب",
    ccPhoneNumber: "رقم الهاتف",
    ccPhoneNumberRequired: "رقم الهاتف مطلوب",
    ccStatus: "حالة",
    ccPriority: "أولوية",
    ccPriorityRequired: "الأولوية مطلوبة",
    ccRemarks: "ملاحظات",
    ccOutstanding: "متأخر",
    ccPrevious: "السابق",
    ccNext: "التالي",
    ccRows: "الصفوف",
    ccActions: "إجراءات",

    // WhatsApp
    ccWhatsApp: "واتساب",
    ccWhatsAppChat: "دردشة واتساب",
    ccWhatsAppOpenChat: "فتح دردشة واتساب",
    ccWhatsAppNoPhone: "لا يوجد رقم جوال على هذه التذكرة",
    ccWhatsAppSend: "إرسال",
    ccWhatsAppSendFailed: "فشل إرسال رسالة واتساب",
    ccWhatsAppMessageSent: "تم إرسال رسالة واتساب",
    ccWhatsAppEmpty: "لا توجد رسائل واتساب بعد. أرسل رسالة لبدء المحادثة.",
    ccWhatsAppComposePlaceholder: "اكتب رسالة واتساب...",
    ccWhatsAppChatWith: "الدردشة مع",
    ccWhatsAppThreadLoadFailed: "فشل تحميل رسائل واتساب",
    ccWhatsAppStatusSent: "تم الإرسال",
    ccWhatsAppStatusDelivered: "تم التسليم",
    ccWhatsAppStatusRead: "مقروءة",
    ccWhatsAppStatusFailed: "فشل",
    ccWhatsAppStatusPending: "قيد الانتظار",
    ccWhatsAppSelectNumber: "اختر رقم الجوال",
    ccWhatsAppCheckingNumber: "جاري التحقق من رقم واتساب...",
    ccWhatsAppNumberValid: "مسجل على واتساب",
    ccWhatsAppNumberInvalidHint: "هذا الرقم غير مسجل على واتساب. قد تفشل الرسائل في التسليم.",
    ccWhatsAppCheckFailed: "تعذر التحقق من رقم واتساب",
    ccWhatsAppAttachFile: "إرفاق ملف",
    ccWhatsAppSelectedFile: "الملف المحدد",
    ccWhatsAppAttachment: "مرفق",
    ccWhatsAppDownload: "تنزيل",
    ccWhatsAppDownloadFailed: "فشل تنزيل المرفق",
    ccWhatsAppNoPhoneHint: "أضف رقم جوال إلى التذكرة لبدء مراسلة واتساب.",
    ccWhatsAppEmptyTitle: "لا توجد رسائل بعد",
    ccWhatsAppComposerHint: "اضغط Enter للإرسال. Shift+Enter لسطر جديد. الحد الأقصى للمرفق 16 ميغابايت.",
    ccWhatsAppFileTooLarge: "يجب ألا يتجاوز حجم المرفق 16 ميغابايت.",
    ccWhatsAppMetaWebhookUrlLabel: "سجّل عنوان callback هذا في Meta Developer Dashboard",
    ccWhatsAppMetaWebhookHint:
      "في Meta Developer Dashboard → WhatsApp → Configuration، اضبط Callback URL على هذا العنوان واستخدم نفس رمز التحقق من webhook أعلاه.",
    ccWhatsAppCopyUrl: "نسخ العنوان",
    ccWhatsAppUrlCopied: "تم نسخ العنوان",
    ccTicketAttachments: "مرفقات التذكرة",
    ccTicketAttachFile: "إرفاق ملف",
    ccTicketAttachmentsHint: "ارفع المستندات أو الصور أو الملفات المتعلقة بهذه التذكرة.",
    ccTicketAttachmentsEmpty: "لا توجد ملفات مرفقة بهذه التذكرة بعد.",
    ccTicketAttachmentUploaded: "تم إرفاق الملف بالتذكرة",
    ccTicketAttachmentUploadFailed: "فشل إرفاق الملف",
    ccTicketAttachmentDownload: "تنزيل",
    ccTicketAttachmentDownloadFailed: "فشل تنزيل الملف",
    ccTicketAttachmentsLoadFailed: "فشل تحميل مرفقات التذكرة",
    ccConfirmDeleteAttachment: "حذف هذا المرفق؟ لا يمكن التراجع عن ذلك.",
    ccTicketAttachmentDeleted: "تم حذف المرفق",
    ccTicketAttachmentDeleteFailed: "فشل حذف المرفق",

    ccPaymentProof: "إثبات الدفع",
    ccPaymentProofOpen: "إثبات الدفع",
    ccPaymentProofHint: "أنشئ شهادة براءة ذمة PDF للعميل بعد التحقق من الدفع. عدّل الحقول، واحفظ PDF على هذه التذكرة، ثم نزّله أو أرسله عبر واتساب.",
    ccPaymentProofLoadFailed: "فشل تحميل إثبات الدفع",
    ccPaymentProofDraftSaved: "تم حفظ مسودة إثبات الدفع",
    ccPaymentProofDraftSaveFailed: "فشل حفظ مسودة إثبات الدفع",
    ccPaymentProofSaved: "تم حفظ إثبات الدفع",
    ccPaymentProofSaveFailed: "فشل حفظ إثبات الدفع",
    ccPaymentProofDownloadFailed: "فشل تنزيل إثبات الدفع",
    ccPaymentProofPreview: "معاينة PDF",
    ccPaymentProofSaveDraft: "حفظ المسودة",
    ccPaymentProofSaveDownload: "حفظ وتنزيل PDF",
    ccPaymentProofDownloadSaved: "تنزيل PDF المحفوظ",
    ccPaymentProofSendWhatsApp: "إرسال عبر واتساب",
    ccPaymentProofWhatsAppSent: "تم إرسال إثبات الدفع عبر واتساب",
    ccPaymentProofWhatsAppFailed: "فشل إرسال إثبات الدفع عبر واتساب",
    ccPaymentProofWhatsAppMessage: "رسالة واتساب",
    ccPaymentProofWhatsAppPhone: "رقم واتساب العميل",
    ccPaymentProofPhoneRequired: "اختر أو أدخل رقم واتساب",
    ccPaymentProofGeneratedAt: "آخر إنشاء",
    ccPaymentProofTemplateTitle: "قالب إثبات الدفع",
    ccPaymentProofTemplatePageHint: "اضبط تخطيط شهادة براءة الذمة والتسميات وتعيين الحقول ورسالة واتساب الافتراضية لمنظمتك.",
    ccPaymentProofTemplateSaved: "تم حفظ قالب إثبات الدفع",
    ccPaymentProofTemplateSaveFailed: "فشل حفظ قالب إثبات الدفع",
    ccPaymentProofTemplateLoadFailed: "فشل تحميل قالب إثبات الدفع",
    ccPaymentProofCertificateDetails: "تفاصيل الشهادة",
    ccPaymentProofLogoUrl: "رابط الشعار",
    ccPaymentProofLogoUpload: "شعار المنظمة",
    ccPaymentProofLogoChooseFile: "رفع الشعار",
    ccPaymentProofLogoEmpty: "لم يتم رفع شعار",
    ccPaymentProofLogoHint: "PNG أو JPEG أو GIF أو WebP حتى 5 ميجابايت. يظهر في رأس شهادة براءة الذمة.",
    ccPaymentProofLogoUploaded: "تم رفع الشعار",
    ccPaymentProofLogoUploadFailed: "فشل رفع الشعار",
    ccPaymentProofAssetsSection: "صور الشهادة",
    ccPaymentProofImageTooLarge: "حجم الصورة يتجاوز 5 ميجابايت",
    ccUploading: "جاري الرفع...",
    ccPaymentProofPrimaryColor: "اللون الأساسي",
    ccPaymentProofPrimaryColorInvalid: "يجب أن يكون اللون الأساسي قيمة hex صالحة (مثل #E87722)",
    ccPaymentProofCompanyNameEn: "اسم الشركة (إنجليزي)",
    ccPaymentProofCompanyNameAr: "اسم الشركة (عربي)",
    ccPaymentProofTitleEn: "عنوان الشهادة (إنجليزي)",
    ccPaymentProofTitleAr: "عنوان الشهادة (عربي)",
    ccPaymentProofBodyEn: "نص الشهادة (إنجليزي)",
    ccPaymentProofBodyAr: "نص الشهادة (عربي)",
    ccPaymentProofTelecomPartner: "اسم شركة الاتصالات",
    ccPaymentProofStampUpload: "صورة الختم",
    ccPaymentProofStampChooseFile: "رفع الختم",
    ccPaymentProofStampEmpty: "لم يتم رفع ختم",
    ccPaymentProofStampHint: "PNG أو JPEG أو GIF أو WebP حتى 5 ميجابايت. يظهر في تذييل الشهادة.",
    ccPaymentProofStampUploaded: "تم رفع الختم",
    ccPaymentProofStampUploadFailed: "فشل رفع الختم",
    ccPaymentProofSignatureUpload: "صورة التوقيع",
    ccPaymentProofSignatureChooseFile: "رفع التوقيع",
    ccPaymentProofSignatureEmpty: "لم يتم رفع توقيع",
    ccPaymentProofSignatureHint: "PNG أو JPEG أو GIF أو WebP حتى 5 ميجابايت. يظهر في تذييل الشهادة.",
    ccPaymentProofSignatureUploaded: "تم رفع التوقيع",
    ccPaymentProofSignatureUploadFailed: "فشل رفع التوقيع",
    ccPaymentProofWhatsAppTemplate: "قالب رسالة واتساب الافتراضية",
    ccPaymentProofFieldMappings: "تعيين الحقول",
    ccPaymentProofMappingNone: "بدون",
    ccPaymentProofColumnsLoadFailed: "تعذر تحميل أعمدة التذاكر",
    ccPaymentProofFieldLabels: "تسميات الحقول",
    ccPaymentProofTemplateAdmin: "قالب إثبات الدفع",
    ccSave: "حفظ",

    ccInboxTitle: "صندوق وارد مركز الاتصالات",
    ccInboxSubtitle: "بدء التتبع وإجراء المكالمات خارجياً وتحديث حالة دورة الحياة",
    ccBrowserPhoneTitle: "هاتف المتصفح",
    ccBrowserPhoneSubtitle: "سجّل الامتداد وتعامل مع المكالمات مباشرة من داخل تطبيق الويب.",
    ccManualCall: "مكالمة يدوية",
    ccAiTriggered: "يُشغَّل بواسطة الذكاء الاصطناعي",
    ccPhoneOptionalPlaceholder: "اختياري",
    ccPhoneOptionalHint: "اختياري. اترك فارغاً لاستخدام رقم الهاتف المحمول المخزن في الحساب.",
    ccManualCallHint: "عند اتصال هاتف المتصفح، تُجرى المكالمات داخل التطبيق عبر Linkus WebRTC. يتم ربط التذاكر الحالية للحساب؛ ولا يتم إنشاء تذكرة جديدة.",
    ccAiCallHint: "يتم أخذ سياق المكالمة الذكية (الرصيد المتبقي وآخر تحديث) من سجل الحساب، وليس من هذا النموذج. يتم ربط التذاكر الحالية للحساب ولا يتم إنشاء تذكرة جديدة؛ يتم تحديث دورة حياة المكالمة يدويًا أثناء انقطاع الخدمة.",
    ccInitiateCallNow: "ابدأ المكالمة الآن",
    ccLiveStatus: "الحالة المباشرة",
    ccMockTelephonyHint:
      "لم يتم إعداد جسر FreeSWITCH أو أن الاتصالات الهاتفية مُحاكاة — معرف المزود اصطناعي (mock-freeswitch-…). التذكرة والمكالمة ما زالتا صالحتين؛ قد تكون الأحداث المباشرة محدودة حتى يتم ربط جسر HTTP.",
    ccCallId: "معرف المكالمة",
    ccNotStarted: "لم يبدأ",
    ccTrackingAccount: "حساب التتبع",
    ccNotSet: "غير محدد",
    ccStream: "بث",
    ccDuration: "المدة",
    ccCallType: "نوع المكالمة",
    ccAccountNoUpper: "رقم الحساب",
    ccDialed: "تم الاتصال به",
    ccCallTicket: "تذكرة المكالمة",
    ccViewTicketInList: "عرض التذكرة في القائمة",
    ccListenRecording: "الاستماع إلى التسجيل",
    ccRecordingProcessing: "جاري معالجة التسجيل…",
    ccRecordingPlaybackFailed: "فشل التشغيل. حدّث التذكرة أو أعد المحاولة بعد قليل.",
    ccCallHistory: "سجل المكالمات",
    ccNoCallsFound: "لا توجد مكالمات",
    ccUnknownAccount: "حساب غير معروف",
    ccRecordingPending: "التسجيل قيد الانتظار – متاح بعد انتهاء المكالمة.",
    ccManualStatusPanelHint: "تم بدء المكالمة في النظام. يرجى إجراء المكالمة يدويًا وتحديث الحالة.",
    ccMarkRinging: "وضع علامة على الرنين",
    ccMarkAnswered: "وضع علامة على الإجابة",
    ccMarkFailed: "وضع علامة على فشل",
    ccCompleteCallNotes: "إكمال المكالمة (ملاحظات اختيارية)",
    ccSummaryOptional: "ملخص (اختياري)",
    ccRecordingUrlOptional: "عنوان URL للتسجيل (اختياري)",
    ccMarkCompleted: "وضع علامة على اكتمال",
    ccEventHistory: "سجل الأحداث",
    ccTimelineInitial: "أولي",
    ccTimelineRealtime: "في الوقت الفعلي",
    ccStreamConnecting: "جاري الاتصال…",
    ccStreamConnected: "متصل",
    ccStreamReconnecting: "إعادة الاتصال…",
    ccStreamClosed: "مغلق",
    ccStreamDisconnected: "قطع الاتصال",
    ccCallTypeAiAutomated: "مؤتمتة ذكية",
    ccCallTypeManual: "يدوي",
    ccCallTypeOutbound: "صادر",
    ccToastCallInitiated: "تم بدء المكالمة في النظام. يرجى إجراء المكالمة يدويًا وتحديث الحالة.",
    ccToastCallInitiatedClickToCall: "تم بدء المكالمة — أجب على هاتفك عند رنينه.",
    ccClickToCallHint: "أدخل الرقم واضغط اتصال. سيرن امتدادك أولاً، ثم العميل.",
    ccInitiatingCall: "جاري بدء المكالمة…",
    ccMakeCall: "اتصل بالعميل",
    ccGlobalDialerTitle: "الهاتف",
    ccDialerCall: "اتصال",
    ccGlobalDialerOpen: "فتح لوحة الاتصال",
    ccGlobalDialerMinimize: "تصغير لوحة الاتصال",
    ccGlobalDialerConnect: "اتصال الهاتف",
    ccDisconnectPhone: "قطع الاتصال",
    ccGlobalDialerConnecting: "جاري الاتصال…",
    ccGlobalDialerReady: "جاهز للاتصال",
    ccGlobalDialerOnCall: "في مكالمة",
    ccGlobalDialerIncoming: "مكالمة واردة",
    ccCallMute: "كتم",
    ccCallUnmute: "إلغاء الكتم",
    ccCallHold: "تعليق",
    ccCallResume: "استئناف",
    ccCallHangUp: "إنهاء",
    ccCallEndCall: "إنهاء المكالمة",
    ccCallKeypad: "لوحة المفاتيح",
    ccCallContacts: "جهات الاتصال",
    ccCallNote: "ملاحظة",
    ccActiveOutboundCall: "مكالمة صادرة نشطة",
    ccActiveInboundCall: "مكالمة واردة نشطة",
    ccCallOnHold: "معلّقة",
    ccCallOutbound: "صادرة",
    ccCallInbound: "واردة",
    ccIncomingCallAccept: "قبول",
    ccIncomingCallReject: "رفض",
    ccIncomingCallAccount: "الحساب",
    ccUnknownCaller: "متصل غير معروف",
    ccGlobalDialerTapToConnect: "اضغط للاتصال",
    ccGlobalDialerEnterToCall: "أدخل الرقم واضغط اتصال",
    ccDialerEnterNumber: "أدخل الرقم",
    ccPhoneStatusReady: "جاهز",
    ccPhoneStatusUnavailable: "غير متاح",
    ccPhoneStatusEnded: "انتهت المكالمة",
    ccPhoneStatusOffline: "غير متصل",
    ccPhoneAdvancedSettings: "الصوت وسجل المكالمات",
    ccBrowserPhonePageHint: "اتصل بالأرقام أدناه. في الشاشات الأخرى استخدم زر الهاتف أسفل اليمين.",
    ccGlobalDialerNavigateHint: "يمكنك الانتقال إلى أي شاشة أثناء المكالمة.",
    ccGlobalDialerOpenBrowserPhone: "فتح هاتف المتصفح",
    ccGlobalDialerResetPhone: "إعادة تعيين الهاتف",
    ccToastBrowserPhoneConnecting: "هاتف المتصفح قيد الاتصال. حاول مرة أخرى بعد لحظة.",
    ccToastBrowserPhoneNotReady:
      "هاتف المتصفح غير مسجل. تحقق من الامتداد/كلمة مرور SIP أو افتح لوحة الاتصال للاتصال.",
    ccToastBrowserPhoneOfficeNetworkRequired:
      "المكالمات عبر المتصفح متاحة فقط على شبكة المكتب. اتصل بـ Wi-Fi المكتب ثم حاول مرة أخرى.",
    ccToastNativeSipRegistrationFailed:
      "تعذر تسجيل هاتف سطح المكتب على PBX. استخدم شبكة المكتب أو VPN، وتحقق من الامتداد وكلمة مرور SIP، وأغلق Wave Desktop لنفس الامتداد.",
    ccToastBrowserPhoneAuthFailed:
      "تعذر تسجيل امتداد الهاتف. تحقق من امتداد الوكيل وكلمة مرور SIP في إدارة المستخدمين.",
    ccBrowserPhoneExtensionMissing:
      "لم يتم تعيين امتداد هاتف لحسابك. اطلب من المسؤول إضافة الامتداد والبريد الإلكتروني في إدارة المستخدمين.",
    ccBrowserPhoneExtensionMismatch:
      "تعذر العثور على امتداد هاتفك في PBX. اطلب من المسؤول التحقق من الامتداد.",
    ccBrowserPhoneUnexpectedError:
      "فشل إعداد الهاتف. حاول مرة أخرى أو تواصل مع المسؤول إذا استمرت المشكلة.",
    ccToastUnableInitiateCall: "غير قادر على بدء المكالمة",
    ccToastInitiateCallFirst: "ابدأ مكالمة أولاً لحل الحساب قبل تحديث الحالة.",
    ccToastCallStatusUpdated: "تم تحديث حالة المكالمة إلى",
    ccToastLinkedTicketNotFound: "لم يتم العثور على التذكرة المرتبطة. قد تكون قد حُذفت.",
    ccToastCallStatusUpdateFailed: "فشل في تحديث الحالة إلى",
    ccTicketsTitle: "تذاكر الدعم",
    ccTicketsSubtitle: "إدارة وتتبع تذاكر التحصيل عبر مساحة عمل {org}.",
    ccAddNewTicket: "أضف تذكرة جديدة",
    ccNewTicket: "تذكرة جديدة",
    ccOutstandingRemarks: "المستحق والملاحظات",
    ccShowingTicketsCount: "عرض {from}-{to} من {total} تذكرة",
    ccTicketFilters: "مرشحات التذكرة",
    ccShowingPage: "عرض الصفحة",
    ccOf: "من",
    ccTotalTicket: "إجمالي التذكرة",
    ccTotalTickets: "إجمالي التذاكر",
    ccHideAdvancedFilters: "إخفاء المرشحات المتقدمة",
    ccShowAdvancedFilters: "إظهار المرشحات المتقدمة",
    ccSearchAccountPlaceholder: "رقم الحساب للبحث...",
    ccGlobalSearchPlaceholder: "البحث في التذاكر والوكلاء والمدينين...",
    ccCalled: "تم الاتصال به",
    ccCalledOnly: "يتم الاتصال فقط",
    ccNotCalled: "لم يتم الاتصال به",
    ccAssignee: "الوكيل المعين",
    ccUnassigned: "غير معين",
    ccAssignTicket: "تعيين التذكرة",
    ccReassignTicket: "إعادة تعيين التذكرة",
    ccAssignSelectedTickets: "تعيين المحدد",
    ccTicketAssignModalTitle: "تعيين التذكرة",
    ccTicketReassignModalTitle: "إعادة تعيين التذكرة",
    ccTicketBulkAssignModalTitle: "تعيين التذاكر المحددة",
    ccTicketAssignModalDescription: "اختر وكيل اتصال لهذه التذكرة.",
    ccTicketBulkAssignModalDescription: "اختر وكيل اتصال لجميع التذاكر المحددة.",
    ccTicketAssignSelectAgent: "وكيل الاتصال",
    ccTicketAssignSelectAgentPlaceholder: "اختر وكيلاً",
    ccTicketAssignConfirm: "حفظ التعيين",
    ccTicketUnassign: "إلغاء التعيين",
    ccTicketAssignSuccess: "تم تحديث تعيين التذكرة.",
    ccTicketUnassignSuccess: "تم إلغاء تعيين التذكرة.",
    ccTicketAssignFailed: "تعذر تحديث تعيين التذكرة.",
    ccTicketUnassignFailed: "تعذر إلغاء تعيين التذكرة.",
    ccTicketsBulkAssignSuccess: "تم تعيين التذاكر المحددة.",
    ccTicketAssignNoAgents: "لم يتم العثور على وكلاء اتصال مؤهلين لهذه المنظمة.",
    ccLoadingUsers: "جاري تحميل الوكلاء…",
    ccAllAssignees: "جميع الوكلاء",
    ccScopedToCurrentAgent: "محدود للوكيل الحالي",
    ccSortBy: "ترتيب حسب",
    ccSortOutstandingAsc: "المستحق: من الأقل إلى الأعلى",
    ccSortOutstandingDesc: "المستحق: من الأعلى إلى الأقل",
    ccSortPaymentDateAsc: "تاريخ الدفع: الأقدم أولاً",
    ccSortPaymentDateDesc: "تاريخ الدفع: الأحدث أولاً",
    ccSortCalledAtAsc: "المكالمات في: الأقدم أولاً",
    ccSortCalledAtDesc: "المكالمات في: الأحدث أولاً",
    ccSortRecentlyUpdated: "تم التحديث مؤخراً",
    ccStatusIds: "معرفات الحالة",
    ccStatusFiltersSelected: "تم تحديد مرشح (مرشحات) الحالة",
    ccMultiSelectTip: "نصيحة: اضغط على Ctrl/Cmd للاختيار من متعدد",
    ccMinOutstanding: "الحد الأدنى المستحق",
    ccMaxOutstanding: "الحد الأقصى المستحق",
    ccPaymentDateFrom: "تاريخ الدفع من",
    ccPaymentDateTo: "تاريخ الدفع إلى",
    ccApplyFilters: "تطبيق المرشحات",
    ccDeleteSelected: "حذف المحدد",
    ccNoTicketsFound: "لم يتم العثور على تذاكر",
    ccNoTicketsHint: "حاول تعديل المرشحات.",
    ccNoTicketsEmpty: "لا توجد تذاكر بعد. أنشئ واحدة من إدخال التذاكر أو ارفع ملف Excel.",
    ccNoTicketsFiltered: "لا توجد تذاكر تطابق الفلاتر الحالية.",
    ccConfirmBulkDeleteTickets: "حذف {count} تذكرة محددة؟ لا يمكن التراجع عن ذلك.",
    ccTicketsListLoadFailed: "فشل تحميل التذاكر",
    ccSelectAllTickets: "تحديد جميع التذاكر",
    ccSelectTicket: "تحديد التذكرة",
    ccPage: "صفحة",
    ccResults: "النتائج",
    ccTicket: "تذكرة",
    ccTicketId: "معرف التذكرة",
    ccTicketOverview: "نظرة عامة على التذكرة",
    ccTargetOutstanding: "متبقي الهدف",
    ccPaymentDate: "تاريخ الدفع",
    ccUpdateTicket: "تحديث التذكرة",
    ccTargetPaymentDate: "تاريخ دفع الهدف",
    ccCalledAt: "تم الاتصال به في",
    ccAdditionalComments: "تعليقات إضافية",
    ccAddRemarkNote: "أضف ملاحظة",
    ccAddAdditionalComments: "أضف تعليقات إضافية",
    ccSaveStatusUpdate: "حفظ تحديث الحالة",
    ccRefreshLiveOutstanding: "تحديث المستحق المباشر",
    ccToastTicketStatusUpdated: "تم تحديث حالة التذكرة",
    ccToastTicketNotFound: "لم يتم العثور على التذكرة. قد تكون قد حُذفت.",
    ccToastTicketStatusUpdateFailed: "فشل في تحديث حالة التذكرة",
    ccToastOutstandingRefreshedPrefix: "تم تحديث المستحق. السابق:",
    ccToastOutstandingRefreshedLive: "مباشر:",
    ccToastOutstandingRefreshedAt: "في:",
    ccToastRefreshOutstandingFailed: "فشل في تحديث المستحق",
    ccToastSelectTicketToDelete: "اختر تذكرة واحدة على الأقل للحذف.",
    ccToastDeleteProcessedPrefix: "تم معالجة الحذف. محذوف:",
    ccToastDeleteAlreadyDeleted: "تم الحذف بالفعل:",
    ccToastDeleteNotFound: "لم يتم العثور:",
    ccToastBulkDeleteFailed: "فشل الحذف الضخم للتذاكر",
    ccToastLoadTicketsFailed: "فشل تحميل التذاكر",
    ccToastLoadStatusOptionsFailed: "فشل تحميل خيارات الحالة",
    ccToastLoadAssigneesFailed: "فشل تحميل قائمة الوكلاء",
    ccAgentWorkspaceTitle: "إدخال التذاكر",
    ccAgentWorkspaceSubtitle: "إنشاء تذكرة واحدة يدوياً باستخدام الحقول المُكوَّنة.",
    ccTicketBulkUploadTitle: "رفع التذاكر جماعياً",
    ccTicketBulkUploadSubtitle: "رفع التذاكر من Excel أو CSV ومراجعة نتائج الرفع السابقة.",
    ccUploadHistoryResultsLabel: "عمليات رفع",
    ccUploadHistoryFile: "الملف",
    ccUploadHistoryDate: "تاريخ الرفع",
    ccUploadHistoryEmptyTitle: "لا توجد عمليات رفع بعد",
    ccUploadHistoryNoRowDetails: "لا توجد تفاصيل على مستوى الصف لهذا الرفع.",
    ccBulkUploadFormHint: "ارفع جدول بيانات، وعيّن الأعمدة، ثم عالج الصفوف إلى تذاكر.",
    ccBulkUploadStepFile: "اختر الملف",
    ccBulkUploadStepMapping: "تعيين الأعمدة",
    ccBulkUploadStepProcess: "معالجة الرفع",
    ccBulkUploadParsing: "جاري قراءة رؤوس الملف…",
    ccBulkUploadValidationFailed: "فشل التحقق",
    ccBulkUploadValidationPending: "التعيين قيد التقدم",
    ccBulkUploadFooterHint: "يتم تجاهل الصفوف ذات خطط الدفع النشطة، وليس فشلها.",
    ccBulkUploadClearFile: "إزالة الملف",
    ccBulkUploadClearConfirm: "إزالة الملف المحدد وإعادة تعيين تعيين الأعمدة؟",
    ccBulkUploadSubmitConfirm: "معالجة هذا الملف وإنشاء تذاكر لجميع الصفوف الصالحة؟",
    ccBulkUploadSuccessTitle: "اكتمل الرفع",
    ccBulkUploadRequiredColumns: "الأعمدة المطلوبة",
    ccUploadHistoryPageTitle: "سجل الرفع",
    ccUploadHistoryPageSubtitle: "مراجعة عمليات رفع Excel السابقة ونتائج الصفوف وأسباب الفشل.",
    ccUploadHistoryRecentTitle: "عمليات الرفع الأخيرة",
    ccUploadHistoryViewAll: "عرض السجل الكامل",
    ccUploadHistoryStatUploads: "إجمالي عمليات الرفع",
    ccUploadHistorySuccessRate: "معدل النجاح",
    ccUploadHistorySearchPlaceholder: "البحث باسم الملف…",
    ccUploadHistoryOutcomeFilter: "نتيجة الرفع",
    ccUploadHistoryOutcome_ALL: "جميع عمليات الرفع",
    ccUploadHistoryOutcome_HAS_FAILURES: "مع إخفاقات",
    ccUploadHistoryOutcome_HAS_IGNORED: "مع صفوف متجاهلة",
    ccUploadHistoryOutcome_CLEAN: "ناجحة بالكامل",
    ccUploadHistoryEmptyFiltered: "لا توجد عمليات رفع تطابق الفلاتر الحالية.",
    ccUploadHistoryLoadFailed: "فشل تحميل سجل الرفع",
    ccUploadHistoryDetailTitle: "تفاصيل الرفع",
    ccUploadHistoryTab_ALL: "جميع الصفوف",
    ccUploadHistoryTab_CREATED: "تم الإنشاء",
    ccUploadHistoryTab_IGNORED: "تم التجاهل",
    ccUploadHistoryTab_FAILED: "فشل",
    ccUploadHistoryTab_REASSIGNED: "أعيد التعيين",
    ccUploadHistoryTab_UNCHANGED: "بدون تغيير",
    ccUploadRowReassigned: "أعيد التعيين",
    ccUploadRowUnchanged: "بدون تغيير",
    ccBulkUploadReassignExisting: "إعادة تعيين التذاكر المفتوحة عند وجود الحساب",
    ccBulkUploadReassignExistingHint:
      "عيّن عمود الوكيل في الملف. الحسابات المطابقة تُحدَّث بدلاً من إنشاء تذاكر مكررة.",
    ccBulkUploadReassignRequiresAgent:
      "فعّل تعيين عمود الوكيل عند إعادة تعيين التذاكر من رفع Excel.",
    ccTicketAssignmentHistoryTitle: "سجل التعيين",
    ccTicketAssignmentHistoryEmpty: "لا توجد تغييرات تعيين مسجلة بعد.",
    ccTicketAssignmentHistoryLoadFailed: "فشل تحميل سجل التعيين",
    ccTicketAssignmentEventAssigned: "تم التعيين إلى {assignee}",
    ccTicketAssignmentEventUnassigned: "تم إلغاء التعيين من {previous}",
    ccTicketAssignmentEventReassigned: "أعيد التعيين من {previous} إلى {next}",
    ccTicketAssignmentSourceExcelUpload: "رفع Excel",
    ccTicketAssignmentSourceExcelReassign: "إعادة تعيين Excel",
    ccTicketAssignmentSourceManualAssign: "تعيين يدوي",
    ccTicketAssignmentSourceManualUnassign: "إلغاء تعيين يدوي",
    ccTicketAssignmentSourceBulkList: "قائمة جماعية",
    ccTicketAssignmentSourceBulkApi: "API جماعي",
    ccTicketAssignmentSourceTicketCreate: "إنشاء تذكرة",
    ccTicketAssignmentSourceUnknown: "النظام",
    ccTicketColumnConfigPageTitle: "إعدادات أعمدة التذاكر",
    ccTicketColumnConfigPageSubtitle: "إدارة الأعمدة المطلوبة وتحديد أماكن ظهورها.",
    ccManualTicketEntry: "إدخال التذكرة اليدوي",
    ccTicketEntryFormHint: "املأ الحقول المطلوبة أدناه. الحقول المعلمة بـ * إلزامية.",
    ccTicketEntrySectionAccount: "تفاصيل الحساب",
    ccTicketEntrySectionAccountHint: "يجب أن يكون رقم الحساب فريداً للتذاكر النشطة.",
    ccTicketEntrySectionContact: "أرقام الاتصال",
    ccTicketEntrySectionContactHint: "أضف رقم هاتف واحداً أو أكثر للتواصل.",
    ccTicketEntrySectionAssignment: "التعيين والأولوية",
    ccTicketEntrySectionPayment: "تفاصيل الدفع",
    ccTicketEntrySectionNotes: "الملاحظات",
    ccTicketEntrySectionNotesHint: "تظهر ملاحظات التفاعل في الجدول الزمني للتذكرة.",
    ccTicketEntrySectionAdditional: "حقول إضافية",
    ccTicketEntryQuickLinks: "روابط سريعة",
    ccTicketEntryRequiredFields: "الحقول المطلوبة",
    ccTicketEntryRequiredHint: "الحقول المطلوبة معلمة بعلامة النجمة.",
    ccTicketEntryViewTickets: "عرض جميع التذاكر",
    ccTicketEntryClearConfirm: "تجاهل التغييرات غير المحفوظة ومسح النموذج؟",
    ccTicketEntryAccountPlanTitle: "خطط الدفع",
    ccTicketEntryAccountPlanNote: "لا يمكن إنشاء تذاكر يدوية للحسابات ذات خطة دفع نشطة. أدرها من قسم خطط الدفع.",
    ccAssignedTo: "معين إلى",
    ccLoadingUsers: "جاري تحميل المستخدمين...",
    ccSelectUserOptional: "تحديد المستخدم (اختياري)",
    ccRemarksInteractionNotes: "ملاحظات الملاحظات/التفاعل",
    ccCallCommentThread: "سجل المكالمات والتعليقات",
    ccNoCallsOnTicket: "لا توجد مكالمات مسجلة على هذه التذكرة بعد.",
    ccNoCommentsForCall: "لا توجد تعليقات لهذه المكالمة بعد.",
    ccAddCallComment: "إضافة تعليق",
    ccCallCommentPlaceholder: "اكتب ملاحظة حول هذه المكالمة...",
    ccCallDirectionInbound: "واردة",
    ccCallDirectionOutbound: "صادرة",
    ccCallAgent: "الوكيل",
    ccCallAt: "وقت الاتصال",
    ccSavingComment: "جاري الحفظ...",
    ccCommentAdded: "تمت إضافة التعليق",
    ccCommentAddFailed: "فشل إضافة التعليق",
    ccCallThreadLoadFailed: "فشل تحميل سجل المكالمات",
    ccAiGenerated: "مُنشأ بالذكاء الاصطناعي",
    ccCreateTicket: "إنشاء تذكرة",
    ccClear: "مسح",
    ccAccountNoExample: "مثال: 112015128",
    ccRemarksPlaceholder: "أضف ملاحظات تفاعل واضحة...",
    ccBulkImport: "الاستيراد الضخم",
    ccBulkImportRequiredColumns: "تحميل عدة تذاكر عبر CSV أو Excel. الأعمدة المطلوبة:",
    ccBulkImportPriorityNote: "يُطبَّق على كل صف في الملف المرفوع.",
    ccBulkImportAgentNameNote: "يجب أن يطابق عمود Agent Name وكيل مكالمات موجوداً في مؤسستك.",
    ccBulkImportOptional: "اختياري:",
    ccBulkImportDynamicHint: "يتم التقاط أي أعمدة في ملفك تلقائياً. قم بتكوين الحقول الإلزامية والمرئية أدناه.",
    ccBulkImportMultiMobileNote: "لإضافة عدة أرقام جوال، اربط عدة أعمدة بحقل رقم الجوال أو ضع الأرقام مفصولة بفواصل في خلية واحدة. الأرقام المحلية بدون رمز دولة تُعامل افتراضياً كعُمان (+968).",
    ccAddMobileNumber: "إضافة رقم جوال",
    ccRemoveMobileNumber: "إزالة رقم الجوال",
    ccMobileNumberPlaceholder: "أدخل رقم الجوال",
    ccMobileNumberHint: "رمز الدولة الافتراضي عُمان (+968). غيّر العلم لبلدان أخرى. الأرقام المحلية الحالية تُعامل كعُمانية.",
    ccMobileNumberInvalid: "أدخل رقم جوال صالحاً مع رمز الدولة.",
    ccMobileNumberInvalidOman: "أرقام الجوال العُمانية يجب أن تكون 7–8 أرقام بعد +968.",
    ccMobileNumberDuplicate: "رقم الجوال هذا مدرج بالفعل.",
    ccMobileNumberMax: "الحد الأقصى {max} أرقام جوال.",
    ccMobileNumberRequired: "مطلوب رقم جوال واحد على الأقل",
    ccBulkPriority: "أولوية البحث الضخم",
    ccUploadHistoryTitle: "سجل الرفع",
    ccUploadHistoryEmpty: "لا توجد عمليات رفع Excel بعد.",
    ccUploadSuccessCount: "تم الإنشاء",
    ccUploadIgnoredCount: "تم التجاهل",
    ccUploadFailedCount: "فشل",
    ccUploadTotalRows: "إجمالي الصفوف",
    ccUploadViewDetails: "عرض التفاصيل",
    ccUploadHideDetails: "إخفاء التفاصيل",
    ccUploadRowCreated: "تم الإنشاء",
    ccUploadRowIgnored: "تم التجاهل",
    ccUploadRowFailed: "فشل",
    ccUploadSummaryToast: "اكتمل الرفع: {success} تم إنشاؤها، {ignored} تم تجاهلها، {failed} فشلت.",
    ccToastAccountPlanTicketBlocked:
      "لا يمكن إنشاء تذكرة يدوية لهذا الحساب لأن لديه خطة دفع نشطة",
    ccDropFileOrBrowse: "أفلت الملف هنا أو انقر للبحث",
    ccFileTypesCsvXls: "CSV، XLS، XLSX",
    ccValidationResult: "نتيجة التحقق",
    ccMissingHeaders: "مفقود:",
    ccHeadersValidated: "تم التحقق من الرؤوس بنجاح",
    ccFoundHeaders: "وجدت:",
    ccProcessBulkUpload: "معالجة التحميل الضخم",
    ccAgentTipTitle: "نصيحة الوكيل",
    ccAgentTipBody: "أضف ملاحظات تفاعل واضحة في التعليقات للحفاظ على الفائدة من خط زمني التذاكر أثناء تتبع المكالمات اليدوي",
    ccColumnConfigTitle: "تكوين أعمدة التذاكر",
    ccColumnConfigSubtitle: "حدد الأعمدة الملتقطة كإلزامية واختر أماكن ظهورها.",
    ccSaveColumnConfig: "حفظ إعدادات الأعمدة",
    ccSaving: "جاري الحفظ...",
    ccColumnLabel: "العمود",
    ccColumnType: "النوع",
    ccMandatory: "إلزامي",
    ccShowInList: "القائمة",
    ccShowInForm: "النموذج",
    ccShowInUpdate: "تحديث",
    ccShowInFilter: "تصفية",
    ccPredefinedColumn: "Predefined",
    ccCustomColumn: "مخصص",
    ccInvalidNumber: "أدخل رقماً صالحاً",
    ccToastLoadColumnConfigFailed: "فشل تحميل تكوين أعمدة التذاكر",
    ccToastColumnConfigSaved: "تم حفظ تكوين الأعمدة",
    ccToastColumnConfigSaveFailed: "فشل حفظ تكوين الأعمدة",
    ccToastTicketCreated: "تم إنشاء التذكرة بنجاح",
    ccToastCreateTicketFailed: "فشل في إنشاء تذكرة",
    ccToastInvalidFileType: "نوع ملف غير صحيح. يرجى تحميل CSV/XLS/XLSX",
    ccToastParseFileFailed: "فشل في تحليل هيكل الملف",
    ccToastNoFileSelected: "لم يتم تحديد ملف",
    ccToastSelectBulkPriority: "يرجى تحديد أولوية ضخمة",
    ccToastHeaderValidationFailed: "فشل التحقق من الرأس",
    ccToastBulkUploadProcessed: "تم معالجة التحميل الضخم",
    ccToastBulkUploadFailed: "فشل التحميل الضخم",
    ccToastLoadOfficeUsersFailed: "فشل تحميل مستخدمي المكتب",
    ccCampaignManagerTitle: "مدير الحملة",
    ccCampaignManagerSubtitle: "أتمتة ومراقبة تدفقات المكالمات الصادرة",
    ccCreateNewCampaign: "إنشاء حملة جديدة",
    ccCampaignName: "اسم الحملة",
    ccCampaignNamePlaceholder: "على سبيل المثال: استعادة الديون Q4",
    ccCampaignNameRequired: "اسم الحملة مطلوب",
    ccExecutionScheduleCron: "جدول التنفيذ (Cron)",
    ccCallTypeRequired: "نوع المكالمة مطلوب",
    ccScheduleCronRequired: "جدول cron مطلوب",
    ccManualAgent: "وكيل يدوي",
    ccMaxRetries: "الحد الأقصى للمحاولات",
    ccDelayMin: "تأخير (دقيقة)",
    ccTriggerConditions: "شروط الحافز",
    ccAddRule: "+ إضافة قاعدة",
    ccFieldMode: "حقل",
    ccExprMode: "تعبير",
    ccExpressionPlaceholder: "مثال: الرصيد > 500",
    ccFieldName: "اسم الحقل",
    ccValue: "قيمة",
    ccSetAsActive: "تعيين كنشط",
    ccCreateCampaign: "إنشاء حملة",
    ccExistingCampaigns: "الحملات الموجودة",
    ccLogicPreview: "معاينة المنطق",
    ccToastCampaignLaunched: "تم إطلاق الحملة بنجاح",
    ccToastCampaignCreateFailed: "فشل في إنشاء حملة",
    ccToastLoadCampaignsFailed: "فشل تحميل قائمة الحملات",
    ccToastCampaignUpdated: "تم تحديث الحملة بنجاح",
    ccToastCampaignDeleted: "تم حذف الحملة بنجاح",
    ccToastCampaignDeleteFailed: "فشل حذف الحملة",
    ccCampaignTasksLoadFailed: "فشل تحميل مهام الحملة",
    ccCampaignFormSubtitle: "اضبط الجدول وقواعد الاستهداف وسياسة إعادة المحاولة.",
    ccScheduleCronHint: "استخدم تعبير Spring cron من 6 حقول، مثل 0 */10 * * * *",
    ccCampaignManualTypeHint: "الحملات اليدوية تُحفظ للمرجع فقط ولا يتم الاتصال بها تلقائياً. استخدم AI automated للمكالمات المجدولة.",
    ccConditionLogic: "منطق الشرط",
    ccTicketLimit: "حد التذاكر لكل تشغيل",
    ccTicketLimitPlaceholder: "اختياري",
    ccRemove: "إزالة",
    ccSaveChanges: "حفظ التغييرات",
    ccAnsweredRate: "معدل الرد",
    ccSearch: "بحث",
    ccCampaignSearchPlaceholder: "البحث باسم الحملة...",
    ccCampaignStatusActive: "نشطة",
    ccCampaignStatusInactive: "غير نشطة",
    ccCampaignsEmptyTitle: "لا توجد حملات بعد",
    ccCampaignsEmptyHint: "أنشئ حملة AI automated لبدء مكالمات الاسترداد المجدولة.",
    ccCampaignResultsLabel: "حملات",
    ccCampaignDeleteConfirm: "حذف الحملة \"{name}\"؟ سيتم أيضاً إزالة المهام المرتبطة بها.",
    ccCampaignDetailSubtitle: "الجدول والحالة ومهام الاتصال الأخيرة",
    ccCampaignRecentTasks: "مهام الاتصال الأخيرة",
    ccCampaignTasksEmpty: "لا توجد مهام اتصال في قائمة الانتظار لهذه الحملة بعد.",
    ccCampaignTaskAiOutcome: "نتيجة AI",
    ccCallTaskStatus_QUEUED: "في قائمة الانتظار",
    ccCallTaskStatus_PROCESSING: "قيد المعالجة",
    ccCallTaskStatus_DIALING: "جاري الاتصال",
    ccCallTaskStatus_AWAITING_COMPLETION: "بانتظار الإكمال",
    ccCallTaskStatus_COMPLETED: "مكتمل",
    ccCallTaskStatus_FAILED: "فشل",
    ccCallTaskStatus_RETRY_WAIT: "إعادة محاولة مجدولة",
    ccAiCallIntent_PAID: "مدفوع",
    ccAiCallIntent_WILL_PAY: "سيدفع",
    ccAiCallIntent_DISPUTE: "نزاع",
    ccAiCallIntent_CALLBACK_REQUIRED: "يتطلب معاودة اتصال",
    ccAiCallIntent_NO_ANSWER: "لا رد",
    ccAiCallIntent_UNKNOWN: "غير معروف",
    ccEditCampaign: "تعديل الحملة",
    ccViewDetails: "عرض التفاصيل",
    ccUpdatedAt: "تم التحديث في",
    ccPhone: "الهاتف",
    ccCampaignPromoTitle: "ابدأ حملة",
    ccCampaignPromoSubtitle: "أطلق جولة استرداد صادرة وتابعها مباشرة.",
    ccCampaignPromoAction: "حملة جديدة",
    ccCampaignPaymentReminderSubtitle:
      "جدولة مكالمات AI للتذاكر غير المعيّنة ذات الرصيد المستحق. تُحدَّث التذاكر تلقائياً من نتائج المكالمة.",
    ccCampaignPaymentReminderPageSubtitle:
      "مكالمات AI لتذكير الدفع للتذاكر غير المعيّنة — النتائج تحدّث حالة التذكرة تلقائياً.",
    ccCampaignPaymentReminderPageIntro:
      "كل تشغيل يجد التذاكر المفتوحة غير المعيّنة المطابقة لقواعدك، يجري مكالمات تذكير AI، ثم يحدّث التذاكر عند رد العملاء.",
    ccCampaignFlowTitle: "كيف تعمل حملات تذكير الدفع",
    ccCampaignFlowHint: "الحملات تستخدم دائماً الاتصال الآلي بالذكاء الاصطناعي.",
    ccCampaignFlowStep1: "إيجاد تذاكر غير معيّنة برصيد مستحق تطابق قواعد الحافز.",
    ccCampaignFlowStep2: "إجراء مكالمات تذكير AI وفق الجدول ونافذة الاتصال.",
    ccCampaignFlowStep3: "تحديث كل تذكرة من نتيجة AI (الحالة، وعد الدفع، الملخص، التسجيل).",
    ccCampaignUnassignedHint: "تُدرج التذاكر غير المعيّنة فقط — يتم فرض ذلك تلقائياً.",
    ccCampaignResetDefaults: "إعادة تعيين defaults تذكير الدفع",
    ccCampaignTypeLabel: "نوع الحملة",
    ccCampaignTypeAiPaymentReminder: "تذكير دفع AI",
    ccCampaignDetailPaymentReminderSubtitle: "مهام اتصال AI وتحديث تلقائي للتذاكر من ردود العملاء",
    ccCampaignOutcomeTitle: "تحديثات التذكرة من نتائج AI",
    ccCampaignOutcomeHint:
      "مدفوع → PAID · سيدفع / لا رد → Follow up · Callback → Callback required · Dispute → Failed · يُحفظ وعد الدفع عند توفره.",
    ccCampaignKpiAiScopeHint: "الأرقام أدناه تشمل مكالمات تذكير الدفع الآلية (AI) فقط — مكالمات الوكلاء اليدوية مستبعدة.",
    ccCampaignKpiAiCalls: "مكالمات AI المنفذة",
    ccCampaignKpiAiAnsweredRate: "معدل رد AI",
    ccCampaignKpiAiSuccessRate: "مدفوع بعد مكالمة AI",
    ccCampaignKpiAiRecovery: "استرداد من مكالمات AI",
    ccAnalyticsTitle: "لوحة المعلومات",
    ccAnalyticsSubtitle: "أهداف الفريق وصحة الحملات واختصارات الوصول في لمحة واحدة.",
    ccDashboardLastUpdated: "آخر تحديث {time} ({timezone})",
    ccRefreshAnalytics: "مزامنة",
    ccExportDashboard: "تصدير",
    ccLast7Days: "آخر 7 أيام",
    ccLast30Days: "آخر 30 يومًا",
    ccLast90Days: "آخر 90 يومًا",
    ccDashboardExported: "تم تنزيل تصدير لوحة المعلومات",
    ccDashboardQuickActions: "إجراءات سريعة",
    ccDashboardLastFetched: "آخر تحديث",
    ccDashboardNotFetchedYet: "لم يتم التحميل بعد",
    ccDashboardOpen: "فتح",
    ccTeamProgress: "تقدم الفريق",
    ccTeamProgressSubtitle: "تنفيذ هدف التذكرة من قبل العضو",
    ccTotalTargets: "إجمالي الأهداف",
    ccCalledTargets: "أهداف المكالمات",
    ccPaidTargets: "أهداف مدفوعة",
    ccRecovery: "استعادة",
    ccCalledCoverage: "تغطية المكالمات",
    ccPaidConversion: "تحويل مدفوع",
    ccMember: "عضو",
    ccAssigned: "معين",
    ccCalledCol: "تم الاتصال به",
    ccPaidCol: "مدفوع",
    ccPending: "قيد الانتظار",
    ccNoTeamProgressData: "لا توجد بيانات تقدم فريق متاحة.",
    ccCampaignAnalytics: "تحليلات الحملة",
    ccCampaignAnalyticsSubtitle: "إنتاجية الحملة المباشرة وجودة النتائج",
    ccTotalCalls: "إجمالي المكالمات",
    ccAnswerRate: "معدل الإجابة",
    ccSuccessRate: "معدل النجاح",
    ccCampaignPerformanceSnapshot: "لقطة أداء الحملة",
    ccAnsweredPercent: "تم الرد %",
    ccSuccessPercent: "معدل النجاح %",
    ccNoCampaignAnalyticsData: "لا توجد بيانات تحليلات حملة متاحة.",
    ccToastLoadAnalyticsFailed: "فشل تحميل التحليلات",
    ccTotalRecovery: "إجمالي الاسترداد",
    ccPerfectTiming: "أفضل وقت للاتصال",
    ccPerfectTimingSubtitle: "متى يجيب العملاء عادةً، حسب الساعة",
    ccPerfectTimingHelp: "يتم تجميع المكالمات حسب ساعة رد العميل (أو وقت بدء المكالمة). معدل الرد = المكالمات المجابة ÷ إجمالي المكالمات في تلك الساعة.",
    ccPerfectTimingHeroTitle: "وقت الاتصال الموصى به",
    ccPerfectTimingHeroStats: "{rate}% معدل رد · {answered} مجابة من {total} مكالمة",
    ccPerfectTimingRecommended: "موصى به أيضاً",
    ccPerfectTimingRecommendedShort: "جيد",
    ccPerfectTimingBest: "الأفضل",
    ccPerfectTimingBestHour: "أفضل وقت للاتصال: {hour}",
    ccPerfectTimingAnswerRate: "معدل الرد",
    ccPerfectTimingAnsweredCalls: "المكالمات المجابة",
    ccPerfectTimingPeriodTotalCalls: "إجمالي المكالمات في الفترة",
    ccPerfectTimingOverallRate: "معدل الرد الإجمالي",
    ccPerfectTimingByHour: "التفصيل حسب الساعة",
    ccPerfectTimingShowingHours: "{count} ساعات بها نشاط مكالمات",
    ccPerfectTimingChartHint: "ارتفاع الشريطة = معدل الرد لتلك الساعة",
    ccPerfectTimingTableTime: "الوقت",
    ccPerfectTimingTableCalls: "إجمالي المكالمات",
    ccPerfectTimingTableAnswered: "مجاب",
    ccPerfectTimingTableRate: "معدل الرد",
    ccPerfectTimingNoData: "لا يوجد سجل مكالمات كافٍ لتحليل التوقيت",
    ccPerfectTimingSample: "بناءً على {count} مكالمة في الفترة المحددة",
    ccVsLastPeriodShort: "مقارنة بالفترة السابقة",
    ccDebtorSegments: "شرائح المدينين",
    ccIndividuals: "أفراد",
    ccSMEs: "الشركات الصغيرة",
    ccCorporates: "شركات",
    ccBestPerformingAgents: "أفضل الوكلاء أداءً",
    ccAgentRatingHelp:
      "التقييم بالنجوم (0–5) للفترة المحددة يجمع: 35% تغطية المكالمات (الأهداف المتصلة ÷ الأهداف المخصصة)، و35% التحويل إلى مدفوع (الأهداف المدفوعة ÷ المتصلة)، و30% التحصيل مقارنة بأفضل وكيل في الفريق. الوكلاء بلا نشاط في هذه الفترة يظهر تقييمهم 0.0.",
    ccAgentRatingHelpLabel: "كيفية حساب تقييم الوكيل",
    ccAgentTableShowing: "عرض {shown} من {total} وكيل",
    ccAgentTableSearchPlaceholder: "البحث عن الوكلاء بالاسم...",
    ccAgentTableShowAll: "عرض كل الوكلاء",
    ccAgentTableShowTop: "عرض أفضل {count}",
    ccAgentTableMoreHidden: "عرض {count} وكلاء إضافيين",
    ccAgentTableNoMatches: "لا يوجد وكلاء مطابقون للبحث",
    ccAgent: "الوكيل",
    ccCalls: "المكالمات",
    ccRating: "التقييم",
    ccShowDetails: "عرض التفاصيل",
    ccOnTrackCoverage: "في المسار نحو هدف تغطية 80%",
    ccCallOutcomes: "نتائج المكالمات",
    ccOutcomeAns: "رد",
    ccOutcomeNoAns: "بدون رد",
    ccOutcomeBusy: "مشغول",
    ccOutcomeCallback: "معاودة",
    ccOutcomePaid: "مدفوع",
    ccOutcomeWrong: "خطأ",
    ccOutcomeDnc: "عدم الاتصال",
    ccWeeklyTrackerTitle: "متتبع المكالمات الأسبوعي",
    ccMenuWeeklyTracker: "المتتبع الأسبوعي",
    ccWeeklyTrackerDownloadExcel: "تنزيل Excel",
    ccWorkflowSettingsTitle: "إعدادات حالات التذاكر",
    ccWorkflowSettingsSubtitle: "أضف حالات تذاكر مخصصة أو أعد تسمية الحالات المدمجة لمساحة العمل.",
    ccAddNewStatus: "أضف حالة جديدة",
    ccStatusKeyLabel: "مفتاح الحالة",
    ccStatusKeyPlaceholder: "مثال AWAITING_DOCS",
    ccStatusKeyHint: "رمز فريد يُستخدم في واجهات برمجة التطبيقات والفلاتر. يُنشأ تلقائياً من اسم العرض.",
    ccStatusKeyInvalid: "استخدم حروفاً وأرقاماً وشرطات سفلية (يبدأ بحرف).",
    ccStatusSettingsViewOnly: "يمكنك عرض الحالات هنا. اطلب من المسؤول الذي لديه صلاحية الإعدادات الإضافة أو التعديل.",
    ccStatusManageFromTickets: "استخدم هذه الحالات في صفحة التذاكر بعد الحفظ.",
    ccConfiguration: "إعدادات",
    ccBaseMapping: "ربط الأساس",
    ccDisplayName: "اسم العرض",
    ccDisplayNamePlaceholder: "مثال: في انتظار العميل",
    ccColorTheme: "مظهر الألوان",
    ccLiveUiPreview: "معاينة واجهة المستخدم المباشرة",
    ccPreviewStatus: "معاينة الحالة",
    ccPreviewTicketLedger: "سيظهر في دفتر التذاكر",
    ccFinalStatus: "الحالة النهائية",
    ccFinalStatusHint: "إغلاق هذه التذكرة يوقف إعادة محاولات الأتمتة.",
    ccEnabled: "مفعل",
    ccSaveWorkflowStatus: "حفظ حالة سير العمل",
    ccActiveStatusLibrary: "مكتبة الحالة النشطة",
    ccSyncStatuses: "مزامنة الحالات",
    ccNoCustomStatuses: "لم تتم تكوين حالات مخصصة حتى الآن.",
    ccFinal: "نهائي",
    ccActive: "نشط",
    ccInactive: "غير نشط",
    ccStatusProTipTitle: "نصيحة حالة احترافية",
    ccStatusProTipBody:
      'استخدم ألوان عالية التباين (مثل الأحمر أو البرتقالي) للحالات التي تتطلب انتباهاً عاجلاً من الوكيل، والألوان الهادئة للحالات النهائية مثل "مكتمل".',
    ccStatusKeyRequired: "مفتاح الحالة مطلوب",
    ccNameRequired: "الاسم مطلوب",
    ccInvalidHexColor: "لون سادس عشري غير صحيح",
    ccToastStatusSaved: "تم حفظ إعدادات الحالة",
    ccToastStatusSaveFailed: "فشل في الحفظ",
    ccToastLoadStatusesFailed: "فشل تحميل الحالات",
    ccStatusLibrary: "مكتبة الحالات",
    ccEditStatus: "تعديل الحالة",
    ccUpdateWorkflowStatus: "تحديث الحالة",
    ccCancelEdit: "إلغاء",
    ccStatusKeyLockedHint: "لا يمكن تغيير مفتاح الحالة بعد الإنشاء.",
    ccEdit: "تعديل",
    ccDelete: "حذف",
    ccActivate: "تفعيل",
    ccDeactivate: "إلغاء التفعيل",
    ccBuiltInStatus: "مدمجة",
    ccToastStatusUpdated: "تم تحديث الحالة",
    ccToastStatusUpdateFailed: "فشل تحديث الحالة",
    ccToastStatusDeleted: "تم حذف الحالة",
    ccToastStatusDeleteFailed: "فشل حذف الحالة",
    ccConfirmDeleteStatus: "حذف هذه الحالة المخصصة؟ لا يمكن التراجع عن ذلك.",
    ccAckNotePlaceholder: "ملاحظة الإقرار (اختياري)",
    ccAcknowledge: "إقرار",
    ccAcknowledgeAlert: "إقرار التنبيه",
    ccAlertAckFailed: "فشل إقرار التنبيه",
    ccAlertAcknowledged: "تم إقرار التنبيه",
    ccAlertAgent: "الوكيل",
    ccAlertDuration: "المدة",
    ccAlertNotificationEscalatedBody: "تم تصعيد تنبيه SLA للاستجابة إلى فريقك.",
    ccAlertNotificationEscalatedTitle: "تم تصعيد تنبيه الوكيل",
    ccAlertNotificationWarningBody: "تذكرة معيّنة أو مكالمة فائتة تحتاج إلى اهتمام.",
    ccAlertNotificationWarningTitle: "تحذير SLA لاستجابة الوكيل",
    ccAlertOpenFor: "مفتوح منذ",
    ccAlertPending: "قيد الانتظار",
    ccAlertPriority: "الأولوية",
    ccAlertReason: "السبب",
    ccAlertReference: "المرجع",
    ccAlertReportEmpty: "لا توجد بيانات تقرير للتصدير",
    ccAlertReportFailed: "فشل تحميل تقرير التنبيهات",
    ccAlertReportTitle: "تقرير تدقيق التنبيهات",
    ccAlertResolveFailed: "فشل حل التنبيه",
    ccAlertResolved: "تم حل التنبيه",
    ccAlertResponseTime: "وقت الاستجابة",
    ccAlertStatus: "الحالة",
    ccAlertTimeline: "الخط الزمني للأحداث",
    ccAlertTimelineEmpty: "لا توجد أحداث مسجّلة لهذا التنبيه.",
    ccAlertsAccessDenied: "ليس لديك صلاحية الوصول إلى مراقب التنبيهات.",
    ccAlertsEmpty: "لا توجد تنبيهات تطابق عوامل التصفية الحالية.",
    ccAlertsLoadFailed: "فشل تحميل التنبيهات",
    ccAlertsLoading: "جاري تحميل التنبيهات…",
    ccAlertsPageSubtitle: "راقب تحذيرات SLA والتصعيدات وأوقات استجابة الفريق.",
    ccAlertsPageTitle: "تنبيهات استجابة الوكيل",
    ccAllAgents: "جميع الوكلاء",
    ccAllStatuses: "جميع الحالات",
    ccBrowserPhoneFlagDisabled: "هاتف المتصفح معطّل. اضبط TELEPHONY_BROWSER_PHONE_ENABLED=true على الخادم.",
    ccBrowserPhoneGrandstreamDesktopOnly:
      "تستخدم هذه المنظمة Grandstream UCM. ثبّت تطبيق Emmad Call Center للسطح المكتب وسجّل الدخول لإجراء المكالمات واستقبالها.",
    ccBrowserPhoneGrandstreamNotConfigured:
      "لم يكتمل إعداد Grandstream. اطلب من المسؤول إكمال إعدادات UCM وامتداد الوكيل وكلمة مرور SIP.",
    ccBrowserPhoneLinkusConnectionFailed: "تعذّر الاتصال بـ D2C Cloud PBX. تحقق من اتصال الإنترنت، حدّث الصفحة، وأعد المحاولة.",
    ccBrowserPhoneMissingEmail: "يحتاج هاتف المتصفح إلى بريدك الإلكتروني لمطابقة امتداد D2C PBX. اطلب من المسؤول تعيين بريدك الإلكتروني.",
    ccBrowserPhoneMissingExtension: "يحتاج هاتف المتصفح إلى امتداد PBX. اطلب من المسؤول تعيين امتداد الوكيل في ملفك الشخصي.",
    ccBrowserPhoneNotEnabled: "هاتف المتصفح غير متاح. اضبط امتداد الوكيل والبريد الإلكتروني وبيانات اعتماد D2C Linkus SDK.",
    ccBrowserPhonePbxCredentialsRejected: "رفض D2C معرّف/مفتاح Linkus SDK. أعد إنشاءهما في PBX → Integrations → Linkus SDK، حدّث D2C_PBX_ACCESS_ID / D2C_PBX_ACCESS_KEY، وأعد تشغيل الخادم.",
    ccBrowserPhonePbxIpBlocked: "حظر D2C PBX عنوان IP هذا للمتصفح في OpenAPI (errcode 70004). اطلب من D2C إضافة IP العام إلى القائمة البيضاء.",
    ccBrowserPhonePbxSslError: "تعذّر على المتصفح التحقق من شهادة HTTPS لـ D2C PBX. تحقق من إعدادات الشبكة/الوكيل أو تواصل مع D2C.",
    ccBrowserPhoneSdkNotConfigured: "بيانات اعتماد D2C Linkus SDK مفقودة على الخادم. اضبط D2C_PBX_ACCESS_ID و D2C_PBX_ACCESS_KEY، ثم أعد تشغيل الخادم.",
    ccCallingDisabled: "المكالمات معطّلة لهذه المنظمة.",
    ccCancel: "إلغاء",
    ccConfirmDialogTitle: "تأكيد الإجراء",
    ccConfirmDialogProceed: "متابعة",
    ccConfirmDialogConfirm: "تأكيد",
    ccDownloadLinkusDesktop: "تنزيل Linkus Desktop",
    ccDownloadLinkusMobile: "تنزيل Linkus Mobile",
    ccDuplicateUploadMappingTitle: "تُربَط هذه الرؤوس بنفس حقل التذكرة:",
    ccExportCsv: "تصدير CSV",
    ccHideTimeline: "إخفاء الخط الزمني",
    ccLinkusExtensionLabel: "امتدادك",
    ccLinkusSoftphoneHint: "تُجرى المكالمات عبر الخادم. ابقَ مسجّل الدخول في Linkus Desktop أو Mobile أو Web Client للرد على click-to-call والمكالمات الواردة.",
    ccLinkusSoftphoneTitle: "الرد عبر Linkus",
    ccLoadReport: "تحميل التقرير",
    ccMenuAgentAlerts: "تنبيهات الوكيل",
    ccMenuAgentResponseAlerts: "تنبيهات استجابة الوكيل",
    ccMenuPaymentConfirmationSettings: "تأكيد الدفع",
    ccMenuSettings: "الإعدادات",
    ccMenuSlaSettings: "SLA الاستجابة",
    ccNoReassign: "عدم إعادة التعيين",
    ccNoSupervisor: "بدون مشرف",
    ccNotificationsSectionUnreadMany: "{count} غير مقروء",
    ccNotificationsSectionUnreadOne: "1 غير مقروء",
    ccOpenLinkusWebClient: "فتح Linkus Web Client",
    ccOperationalAlerts: "التنبيهات التشغيلية",
    ccOperationalAlertsSubtitle: "{open} مفتوح · {escalated} مُصعَّد",
    ccPageOf: "صفحة {page} من {total}",
    ccPaymentConfirmationEnabled: "تفعيل تأكيد الدفع لهذه المنظمة",
    ccPaymentConfirmationFieldMapping: "تعيين حقول الحمولة",
    ccPaymentConfirmationInfoAmount: "المبلغ",
    ccPaymentConfirmationInfoConfirmedAt: "تم التأكيد في",
    ccPaymentConfirmationInfoReference: "المرجع",
    ccPaymentConfirmationInfoSource: "المصدر",
    ccPaymentConfirmationInfoTitle: "تم تأكيد الدفع",
    ccPaymentConfirmationInfoBadge: "مؤكد",
    ccPaymentConfirmationSourceWebhook: "Webhook",
    ccPaymentConfirmationSourceRestPoll: "استطلاع REST",
    ccPaymentConfirmationSourceMessageQueue: "قائمة رسائل",
    ccSecretConfiguredHint: "تم التكوين — اتركه فارغًا للاحتفاظ بالقيمة الحالية",
    ccPaymentConfirmationDisableConfirm: "تعطيل تأكيد الدفع يوقف التحديث التلقائي للتذاكر. هل تريد المتابعة؟",
    ccPaymentConfirmationProviderChangeConfirm: "تغيير وضع التكامل قد يتطلب تحديث نظامك الخارجي. هل تريد المتابعة؟",
    ccPaymentConfirmationInvalidMqJson: "JSON اتصال MQ غير صالح",
    ccPaymentConfirmationPollBaseUrlRequired: "عنوان URL الأساسي للاستطلاع مطلوب عند التفعيل",
    ccPaymentConfirmationPollBaseUrlInvalid: "أدخل عنوان URL أساسي صالح للاستطلاع (https://...)",
    ccPaymentConfirmationPollPathRequired: "قالب مسار الاستطلاع مطلوب عند التفعيل",
    ccPaymentConfirmationPollIntervalInvalid: "يجب أن تكون فترة الاستطلاع بين 1 و 1440 دقيقة",
    ccPaymentConfirmationWebhookSecretRequired: "سر webhook مطلوب عند التفعيل",
    ccPaymentConfirmationMqConfigRequired: "JSON اتصال MQ مطلوب عند التفعيل",
    ccPaymentConfirmationFieldAccountNo: "حقل رقم الحساب",
    ccPaymentConfirmationFieldAmount: "حقل المبلغ",
    ccPaymentConfirmationFieldReference: "حقل المرجع",
    ccPaymentConfirmationFieldPaidAt: "حقل تاريخ الدفع",
    ccPaymentConfirmationFieldIsPaid: "حقل حالة الدفع",
    ccPaymentConfirmationFieldMappingHint: "اربط مفاتيح JSON من حمولة الدفع بالحقول التي يتوقعها مركز الاتصال.",
    ccPaymentConfirmationPayloadExampleTitle: "مثال على حمولة webhook",
    ccPaymentConfirmationPayloadExampleHint: "يجب أن يرسل نظامك JSON مثل هذا. يمكن أن تختلف أسماء الحقول إذا تم تعيينها أعلاه.",
    ccPaymentConfirmationCopyUrl: "نسخ العنوان",
    ccPaymentConfirmationUrlCopied: "تم نسخ العنوان",
    ccPaymentConfirmationCopyUrlFailed: "تعذر نسخ العنوان",
    ccPaymentConfirmationEventLogTitle: "أحداث التكامل الأخيرة",
    ccPaymentConfirmationEventLogSubtitle: "سجل محاولات تأكيد الدفع — مفيد لتصحيح المدفوعات الفاشلة أو غير المطابقة.",
    ccPaymentConfirmationEventLogEmpty: "لا توجد أحداث تأكيد دفع بعد",
    ccPaymentConfirmationEventLogEmptyHint: "تظهر الأحداث هنا عند إرسال نظامك إشعارات الدفع أو عند تشغيل الاستطلاع.",
    ccPaymentConfirmationEventLogLoadFailed: "فشل تحميل أحداث تأكيد الدفع",
    ccPaymentConfirmationEventProcessed: "تم التطبيق",
    ccPaymentConfirmationEventFailed: "فشل",
    ccPaymentConfirmationEventSkipped: "تم التخطي",
    ccPaymentConfirmationEventAccountNo: "رقم الحساب",
    ccPaymentConfirmationEventReference: "المرجع",
    ccPaymentConfirmationEventProvider: "المزود",
    ccPaymentConfirmationEventCreatedAt: "وقت الاستلام",
    ccPaymentConfirmationEventError: "التفاصيل",
    ccPaymentConfirmationEventResultsLabel: "أحداث",
    ccPaymentConfirmationLoadFailed: "فشل تحميل إعدادات تأكيد الدفع",
    ccPaymentConfirmationMqBrokerType: "نوع وسيط الرسائل",
    ccPaymentConfirmationMqConfigHint: "قدّم host و port و username و password و virtualHost و queue كـ JSON.",
    ccPaymentConfirmationMqConfigJson: "JSON اتصال MQ",
    ccPaymentConfirmationPollAuthSecret: "سر مصادقة الاستطلاع",
    ccPaymentConfirmationPollAuthType: "نوع مصادقة الاستطلاع",
    ccPaymentConfirmationPollBaseUrl: "عنوان URL الأساسي لواجهة الاستطلاع",
    ccPaymentConfirmationPollInterval: "فترة الاستطلاع (بالدقائق)",
    ccPaymentConfirmationPollPathTemplate: "قالب مسار الاستطلاع",
    ccPaymentConfirmationProvider: "وضع التكامل",
    ccPaymentConfirmationProviderMessageQueue: "قائمة رسائل (وسيط مقدّم من المنظمة)",
    ccPaymentConfirmationProviderRestPoll: "استطلاع REST API (نتحقق من واجهة المنظمة برقم الحساب)",
    ccPaymentConfirmationProviderWebhook: "Webhook (المنظمة تُرسل أحداث الدفع)",
    ccPaymentConfirmationSave: "حفظ إعدادات تأكيد الدفع",
    ccPaymentConfirmationSaveFailed: "فشل حفظ إعدادات تأكيد الدفع",
    ccPaymentConfirmationSaveSuccess: "تم حفظ إعدادات تأكيد الدفع",
    ccPaymentConfirmationSettingsSubtitle: "اضبط كيف تُبلّغ منظمتك مركز الاتصال عند دفع العملاء (webhook أو استطلاع REST أو قائمة رسائل).",
    ccPaymentConfirmationSettingsTitle: "تأكيد الدفع",
    ccPaymentConfirmationTestConnection: "اختبار الاتصال",
    ccPaymentConfirmationTestFailed: "فشل اختبار الاتصال",
    ccPaymentConfirmationTestSuccess: "نجح اختبار الاتصال",
    ccPaymentConfirmationWebhookHint: "POST JSON موقّع يتضمن رقم الحساب وتفاصيل الدفع عند دفع العميل.",
    ccPaymentConfirmationWebhookSecret: "سر webhook",
    ccPaymentConfirmationWebhookUrlLabel: "سجّل عنوان webhook هذا في نظام الدفع",
    ccReassignToAgent: "إعادة تعيين التذكرة إلى",
    ccRefresh: "تحديث",
    ccReportAcknowledged: "تم الإقرار",
    ccReportAutoResolved: "حُلّ تلقائياً",
    ccReportCreated: "تم الإنشاء",
    ccReportEscalatedL1: "تصعيد L1",
    ccReportEscalatedL2: "تصعيد L2",
    ccReportResolved: "تم الحل",
    ccResolutionNotePlaceholder: "ملاحظة الحل (اختياري)",
    ccResolve: "حل",
    ccResolveAlert: "حل التنبيه",
    ccSaveSlaSettings: "حفظ إعدادات SLA",
    ccSlaAckGraceMinutes: "مهلة الإقرار (بالدقائق)",
    ccSlaAckGraceNote: "+{duration} مهلة إقرار قبل L2",
    ccSlaCopiedFrom: "تم نسخ العتبات من {source}",
    ccSlaCopyFrom: "نسخ من",
    ccSlaCopyFromPlaceholder: "اختر النوع",
    ccSlaDescriptionColumn: "الوصف",
    ccSlaDisabledTimeline: "التصعيد متوقّف مؤقتاً بينما نوع التنبيه هذا معطّل.",
    ccSlaDurationColumn: "المدة",
    ccSlaEnabled: "مفعّل",
    ccSlaEscalationPreview: "الخط الزمني للتصعيد",
    ccSlaImmediate: "فوري",
    ccSlaL1Minutes: "تصعيد L1 (بالدقائق)",
    ccSlaL2Minutes: "تصعيد L2 (بالدقائق)",
    ccSlaPriorityHint: "تُصعَّد التنبيهات الحرجة وعالية الأولوية أسرع. عطّل نوعاً لإيقاف التنبيهات والتصعيد لتلك الفئة.",
    ccSlaResetDefaults: "إعادة التعيين للافتراضي",
    ccSlaResetToDefaults: "إعادة التعيين للعتبات الافتراضية",
    ccSlaSaveHint: "تُطبَّق التغييرات على جميع أنواع التنبيهات عند الحفظ.",
    ccSlaSettingsLink: "إعدادات SLA",
    ccSlaSettingsLoadFailed: "فشل تحميل إعدادات SLA",
    ccSlaSettingsLoading: "جاري تحميل إعدادات SLA…",
    ccSlaSettingsPageSubtitle: "اضبط مؤقتات التحذير والتصعيد للتذاكر المعيّنة والمكالمات الفائتة.",
    ccSlaSettingsPageTitle: "SLA الاستجابة",
    ccSlaSettingsSaveFailed: "فشل حفظ إعدادات SLA",
    ccSlaSettingsSaved: "تم حفظ إعدادات SLA",
    ccSlaStatusOff: "إيقاف",
    ccSlaStatusOn: "تشغيل",
    ccSlaStepPending: "معيّن",
    ccSlaThresholdColumn: "العتبة",
    ccSlaThresholdsSubtitle: "اضبط مؤقتات التحذير والتصعيد لكل نوع تنبيه. يمكن استخدام الدقائق أو الساعات أو الأيام.",
    ccSlaThresholdsTitle: "عتبات SLA للاستجابة",
    ccSlaTypeColumn: "النوع",
    ccSlaUnitDay: "يوم",
    ccSlaUnitDayShort: "ي",
    ccSlaUnitDays: "أيام",
    ccSlaUnitHour: "ساعة",
    ccSlaUnitHourShort: "س",
    ccSlaUnitHours: "ساعات",
    ccSlaUnitMinute: "دقيقة",
    ccSlaUnitMinuteShort: "د",
    ccSlaUnitMinutes: "دقائق",
    ccSlaWarningMinutes: "التحذير (بالدقائق)",
    ccSlaItemType_TICKET: "تذكرة",
    ccSlaItemType_MISSED_CALL: "مكالمة فائتة",
    ccSlaItemType_BULK_ASSIGNMENT: "تعيين جماعي",
    ccSlaItemDesc_TICKET: "SLA الاستجابة للتذاكر المعيّنة حديثاً أو التي تتطلب معاودة الاتصال.",
    ccSlaItemDesc_MISSED_CALL: "SLA معاودة الاتصال بعد مكالمة واردة فائتة.",
    ccSlaItemDesc_BULK_ASSIGNMENT: "SLA بدء العمل بعد رفع تذاكر جماعي كبير.",
    ccSlaField_warning: "تحذير",
    ccSlaField_escalationL1: "تصعيد L1",
    ccSlaField_escalationL2: "تصعيد L2",
    ccSlaField_acknowledgementGrace: "مهلة الإقرار",
    ccSlaFieldDesc_warning: "الوقت قبل دخول التنبيه في حالة التحذير",
    ccSlaFieldDesc_escalationL1: "التصعيد إلى مشرف الوكيل",
    ccSlaFieldDesc_escalationL2: "التصعيد إلى مسؤولي مركز الاتصال",
    ccSlaFieldDesc_acknowledgementGrace: "مهلة بعد الإقرار قبل تصعيد L2",
    ccSlaUnitLabel_MINUTES: "دقائق",
    ccSlaUnitLabel_HOURS: "ساعات",
    ccSlaUnitLabel_DAYS: "أيام",
    ccSupervisor: "المشرف",
    ccSupervisorMappingSubtitle: "اربط كل وكيل مكالمات بمشرف لتصعيد L1.",
    ccSupervisorMappingTitle: "تعيين المشرف",
    ccToastDuplicateUploadMapping: "يمكن تعيين كل حقل تذكرة من عمود رفع واحد فقط.",
    ccUploadColumnMapping: "تعيين الأعمدة",
    ccUploadKeepAsNew: "الاحتفاظ كحقل جديد:",
    ccUploadMappedTo: "مُربَط بـ:",
    ccViewAllAlerts: "عرض جميع التنبيهات",
    ccViewTimeline: "الخط الزمني",
    ccWeeklyTrackerAgents: "وكلاء المكالمات",
    ccWeeklyTrackerApplyHint: "اختر الوكلاء، ثم انقر مزامنة لوحة المعلومات لتحميل البيانات.",
    ccWeeklyTrackerCallType: "نوع المكالمة (الحالة)",
    ccWeeklyTrackerCallsCompleted: "المكالمات المكتملة",
    ccWeeklyTrackerCallsThisWeek: "مكالمات هذا الأسبوع",
    ccWeeklyTrackerClearAll: "مسح",
    ccWeeklyTrackerCombinedCalls: "المكالمات المجمّعة (جميع الوكلاء)",
    ccWeeklyTrackerCommitments: "الالتزامات المسجّلة",
    ccWeeklyTrackerDailyCalls: "المكالمات اليومية",
    ccWeeklyTrackerExportFailed: "فشل تنزيل تقرير Excel",
    ccWeeklyTrackerExportSuccess: "تم تنزيل Excel للمتتبع الأسبوعي",
    ccWeeklyTrackerExporting: "جاري التصدير…",
    ccWeeklyTrackerFollowupsDone: "المتابعات المنجزة",
    ccWeeklyTrackerKpiCollected: "المبلغ المحصّل (OMR)",
    ccWeeklyTrackerKpiMissed: "المتابعات الفائتة",
    ccWeeklyTrackerKpiPromise: "تحقّق وعد الدفع",
    ccWeeklyTrackerLoadFailed: "فشل تحميل المتتبع الأسبوعي",
    ccWeeklyTrackerNeedsAttention: "يحتاج إلى اهتمام",
    ccWeeklyTrackerNoData: "لا توجد بيانات للمتتبع الأسبوعي للفلاتر المحددة.",
    ccWeeklyTrackerQuickDesc: "أهداف الوكيل حسب الحالة مع تصدير Excel",
    ccWeeklyTrackerRecoveredThisWeek: "المُسترد هذا الأسبوع",
    ccWeeklyTrackerSearchAgents: "البحث عن الوكلاء واختيارهم…",
    ccWeeklyTrackerSelectAgents: "اختر وكيل مكالمات واحداً على الأقل.",
    ccWeeklyTrackerSelectAll: "تحديد الكل",
    ccWeeklyTrackerSelectedCount: "{count} من {total} وكيل محدّد",
    ccWeeklyTrackerSubtitle: "عدد المكالمات اليومية حسب حالة التذكرة (نوع المكالمة) للوكلاء المحددين. أسبوع عمل الأحد–الخميس.",
    ccWeeklyTrackerTargetCallsPlaced: "{done} من {target} مكالمة مستهدفة هذا الأسبوع",
    ccWeeklyTrackerWeekOf: "أسبوع (الأحد)",
    ccWeeklyTrackerWeeklyTarget: "الهدف الأسبوعي",
    ccWeeklyTrackerWeeklyTotal: "الإجمالي الأسبوعي",
  },
  ur: {
    ccManualCallHint:
      "جب براؤزر فون منسلک ہو تو کالز ایپ کے اندر Linkus WebRTC سے کی جاتی ہیں۔ اکاؤنٹ کی موجودہ ٹکٹس منسلک ہو جاتی ہیں؛ نئی ٹکٹ نہیں بنتی۔",
    ccAiCallHint:
      "AI کال کا سیاق و سباق (بقایا رقم اور آخری اپ ڈیٹ) اکاؤنٹ ریکارڈ سے لیا جاتا ہے، اس فارم سے نہیں۔ اکاؤنٹ کی موجودہ ٹکٹس منسلک ہو جاتی ہیں؛ نئی ٹکٹ نہیں بنتی۔ فراہم کنندہ کی خرابی کے دوران کال لائف سائیکل دستی طور پر اپ ڈیٹ ہوتی ہے۔",
    ccMockTelephonyHint:
      "FreeSWITCH برج ترتیب نہیں دیا گیا یا ٹیلیفونی موک ہے — فراہم کنندہ ID مصنوعی ہے (mock-freeswitch-…). کال اور ٹکٹ پھر بھی درست ہیں؛ HTTP برج منسلک ہونے تک لائیو ایونٹس محدود ہو سکتے ہیں۔",
    ccToastCallInitiated:
      "سسٹم میں کال شروع ہو گئی۔ براہ کرم دستی طور پر کال کریں اور اسٹیٹس اپ ڈیٹ کریں۔",
    ccToastCallInitiatedClickToCall: "کال شروع ہو گئی — جب فون بجے تو جواب دیں۔",
    ccClickToCallHint: "نمبر درج کریں اور کال پر ٹیپ کریں۔ پہلے آپ کا ایکسٹینشن بجے گا، پھر کسٹمر۔",
    ccInitiatingCall: "کال شروع ہو رہی ہے…",
    ccMakeCall: "کسٹمر کو کال کریں",
    ccGlobalDialerTitle: "فون",
    ccDialerCall: "کال",
    ccGlobalDialerOpen: "فون ڈائلر کھولیں",
    ccGlobalDialerMinimize: "ڈائلر چھوٹا کریں",
    ccGlobalDialerConnect: "فون منسلک کریں",
    ccDisconnectPhone: "منقطع کریں",
    ccGlobalDialerConnecting: "منسلک ہو رہا ہے…",
    ccGlobalDialerReady: "کال کے لیے تیار",
    ccGlobalDialerOnCall: "کال جاری ہے",
    ccGlobalDialerIncoming: "آنے والی کال",
    ccCallMute: "میوٹ",
    ccCallUnmute: "ان میوٹ",
    ccCallHold: "ہولڈ",
    ccCallResume: "دوبارہ شروع",
    ccCallHangUp: "کال ختم",
    ccCallEndCall: "کال ختم کریں",
    ccCallKeypad: "کی پیڈ",
    ccCallContacts: "رابطے",
    ccCallNote: "نوٹ",
    ccActiveOutboundCall: "فعال آؤٹ باؤنڈ کال",
    ccActiveInboundCall: "فعال ان باؤنڈ کال",
    ccCallOnHold: "ہولڈ پر",
    ccCallOutbound: "آؤٹ باؤنڈ",
    ccCallInbound: "ان باؤنڈ",
    ccIncomingCallAccept: "قبول کریں",
    ccIncomingCallReject: "رد کریں",
    ccIncomingCallAccount: "اکاؤنٹ",
    ccUnknownCaller: "نامعلوم کالر",
    ccGlobalDialerTapToConnect: "منسلک ہونے کے لیے ٹیپ کریں",
    ccGlobalDialerEnterToCall: "نمبر درج کریں اور کال پر ٹیپ کریں",
    ccDialerEnterNumber: "نمبر درج کریں",
    ccPhoneStatusReady: "تیار",
    ccPhoneStatusUnavailable: "دستیاب نہیں",
    ccPhoneStatusEnded: "کال ختم",
    ccPhoneStatusOffline: "آف لائن",
    ccPhoneAdvancedSettings: "آڈیو اور کال ہسٹری",
    ccBrowserPhonePageHint: "نیچے نمبر ڈائل کریں۔ دوسری اسکرینز پر نیچے دائیں فون بٹن استعمال کریں۔",
    ccGlobalDialerNavigateHint: "کال کے دوران آپ کسی بھی اسکرین پر جا سکتے ہیں۔",
    ccGlobalDialerOpenBrowserPhone: "براؤزر فون کھولیں",
    ccGlobalDialerResetPhone: "فون ری سیٹ کریں",
    ccToastBrowserPhoneConnecting: "براؤزر فون منسلک ہو رہا ہے۔ تھوڑی دیر بعد دوبارہ کوشش کریں۔",
    ccToastBrowserPhoneNotReady:
      "براؤزر فون رجسٹرڈ نہیں۔ SIP پاس ورڈ چیک کریں یا فون ڈائلر کھول کر منسلک ہوں۔",
    ccToastBrowserPhoneOfficeNetworkRequired:
      "براؤزر کالنگ صرف آفس نیٹ ورک پر کام کرتی ہے۔ آفس Wi-Fi سے منسلک ہو کر دوبارہ کوشش کریں۔",
    ccToastNativeSipRegistrationFailed:
      "ڈیسک ٹاپ فون PBX پر رجسٹر نہیں ہو سکا۔ آفس LAN یا VPN استعمال کریں، ایکسٹینشن اور SIP پاس ورڈ چیک کریں، اور اسی ایکسٹینشن کے لیے Wave Desktop بند کریں۔",
    ccToastBrowserPhoneAuthFailed:
      "فون ایکسٹینشن رجسٹر نہیں ہو سکی۔ یوزر ایڈمن میں ایجنٹ ایکسٹینشن اور SIP پاس ورڈ چیک کریں۔",
    ccBrowserPhoneExtensionMissing:
      "آپ کے اکاؤنٹ پر فون ایکسٹینشن سیٹ نہیں ہے۔ ایڈمن سے کہیں کہ یوزر ایڈمن میں آپ کی ایکسٹینشن اور ای میل شامل کرے۔",
    ccBrowserPhoneExtensionMismatch:
      "آپ کی فون ایکسٹینشن PBX پر نہیں ملی۔ ایڈمن سے اپنی ایکسٹینشن کی تصدیق کروائیں۔",
    ccBrowserPhoneUnexpectedError:
      "فون سیٹ اپ ناکام ہوا۔ دوبارہ کوشش کریں یا اگر مسئلہ برقرار رہے تو منتظم سے رابطہ کریں۔",
    ccAgentTipBody:
      "دستی کال ٹریکنگ کے دوران واضح تعامل کے نوٹس ریمارکس میں شامل کریں تاکہ ٹکٹ ٹائم لائن مفید رہے۔",
    ccMenuTickets: "ٹکٹس",
    ccMenuAccountPlans: "Payment Plans",
    ccMenuAccountPlanTypes: "Plan Templates",
    ccMenuAgentWorkspace: "ٹکٹ انٹری",
    ccMenuTicketBulkUpload: "بلک اپ لوڈ",
    ccMenuTicketUploadHistory: "اپ لوڈ ہسٹری",
    ccMenuTicketColumns: "کالم سیٹنگز",
    ccMenuTicketSettingsMerged: "ٹکٹ سیٹنگز",
    ccTicketSettingsPageTitle: "ٹکٹ سیٹنگز",
    ccTicketSettingsPageSubtitle: "اپنی تنظیم کے لیے ٹکٹ کالمز اور اسٹیٹس workflow ترتیب دیں۔",
    ccTicketSettingsTabColumns: "کالمز",
    ccTicketSettingsTabStatuses: "اسٹیٹس",
    ccTicketSettingsNoTabAccess: "ٹکٹ سیٹنگز دیکھنے کی اجازت نہیں ہے۔",
    ccAccountPlansPageTitle: "Payment Plans",
    ccAccountPlansPageSubtitle: "ہر account کے لیے installment collections اور automated payment reminder tickets شیڈول کریں۔",
    ccAccountPlansManageTypes: "Plan templates manage کریں",
    ccAccountPlanType: "Plan template",
    ccAccountPlanSelectType: "Plan template منتخب کریں",
    ccAccountPlanTypeRequired: "Plan template درکار ہے",
    ccAccountPlanTypeSelectHint: "Schedule، assignment، reminders اور ticket defaults منتخب template سے آتے ہیں۔",
    ccAccountPlanTypesPageTitle: "Plan Templates",
    ccAccountPlanTypesPageSubtitle: "Payment plans کے لیے دوبارہ استعمال ہونے والے schedule templates define کریں۔",
    ccAccountPlanTypesBackToPlans: "Payment plans پر واپس",
    ccAccountPlanTypeCreate: "Template بنائیں",
    ccAccountPlanTypeCreateTitle: "Plan template بنائیں",
    ccAccountPlanTypeEditTitle: "Plan template میں ترمیم",
    ccAccountPlanTypeFormHint: "Template settings اس template سے بنائے گئے ہر payment plan پر لاگو ہوتی ہیں۔",
    ccAccountPlanTypeName: "Template نام",
    ccAccountPlanTypeDescription: "تفصیل",
    ccAccountPlanTypeNameRequired: "Template نام درکار ہے",
    ccAccountPlanTypeDefaultAgent: "Default call agent",
    ccAccountPlanTypeDefaultAgentOptional: "Plan creation پر منتخب کریں",
    ccAccountPlanTypeDefaultAgentHint: "Optional۔ ہر payment plan بناتے وقت agent منتخب کرنے کے لیے خالی چھوڑیں۔",
    ccAccountPlanTypeActive: "Active (نئے payment plans کے لیے دستیاب)",
    ccAccountPlanTypesEmptyTitle: "ابھی کوئی plan template نہیں",
    ccAccountPlanTypesEmptyHint: "Payment plans شامل کرنے سے پہلے installment schedules معیاری بنانے کے لیے template بنائیں۔",
    ccAccountPlanTypeCreated: "Plan template بن گیا",
    ccAccountPlanTypeUpdated: "Plan template اپڈیٹ ہو گیا",
    ccAccountPlanTypeLoadFailed: "Plan templates لوڈ نہیں ہو سکے",
    ccAccountPlanTypeSaveFailed: "Plan template محفوظ نہیں ہو سکا",
    ccAccountPlanTypeInfoTitle: "Plan templates کیسے کام کرتے ہیں",
    ccAccountPlanTypeInfoBody:
      "Templates installment frequency، reminders اور ticket defaults define کرتے ہیں۔ ہر payment plan ایک template منتخب کرتا ہے اور account-specific amount و dates شامل کرتا ہے۔",
    ccAccountPlanTypeKpiTotal: "کل templates",
    ccAccountPlanTypeKpiActive: "Active templates",
    ccAccountPlanTypeKpiInUse: "استعمال میں",
    ccAccountPlanTypeUsage: "Usage",
    ccAccountPlanTypeUsageHint: "Active / total payment plans",
    ccAccountPlanTypeSearchPlaceholder: "Template نام سے تلاش…",
    ccAccountPlanTypeDetailSubtitle: "Template configuration اور usage",
    ccAccountPlanTypeDeactivateBlocked: "جب active payment plans اس template استعمال کر رہے ہوں تو deactivate نہیں کیا جا سکتا۔",
    ccAccountPlanTypeUsageSummary: "{count} payment plan(s) استعمال کر رہے ہیں۔",
    ccAccountPlanTypeResultsLabel: "templates",
    ccAccountPlanTypeScheduleSummary: "{frequency} · {count} installments",
    ccAccountPlanTypeScheduleColumn: "Schedule",
    ccAccountPlanTypeEmptyFilteredTitle: "آپ کے filters سے کوئی template match نہیں",
    ccAccountPlanTypeEmptyFilteredHint: "Search یا status filters adjust کریں۔",
    ccAccountPlanTypeKpiClickHint: "List filter کرنے کے لیے card پر click کریں۔",
    ccAccountPlanTypeDetailScheduleSection: "Installment schedule",
    ccAccountPlanTypeDetailTicketSection: "Ticket defaults",
    ccAccountPlanTypeDetailUsageSection: "Usage",
    ccAccountPlanTypeUsageEmpty: "ابھی کسی payment plan میں استعمال نہیں۔",
    ccAccountPlanTypeInUseWarning:
      "یہ template {count} payment plan(s) استعمال کر رہا ہے۔ Changes صرف future installments پر لاگو ہوں گی۔",
    ccAccountPlanTypeBreadcrumbPaymentPlans: "Payment Plans",
    ccAccountPlanTypeFormSectionBasic: "Basic information",
    ccAccountPlanTypeFormSectionSchedule: "Installment schedule",
    ccAccountPlanTypeFormSectionTickets: "Ticket defaults",
    ccAccountPlanTypeFormPreviewTitle: "Template summary",
    ccAccountPlanTypeFormPreviewBody: "Reminder {reminder} · {assignment} · priority {priority}",
    ccAccountPlanCreate: "Payment plan بنائیں",
    ccAccountPlanCreateTitle: "Payment plan بنائیں",
    ccAccountPlanEditTitle: "Payment plan میں ترمیم",
    ccAccountPlanDetailTitle: "Payment plan کی تفصیل",
    ccAccountPlanAccount: "اکاؤنٹ نمبر",
    ccAccountPlanMobileNumbers: "موبائل نمبر",
    ccAccountPlanAmount: "قسط کی رقم (OMR)",
    ccAccountPlanTotalAmount: "کل رقم (OMR)",
    ccAccountPlanAmountColumn: "رقم",
    ccAccountPlanTotalAmountRequired: "کل رقم درکار ہے",
    ccAccountPlanPerInstallmentHint: "راؤنڈنگ سے پہلے تقریباً {amount} OMR فی قسط",
    ccAccountPlanPreviewSchedule: "شیڈول دیکھیں",
    ccAccountPlanPreviewTitle: "قسطوں کا پیش نظارہ",
    ccAccountPlanPreviewEmpty: "شیڈول دیکھنے کے لیے کل رقم، قسطوں کی تعداد اور آغاز کی تاریخ درج کریں۔",
    ccAccountPlanPreviewTotal: "کل: {amount} OMR",
    ccAccountPlanInstallmentCount: "قسطوں کی تعداد",
    ccAccountPlanFrequency: "تعدد",
    ccAccountPlanFrequency_ONE_TIME: "یکمختلف ادائیگی",
    ccAccountPlanOneTimeNote: "یکمختلف پلان واجب الادا تاریخ پر ایک ہی ادائیگی ٹکٹ بناتا ہے۔",
    ccAccountPlanStartDate: "آغاز کی تاریخ (پہلی واجب الادا)",
    ccAccountPlanAssignmentMode: "ایجنٹ تفویض",
    ccAccountPlanAssignedAgent: "تفویض شدہ کال ایجنٹ",
    ccAccountPlanAutoAssign: "خودکار تفویض",
    ccAccountPlanSelectAgent: "کال ایجنٹ منتخب کریں",
    ccAccountPlanReminderOffset: "ادائیگی یاد دہانی",
    ccAccountPlanTicketPriority: "ٹکٹ ترجیح",
    ccAccountPlanRemarksTemplate: "ڈیفالٹ ٹکٹ ریمارکس",
    ccAccountPlanAmountRequired: "قسط کی رقم درکار ہے",
    ccAccountPlanAmountMin: "قسط کی رقم صفر سے زیادہ ہونی چاہیے",
    ccAccountPlanFrequencyRequired: "تعدد درکار ہے",
    ccAccountPlanInstallmentCountRequired: "قسطوں کی تعداد درکار ہے",
    ccAccountPlanInstallmentCountMax: "قسطوں کی تعداد 120 سے زیادہ نہیں ہو سکتی",
    ccAccountPlanStartDateRequired: "آغاز کی تاریخ درکار ہے",
    ccAccountPlanAssignedAgentRequired: "تفویض شدہ کال ایجنٹ درکار ہے",
    ccAccountPlanProgress: "پیشرفت",
    ccAccountPlanNextDue: "اگلی واجب الادا",
    ccAccountPlanStatus: "حالت",
    ccAccountPlanActions: "اقدامات",
    ccAccountPlanView: "دیکھیں",
    ccAccountPlanEdit: "ترمیم",
    ccAccountPlanCancel: "پلان منسوخ کریں",
    ccAccountPlanCancelConfirm: "کیا یہ payment plan اور زیر التواء قسطیں منسوخ کریں؟",
    ccAccountPlanCancelFailed: "Payment plan منسوخ کرنے میں ناکامی",
    ccAccountPlanInstallmentSchedule: "قسطوں کا شیڈول",
    ccAccountPlanDueDate: "واجب الادا تاریخ",
    ccAccountPlanReminderDate: "یاد دہانی کی تاریخ",
    ccAccountPlanTicket: "ٹکٹ",
    ccAccountPlanOpenTicket: "ٹکٹ کھولیں",
    ccAccountPlansEmpty: "کوئی payment plan نہیں ملا۔",
    ccAccountPlansLoadFailed: "Payment plans لوڈ کرنے میں ناکامی",
    ccAccountPlansSaveFailed: "Payment plan محفوظ کرنے میں ناکامی",
    ccAccountPlanFilterStatus: "حالت",
    ccAccountPlanFilterAll: "سب",
    ccAccountPlanFiltersTitle: "فلٹرز",
    ccAccountPlanResultsLabel: "پلان",
    ccAccountPlanInstallmentsLabel: "قسطیں",
    ccAccountPlansEmptyTitle: "ابھی کوئی payment plan نہیں",
    ccAccountPlansEmptyHint: "کسی account کے لیے recurring collection tickets اور reminders شیڈول کرنے کے لیے payment plan بنائیں۔",
    ccClearFilters: "فلٹرز صاف کریں",
    ccListFiltersTitle: "فلٹرز",
    ccAlertsResultsLabel: "الرٹس",
    ccAlertsEmptyTitle: "کوئی الرٹ نہیں ملا",
    ccAlertsEmptyHint: "فلٹرز ایڈجسٹ کریں یا بعد میں نئے SLA انتباہات دیکھیں۔",
    ccNotificationsResultsLabel: "اطلاعات",
    ccNotificationsEmptyTitle: "ابھی کوئی اطلاع نہیں",
    ccNotificationsUnreadOnly: "صرف غیر پڑھی ہوئی",
    ccNotificationsColumnTitle: "عنوان",
    ccNotificationsColumnMessage: "پیغام",
    ccNotificationsColumnDate: "تاریخ",
    ccRead: "پڑھا ہوا",
    ccUnread: "غیر پڑھا",
    ccOpen: "کھولیں",
    ccAccountPlanStatus_ACTIVE: "فعال",
    ccAccountPlanStatus_COMPLETED: "مکمل",
    ccAccountPlanStatus_CANCELLED: "منسوخ",
    ccAccountPlanStatus_SCHEDULED: "شیڈول شدہ",
    ccAccountPlanStatus_TICKET_CREATED: "ٹکٹ بن گیا",
    ccAccountPlanStatus_SKIPPED: "چھوڑ دیا",
    ccMenuInbox: "ان باکس",
    ccMenuBrowserPhone: "براؤزر فون",
    ccMenuCampaigns: "مہمات",
    ccMenuAnalytics: "ڈیش بورڈ",
    ccMenuTicketSettings: "اسٹیٹس سیٹنگز",
    ccMenuWhatsAppSettings: "واٹس ایپ سیٹنگز",
    ccMenuNotifications: "اطلاعات",
    ccMenuPaymentReminders: "ادائیگی کی یاد دہانیاں",
    ccMenuPaymentConfirmations: "ادائیگی کی تصدیقات",
    ccMenuOrganizations: "تنظیمیں",
    ccMenuOrganizationsTooltip: "پلیٹ فارم تنظیمیں منظم کریں",
    ccDisabled: "غیر فعال",
    ccOrgPageTitle: "تنظیمیں",
    ccOrgTableOrganization: "Organization",
    ccOrgStatTotal: "Total organizations",
    ccOrgStatTotalHint: "Across all pages matching your filters",
    ccOrgStatActive: "Active on page",
    ccOrgStatActiveHint: "Of {count} rows shown",
    ccOrgStatCalling: "Calling enabled",
    ccOrgStatCallingHint: "On this page ({count} rows)",
    ccOrgPageSubtitle: "کرایہ دار تنظیمیں، فیچرز، اور انٹیگریشن سیٹنگز بنائیں اور منظم کریں۔",
    ccOrgCreate: "نئی تنظیم",
    ccOrgInfoTitle: "پلیٹ فارم تنظیمیں",
    ccOrgInfoBody:
      "ہر تنظیم اپنے صارفین، ٹکٹوں، اور انٹیگریشنز کے ساتھ الگ کرایہ دار ہے۔ ہر تنظیم کے لیے payment proof template، payment confirmation، اور WhatsApp ترتیب دینے کے لیے action icons استعمال کریں۔",
    ccOrgLoadFailed: "تنظیمیں لوڈ کرنے میں ناکام",
    ccOrgResultsLabel: "تنظیمیں",
    ccOrgSearchPlaceholder: "کوڈ یا نام سے تلاش...",
    ccOrgSortBy: "ترتیب",
    ccOrgSortDirection: "سمت",
    ccOrgSortAsc: "صعودی",
    ccOrgSortDesc: "نزولی",
    ccOrgSortCode: "کوڈ",
    ccOrgSortName: "نام",
    ccOrgSortCreated: "تاریخِ تخلیق",
    ccOrgIncludeInactive: "غیر فعال شامل کریں",
    ccOrgPerPage: "فی صفحہ",
    ccOrgEmptyTitle: "ابھی کوئی تنظیم نہیں",
    ccOrgEmptyHint: "نیا کرایہ دار شامل کرنے کے لیے پہلی تنظیم بنائیں۔",
    ccOrgEmptyFilteredTitle: "آپ کے فلٹرز سے کوئی تنظیم نہیں ملی",
    ccOrgEmptyFilteredHint: "مختلف تلاش یا غیر فعال تنظیمیں شامل کر کے دوبارہ کوشش کریں۔",
    ccOrgCode: "کوڈ",
    ccOrgName: "نام",
    ccOrgCalling: "کالنگ",
    ccOrgAiCalling: "AI کالنگ",
    ccOrgCreatedAt: "تخلیق",
    ccOrgPaymentProofTemplate: "Payment proof template",
    ccOrgPaymentConfirmation: "Payment confirmation",
    ccOrgWhatsApp: "WhatsApp settings",
    ccOrgTelephony: "Telephony provider",
    ccTelephonyProvider: "Telephony provider",
    ccTelephonyProviderD2c: "D2C Cloud PBX",
    ccTelephonyProviderGrandstream: "Grandstream UCM",
    ccTelephonyD2cSection: "Cloud PBX overrides",
    ccTelephonyD2cHint: "Leave blank to use server environment defaults.",
    ccTelephonyD2cBaseUrl: "PBX base URL",
    ccTelephonyD2cAccessId: "Access ID",
    ccTelephonyD2cAccessKey: "Access key",
    ccTelephonyGrandstreamSection: "Grandstream UCM connection",
    ccTelephonyGrandstreamDesktopHint:
      "Agent calling uses the Emmad desktop app with native SIP (UDP/TCP to UCM), like Wave Desktop. Set SIP domain to the UCM realm; LAN IP from base URL is used for registration when present.",
    ccTelephonyGsBaseUrl: "UCM API base URL",
    ccTelephonyGsUsername: "API username",
    ccTelephonyGsPassword: "API password",
    ccTelephonyGsRecordingBaseUrl: "Recording base URL",
    ccTelephonyGsSipDomain: "SIP domain",
    ccTelephonyGsSipWssUrl: "SIP WSS URL",
    ccTelephonyGsSipWsUrl: "SIP WS URL (optional)",
    ccTelephonyGsCdrEndpoint: "CDR endpoint path",
    ccTelephonyGsCdrSyncEnabled: "Enable scheduled CDR sync",
    ccTelephonyGsWebrtcEnabled: "Enable browser WebRTC",
    ccTelephonyGsWebhookSecret: "Event webhook secret",
    ccTelephonyGsSipDomainRequired: "SIP domain is required for Grandstream",
    ccTelephonyGsSipWssRequired: "SIP WSS or WS URL is required for Grandstream",
    ccTelephonySecretConfigured: "A value is already saved; leave blank to keep it.",
    ccTelephonyLoadFailed: "Failed to load telephony configuration",
    ccTelephonySaveSuccess: "Telephony configuration saved",
    ccTelephonySaveFailed: "Failed to save telephony configuration",
    ccOrgDeactivate: "غیر فعال کریں",
    ccOrgDeactivateTitle: "تنظیم غیر فعال کریں",
    ccOrgDeactivateConfirm: "\"{name}\" غیر فعال کریں؟ صارفین اس تنظیم میں لاگ ان نہیں کر سکیں گے۔",
    ccOrgAccessDenied: "صرف platform super-admin تنظیمیں منظم کر سکتے ہیں۔",
    ccOrgLogoUploaded: "تنظیم کا لوگو اپ لوڈ ہو گیا",
    ccOrgLogoUploadFailed: "لوگو اپ لوڈ ناکام",
    ccOrgNameRequired: "تنظیم کا نام ضروری ہے",
    ccOrgCodeRequired: "تنظیم کا کوڈ ضروری ہے",
    ccOrgCodeInvalid: "کوڈ میں بڑے حروف، نمبر، _ یا - ہونے چاہئیں",
    ccOrgCodePlaceholder: "ACME",
    ccOrgNamePlaceholder: "Acme Collections",
    ccOrgCreateSuccess: "تنظیم بن گئی",
    ccOrgUpdated: "تنظیم اپ ڈیٹ ہو گئی",
    ccOrgSaveFailed: "تنظیم محفوظ کرنے میں ناکام",
    ccOrgDeactivated: "تنظیم غیر فعال ہو گئی",
    ccOrgDeactivateFailed: "تنظیم غیر فعال کرنے میں ناکام",
    ccOrgTemplateLoadFailed: "Payment proof template لوڈ ناکام",
    ccOrgTemplateSaved: "Payment proof template محفوظ ہو گیا",
    ccOrgTemplateSaveFailed: "Payment proof template محفوظ ناکام",
    ccOrgCreateTitle: "تنظیم بنائیں",
    ccOrgEditTitle: "تنظیم میں ترمیم",
    ccOrgCreateFormHint: "منفرد tenant code منتخب کریں۔ بننے کے بعد تبدیل نہیں ہو سکتا۔",
    ccOrgEditFormHint: "تنظیم کی تفصیلات، calling features، اور لوگو اپ ڈیٹ کریں۔",
    ccOrgCodeHint: "بڑے حروف، نمبر، _ یا - (مثلاً ACME_CO)",
    ccOrgPageSize: "صفحے کا سائز",
    ccOrgRetiredCode: "Retired code",
    ccOrgDefaultProtected: "Default platform organization غیر فعال نہیں کی جا سکتی",
    ccOrgActionsLegend: "Row actions: payment proof template, payment confirmation, WhatsApp, edit, deactivate",
    ccOrgActive: "فعال",
    ccOrgCallingFeatures: "Calling features",
    ccOrgSectionIdentity: "Organization details",
    ccOrgSectionIdentityHint: "Code is permanent after create; name and status can be updated anytime.",
    ccOrgSectionBranding: "Branding",
    ccOrgCallingChannelsHint: "Control where agents can place calls. PBX settings are configured separately.",
    ccOrgCallingEnabled: "Manual calling فعال کریں",
    ccOrgCallingMasterHint: "Master switch for call logs, campaigns, recordings, and agent dialers.",
    ccOrgWebCallingEnabled: "Web calling",
    ccOrgWebCallingHint: "Browser phone and WebRTC in the web app.",
    ccOrgDesktopCallingEnabled: "Desktop calling",
    ccOrgDesktopCallingHint: "WASL desktop app with native SIP (UDP/TCP).",
    ccOrgAiCallingEnabled: "AI automated calling فعال کریں",
    ccOrgAiCallingHint: "Outbound AI voice campaigns for this organization.",
    ccOrgCallingMasterOffNote: "Turn on manual calling above to enable web, desktop, or AI channels.",
    ccOrgActiveHint: "Inactive organizations cannot be selected by agents.",
    ccOrgLogoFormatsHint: "PNG, JPG, GIF, or WebP.",
    ccOrgTelephonyProviderUnset: "Telephony not configured",
    ccOrgChannelsWeb: "Web",
    ccOrgChannelsDesktop: "Desktop",
    ccOrgLogo: "لوگو",
    ccOrgLogoHint: "Payment proof documents پر دکھایا جاتا ہے۔ PNG یا JPG بہتر ہے۔",
    ccOrgNoLogo: "کوئی لوگو اپ لوڈ نہیں",
    ccOrgLogoUploading: "اپ لوڈ ہو رہا ہے...",
    ccOrgLogoUpload: "لوگو اپ لوڈ کریں",
    ccNotificationsTitle: "اطلاعات",
    ccNotificationsSubtitleOne: "1 غیر پڑھی ہوئی اطلاع",
    ccNotificationsSubtitleMany: "{count} غیر پڑھی ہوئی اطلاعات",
    ccNotificationsEmpty: "ابھی کوئی اطلاع نہیں۔",
    ccNotificationsMarkAllRead: "سب کو پڑھا ہوا نشان زد کریں",
    ccNotificationsMarkAllReadSuccess: "تمام اطلاعات پڑھی ہوئی نشان زد ہو گئیں",
    ccNotificationsMarkAllReadFailed: "تمام اطلاعات نشان زد کرنے میں ناکام",
    ccNotificationsMarkReadFailed: "اطلاع نشان زد کرنے میں ناکام",
    ccNotificationsLoadFailed: "اطلاعات لوڈ کرنے میں ناکام",
    ccNotificationsPaymentAlertTitle: "آج ادائیگی واجب ہے",
    ccNotificationsPaymentConfirmedTitle: "ادائیگی کی تصدیق",
    ccNotificationsRemindersSection: "ادائیگی کی یاد دہانیاں",
    ccNotificationsConfirmationsSection: "ادائیگی کی تصدیقات",
    ccNotificationsRemindersEmpty: "ابھی کوئی ادائیگی کی یاد دہانی نہیں۔",
    ccNotificationsConfirmationsEmpty: "ابھی کوئی ادائیگی کی تصدیق نہیں۔",
    ccWhatsAppSettingsTitle: "واٹس ایپ سیٹنگز",
    ccWhatsAppSettingsSubtitle: "اس تنظیم کے لیے WhatsApp credentials ترتیب دیں (Direct Meta یا Flowmatical)۔",
    ccWhatsAppProvider: "Provider",
    ccWhatsAppProviderMetaDirect: "Direct Meta API",
    ccWhatsAppProviderFlowmatical: "Flowmatical",
    ccWhatsAppFlowmaticalBaseUrl: "Flowmatical base URL",
    ccWhatsAppFlowmaticalBaseUrlRequired: "WhatsApp فعال ہونے پر Flowmatical base URL درکار ہے",
    ccWhatsAppFlowmaticalApiToken: "Flowmatical API token",
    ccWhatsAppFlowmaticalWebhookSecret: "Flowmatical webhook secret",
    ccWhatsAppFlowmaticalWebhookUrlLabel: "Flowmatical میں یہ inbound webhook URL رجسٹر کریں",
    ccWhatsAppFlowmaticalWebhookHint: "Flowmatical میں Settings > Integrations & API > Signed webhooks پر جائیں، یہ URL پیسٹ کریں، delivery فعال کریں، اور whsec_ سیکрет ایک بار دکھائے جانے پر کاپی کریں۔ Meta callbacks یہاں نہ بھیجیں — Flowmatical Meta کو platform level پر manage کرتا ہے۔",
    ccWhatsAppFlowmaticalApiTokenHint: "Settings > Integrations & API > API tokens سے messages:send (اور ضرورت ہو تو templates:read) والے scoped flw_ token بنائیں۔",
    ccWhatsAppFlowmaticalBaseUrlHint: "صرف API host (ڈیفالٹ https://api.flowmatical.com)۔ /api/v1/integrations/public شامل نہ کریں۔",
    ccWhatsAppEnabled: "اس تنظیم کے لیے WhatsApp فعال کریں",
    ccWhatsAppPhoneNumberId: "فون نمبر ID",
    ccWhatsAppPhoneNumberIdRequired: "WhatsApp فعال ہونے پر فون نمبر ID درکار ہے",
    ccWhatsAppAccessToken: "Access token",
    ccWhatsAppWebhookVerifyToken: "Webhook verify token",
    ccWhatsAppAppSecret: "App secret",
    ccWhatsAppApiVersion: "Graph API version",
    ccWhatsAppApiVersionRequired: "API version درکار ہے",
    ccWhatsAppSecretConfiguredHint: "پہلے سے ترتیب شدہ — موجودہ قدر رکھنے کے لیے خالی چھوڑیں",
    ccWhatsAppSettingsHint: "اسرار ہر تنظیم کے لیے محفوظ ہوتے ہیں۔ موجودہ اقدار رکھنے کے لیے خالی چھوڑیں۔",
    ccWhatsAppSave: "WhatsApp سیٹنگز محفوظ کریں",
    ccWhatsAppSaveSuccess: "WhatsApp سیٹنگز محفوظ ہو گئیں",
    ccWhatsAppSaveFailed: "WhatsApp سیٹنگز محفوظ کرنے میں ناکام",
    ccWhatsAppLoadFailed: "WhatsApp سیٹنگز لوڈ کرنے میں ناکام",
    ccEmDash: "—",
    ccBack: "← واپس",
    ccProfileTitle: "میرا پروفائل",
    ccProfileSubtitle: "اپنے اکاؤنٹ کی تفصیلات دیکھیں اور اپ ڈیٹ کریں۔",
    ccProfileName: "مکمل نام",
    ccProfileEmail: "ای میل پتہ",
    ccProfileRole: "کردار",
    ccProfileOrganization: "تنظیم",
    ccProfileAgentExtension: "ایجنٹ ایکسٹینشن",
    ccProfileSave: "پروفائل محفوظ کریں",
    ccProfileUpdated: "پروفائل اپ ڈیٹ ہو گیا",
    ccProfileLoadFailed: "پروفائل لوڈ کرنے میں ناکام",
    ccProfileUpdateFailed: "پروفائل اپ ڈیٹ کرنے میں ناکام",
    ccProfileChangePassword: "پاس ورڈ تبدیل کریں",
    ccProfileChangePasswordHint: "مضبوط پاس ورڈ استعمال کریں جس میں بڑے و چھوٹے حروف، نمبر اور خاص علامت شامل ہو۔",
    ccCurrentPassword: "موجودہ پاس ورڈ",
    ccNewPassword: "نیا پاس ورڈ",
    ccConfirmPassword: "نیا پاس ورڈ تصدیق کریں",
    ccCurrentPasswordRequired: "موجودہ پاس ورڈ درکار ہے",
    ccNewPasswordRequired: "نیا پاس ورڈ درکار ہے",
    ccConfirmPasswordRequired: "براہ کرم نیا پاس ورڈ تصدیق کریں",
    ccPasswordComplexity: "پاس ورڈ کم از کم 8 حروف کا ہونا چاہیے اور اس میں بڑے، چھوٹے حروف، نمبر اور خاص علامت شامل ہونی چاہیے",
    ccPasswordsMustMatch: "پاس ورڈ مماثل نہیں",
    ccPasswordChanged: "پاس ورڈ کامیابی سے تبدیل ہو گیا",
    ccPasswordChangeFailed: "پاس ورڈ تبدیل کرنے میں ناکام",
    ccInvalidEmail: "درست ای میل درج کریں",
    ccEmailRequired: "ای میل درکار ہے",
    ccShowPassword: "پاس ورڈ دکھائیں",
    ccHidePassword: "پاس ورڈ چھپائیں",
    ccClose: "بند کریں",
    ccReset: "ریسیٹ",
    ccLoading: "لوڈ ہو رہا ہے...",
    ccSaving: "محفوظ ہو رہا ہے...",
    ccDeleting: "حذف ہو رہا ہے...",
    ccRefreshing: "تازہ کیا جا رہا ہے...",
    ccProcessing: "پروسیس ہو رہا ہے...",
    ccAll: "تمام",
    ccUnknown: "نامعلوم",
    ccUserFallback: "صارف",
    ccAccountNo: "اکاؤنٹ نمبر",
    ccMobileNumber: "موبائل نمبر",
    ccCustomerName: "گاہک کا نام",
    ccAccountNumber: "اکاؤنٹ نمبر",
    ccAccountNumberRequired: "اکاؤنٹ نمبر درکار ہے",
    ccPhoneNumber: "فون نمبر",
    ccPhoneNumberRequired: "فون نمبر درکار ہے",
    ccStatus: "اسٹیٹس",
    ccPriority: "ترجیح",
    ccPriorityRequired: "ترجیح درکار ہے",
    ccRemarks: "ریمارکس",
    ccOutstanding: "بقایا",
    ccPrevious: "پچھلا",
    ccNext: "اگلا",
    ccRows: "قطاریں",
    ccActions: "اعمال",

    // WhatsApp
    ccWhatsApp: "واٹس ایپ",
    ccWhatsAppChat: "واٹس ایپ چیٹ",
    ccWhatsAppOpenChat: "واٹس ایپ چیٹ کھولیں",
    ccWhatsAppNoPhone: "اس ٹکٹ پر موبائل نمبر نہیں ہے",
    ccWhatsAppSend: "بھیجیں",
    ccWhatsAppSendFailed: "واٹس ایپ پیغام بھیجنے میں ناکامی",
    ccWhatsAppMessageSent: "واٹس ایپ پیغام بھیج دیا گیا",
    ccWhatsAppEmpty: "ابھی تک کوئی واٹس ایپ پیغام نہیں۔ گفتگو شروع کرنے کے لیے پیغام بھیجیں۔",
    ccWhatsAppComposePlaceholder: "واٹس ایپ پیغام لکھیں...",
    ccWhatsAppChatWith: "چیٹ کریں",
    ccWhatsAppThreadLoadFailed: "واٹس ایپ پیغامات لوڈ کرنے میں ناکامی",
    ccWhatsAppStatusSent: "بھیجا گیا",
    ccWhatsAppStatusDelivered: "پہنچایا گیا",
    ccWhatsAppStatusRead: "پڑھا گیا",
    ccWhatsAppStatusFailed: "ناکام",
    ccWhatsAppStatusPending: "زیر التواء",
    ccWhatsAppSelectNumber: "موبائل نمبر منتخب کریں",
    ccWhatsAppCheckingNumber: "واٹس ایپ نمبر کی تصدیق ہو رہی ہے...",
    ccWhatsAppNumberValid: "واٹس ایپ پر رجسٹرڈ",
    ccWhatsAppNumberInvalidHint: "یہ نمبر واٹس ایپ پر رجسٹرڈ نہیں ہے۔ پیغامات نہیں پہنچ سکتے۔",
    ccWhatsAppCheckFailed: "واٹس ایپ نمبر کی تصدیق نہیں ہو سکی",
    ccWhatsAppAttachFile: "فائل منسلک کریں",
    ccWhatsAppSelectedFile: "منتخب فائل",
    ccWhatsAppAttachment: "منسلکہ",
    ccWhatsAppDownload: "ڈاؤن لوڈ",
    ccWhatsAppDownloadFailed: "منسلکہ ڈاؤن لوڈ کرنے میں ناکامی",
    ccWhatsAppNoPhoneHint: "واٹس ایپ پیغامات شروع کرنے کے لیے ٹکٹ پر موبائل نمبر شامل کریں۔",
    ccWhatsAppEmptyTitle: "ابھی تک کوئی پیغام نہیں",
    ccWhatsAppComposerHint: "بھیجنے کے لیے Enter دبائیں۔ نئی لائن کے لیے Shift+Enter۔ زیادہ سے زیادہ منسلکہ 16 MB۔",
    ccWhatsAppFileTooLarge: "منسلکہ 16 MB یا اس سے چھوٹا ہونا چاہیے۔",
    ccWhatsAppMetaWebhookUrlLabel: "Meta Developer Dashboard میں یہ callback URL رجسٹر کریں",
    ccWhatsAppMetaWebhookHint:
      "Meta Developer Dashboard → WhatsApp → Configuration میں Callback URL یہ address سیٹ کریں اور اوپر والا webhook verify token استعمال کریں۔",
    ccWhatsAppCopyUrl: "URL کاپی کریں",
    ccWhatsAppUrlCopied: "URL کلپ بورڈ پر کاپی ہو گیا",
    ccTicketAttachments: "ٹکٹ منسلکات",
    ccTicketAttachFile: "فائل منسلک کریں",
    ccTicketAttachmentsHint: "اس ٹکٹ سے متعلق دستاویزات، تصاویر، یا دیگر فائلیں اپ لوڈ کریں۔",
    ccTicketAttachmentsEmpty: "اس ٹکٹ سے ابھی تک کوئی فائل منسلک نہیں ہے۔",
    ccTicketAttachmentUploaded: "فائل ٹکٹ سے منسلک ہو گئی",
    ccTicketAttachmentUploadFailed: "فائل منسلک کرنے میں ناکامی",
    ccTicketAttachmentDownload: "ڈاؤن لوڈ",
    ccTicketAttachmentDownloadFailed: "فائل ڈاؤن لوڈ کرنے میں ناکامی",
    ccTicketAttachmentsLoadFailed: "ٹکٹ منسلکات لوڈ کرنے میں ناکامی",
    ccConfirmDeleteAttachment: "یہ منسلکہ حذف کریں؟ اسے واپس نہیں کیا جا سکتا۔",
    ccTicketAttachmentDeleted: "منسلکہ حذف ہو گیا",
    ccTicketAttachmentDeleteFailed: "منسلکہ حذف کرنے میں ناکامی",

    ccPaymentProof: "ادائیگی کا ثبوت",
    ccPaymentProofOpen: "ادائیگی کا ثبوت",
    ccPaymentProofHint: "ادائیگی کی تصدیق کے بعد گاہک کے لیے کلیئرنس سرٹیفکیٹ PDF بنائیں۔ فیلڈز میں ترمیم کریں، PDF اس ٹکٹ پر محفوظ کریں، پھر ڈاؤن لوڈ یا واٹس ایپ سے بھیجیں۔",
    ccPaymentProofLoadFailed: "ادائیگی کا ثبوت لوڈ کرنے میں ناکامی",
    ccPaymentProofDraftSaved: "ادائیگی کے ثبوت کا مسودہ محفوظ ہو گیا",
    ccPaymentProofDraftSaveFailed: "ادائیگی کے ثبوت کا مسودہ محفوظ کرنے میں ناکامی",
    ccPaymentProofSaved: "ادائیگی کا ثبوت محفوظ ہو گیا",
    ccPaymentProofSaveFailed: "ادائیگی کا ثبوت محفوظ کرنے میں ناکامی",
    ccPaymentProofDownloadFailed: "ادائیگی کا ثبوت ڈاؤن لوڈ کرنے میں ناکامی",
    ccPaymentProofPreview: "PDF پیش نظارہ",
    ccPaymentProofSaveDraft: "مسودہ محفوظ کریں",
    ccPaymentProofSaveDownload: "محفوظ کریں اور PDF ڈاؤن لوڈ کریں",
    ccPaymentProofDownloadSaved: "محفوظ PDF ڈاؤن لوڈ کریں",
    ccPaymentProofSendWhatsApp: "واٹس ایپ سے بھیجیں",
    ccPaymentProofWhatsAppSent: "ادائیگی کا ثبوت واٹس ایپ سے بھیج دیا گیا",
    ccPaymentProofWhatsAppFailed: "واٹس ایپ سے ادائیگی کا ثبوت بھیجنے میں ناکامی",
    ccPaymentProofWhatsAppMessage: "واٹس ایپ پیغام",
    ccPaymentProofWhatsAppPhone: "گاہک کا واٹس ایپ نمبر",
    ccPaymentProofPhoneRequired: "واٹس ایپ فون نمبر منتخب یا درج کریں",
    ccPaymentProofGeneratedAt: "آخری بار بنایا گیا",
    ccPaymentProofTemplateTitle: "ادائیگی کے ثبوت کا ٹیمپلیٹ",
    ccPaymentProofTemplatePageHint: "اپنی تنظیم کے لیے کلیئرنس سرٹیفکیٹ کا لے آؤٹ، لیبلز، فیلڈ میپنگ، اور ڈیفالٹ واٹس ایپ پیغام ترتیب دیں۔",
    ccPaymentProofTemplateSaved: "ادائیگی کے ثبوت کا ٹیمپلیٹ محفوظ ہو گیا",
    ccPaymentProofTemplateSaveFailed: "ادائیگی کے ثبوت کا ٹیمپلیٹ محفوظ کرنے میں ناکامی",
    ccPaymentProofTemplateLoadFailed: "ادائیگی کے ثبوت کا ٹیمپلیٹ لوڈ کرنے میں ناکامی",
    ccPaymentProofCertificateDetails: "سرٹیفکیٹ کی تفصیلات",
    ccPaymentProofLogoUrl: "لوگو URL",
    ccPaymentProofLogoUpload: "تنظیم کا لوگو",
    ccPaymentProofLogoChooseFile: "لوگو اپ لوڈ کریں",
    ccPaymentProofLogoEmpty: "کوئی لوگو اپ لوڈ نہیں",
    ccPaymentProofLogoHint: "PNG، JPEG، GIF، یا WebP، زیادہ سے زیادہ 5 MB۔ کلیئرنس سرٹیفکیٹ کے ہیڈر میں دکھایا جائے گا۔",
    ccPaymentProofLogoUploaded: "لوگو اپ لوڈ ہو گیا",
    ccPaymentProofLogoUploadFailed: "لوگو اپ لوڈ ناکام",
    ccPaymentProofAssetsSection: "سرٹیفکیٹ کی تصاویر",
    ccPaymentProofImageTooLarge: "تصویر 5 MB سے بڑی ہے",
    ccUploading: "اپ لوڈ ہو رہا ہے...",
    ccPaymentProofPrimaryColor: "بنیادی رنگ",
    ccPaymentProofPrimaryColorInvalid: "بنیادی رنگ درست hex ویلیو ہونا چاہیے (مثال: #E87722)",
    ccPaymentProofCompanyNameEn: "کمپنی کا نام (انگریزی)",
    ccPaymentProofCompanyNameAr: "کمپنی کا نام (عربی)",
    ccPaymentProofTitleEn: "سرٹیفکیٹ عنوان (انگریزی)",
    ccPaymentProofTitleAr: "سرٹیفکیٹ عنوان (عربی)",
    ccPaymentProofBodyEn: "سرٹیفکیٹ متن (انگریزی)",
    ccPaymentProofBodyAr: "سرٹیفکیٹ متن (عربی)",
    ccPaymentProofTelecomPartner: "ٹیلی کام پارٹنر کا نام",
    ccPaymentProofStampUpload: "مہر کی تصویر",
    ccPaymentProofStampChooseFile: "مہر اپ لوڈ کریں",
    ccPaymentProofStampEmpty: "کوئی مہر اپ لوڈ نہیں",
    ccPaymentProofStampHint: "PNG، JPEG، GIF، یا WebP، زیادہ سے زیادہ 5 MB۔ سرٹیفکیٹ کے فوٹر میں دکھایا جائے گا۔",
    ccPaymentProofStampUploaded: "مہر اپ لوڈ ہو گئی",
    ccPaymentProofStampUploadFailed: "مہر اپ لوڈ ناکام",
    ccPaymentProofSignatureUpload: "دستخط کی تصویر",
    ccPaymentProofSignatureChooseFile: "دستخط اپ لوڈ کریں",
    ccPaymentProofSignatureEmpty: "کوئی دستخط اپ لوڈ نہیں",
    ccPaymentProofSignatureHint: "PNG، JPEG، GIF، یا WebP، زیادہ سے زیادہ 5 MB۔ سرٹیفکیٹ کے فوٹر میں دکھایا جائے گا۔",
    ccPaymentProofSignatureUploaded: "دستخط اپ لوڈ ہو گیا",
    ccPaymentProofSignatureUploadFailed: "دستخط اپ لوڈ ناکام",
    ccPaymentProofWhatsAppTemplate: "ڈیفالٹ واٹس ایپ پیغام ٹیمپلیٹ",
    ccPaymentProofFieldMappings: "فیلڈ میپنگ",
    ccPaymentProofMappingNone: "کوئی نہیں",
    ccPaymentProofColumnsLoadFailed: "ٹکٹ کالم لوڈ نہیں ہو سکے",
    ccPaymentProofFieldLabels: "فیلڈ لیبلز",
    ccPaymentProofTemplateAdmin: "ادائیگی کے ثبوت کا ٹیمپلیٹ",
    ccSave: "محفوظ کریں",

    ccInboxTitle: "کال سینٹر ان باکس",
    ccInboxSubtitle: "ٹریکنگ شروع کریں، بیرونی کالیں کریں، اور لائف سائیکل اسٹیٹس اپ ڈیٹ کریں",
    ccBrowserPhoneTitle: "براؤزر فون",
    ccBrowserPhoneSubtitle: "اپنا ایکسٹینشن رجسٹر کریں اور ویب ایپ کے اندر براہِ راست کال ہینڈل کریں۔",
    ccManualCall: "دستی کال",
    ccAiTriggered: "AI سے متحرک",
    ccPhoneOptionalHint: "اختیاری۔ اکاؤنٹ پر محفوظ موبائل نمبر استعمال کرنے کے لیے خالی چھوڑ دیں۔",
    ccInitiateCallNow: "ابھی کال شروع کریں",
    ccLiveStatus: "لائیو اسٹیٹس",
    ccCallId: "کال ID",
    ccNotStarted: "شروع نہیں ہوا",
    ccTrackingAccount: "ٹریکنگ اکاؤنٹ",
    ccNotSet: "سیٹ نہیں",
    ccStream: "اسٹریم",
    ccDuration: "مدت",
    ccCallType: "کال کی قسم",
    ccAccountNoUpper: "اکاؤنٹ نمبر",
    ccDialed: "ڈائل کیا",
    ccCallTicket: "کال ٹکٹ",
    ccViewTicketInList: "فہرست میں ٹکٹ دیکھیں",
    ccListenRecording: "ریکارڈنگ سنیں",
    ccRecordingProcessing: "ریکارڈنگ پروسیس ہو رہی ہے…",
    ccRecordingPlaybackFailed: "پلے بیک ناکام۔ ٹکٹ ریفریش کریں یا تھوڑی دیر بعد دوبارہ کوشش کریں۔",
    ccCallHistory: "کال کی تاریخ",
    ccNoCallsFound: "کوئی کال نہیں ملی",
    ccUnknownAccount: "نامعلوم اکاؤنٹ",
    ccRecordingPending: "ریکارڈنگ زیر التواء — کال مکمل ہونے کے بعد دستیاب ہوگی۔",
    ccManualStatusPanelHint: "سسٹم میں کال شروع ہو گئی۔ براہ کرم دستی طور پر کال کریں اور اسٹیٹس اپ ڈیٹ کریں۔",
    ccMarkRinging: "رنگنگ نشان زد کریں",
    ccMarkAnswered: "جواب دیا نشان زد کریں",
    ccMarkFailed: "ناکام نشان زد کریں",
    ccCompleteCallNotes: "کال مکمل کریں (اختیاری نوٹس)",
    ccSummaryOptional: "خلاصہ (اختیاری)",
    ccRecordingUrlOptional: "ریکارڈنگ URL (اختیاری)",
    ccMarkCompleted: "مکمل نشان زد کریں",
    ccEventHistory: "ایونٹ کی تاریخ",
    ccTimelineInitial: "ابتدائی",
    ccTimelineRealtime: "حقیقی وقت",
    ccStreamConnecting: "منسلک ہو رہا ہے…",
    ccStreamConnected: "منسلک",
    ccStreamReconnecting: "دوبارہ منسلک ہو رہا ہے…",
    ccStreamClosed: "بند",
    ccStreamDisconnected: "منقطع",
    ccCallTypeAiAutomated: "AI خودکار",
    ccCallTypeManual: "دستی",
    ccCallTypeOutbound: "آؤٹ باؤنڈ",
    ccToastUnableInitiateCall: "کال شروع کرنے میں ناکام",
    ccToastInitiateCallFirst: "اکاؤنٹ حل کرنے سے پہلے اسٹیٹس اپ ڈیٹ کرنے کے لیے پہلے کال شروع کریں۔",
    ccToastCallStatusUpdated: "کال اسٹیٹس اپ ڈیٹ ہوا:",
    ccToastLinkedTicketNotFound: "منسلک ٹکٹ نہیں ملا۔ ہو سکتا ہے حذف کر دیا گیا ہو۔",
    ccToastCallStatusUpdateFailed: "اسٹیٹس اپ ڈیٹ کرنے میں ناکام:",
    ccTicketsTitle: "سپورٹ ٹکٹس",
    ccTicketsSubtitle: "{org} ورک اسپیس میں ریکوری ٹکٹس کا انتظام اور ٹریک کریں۔",
    ccAddNewTicket: "نیا ٹکٹ شامل کریں",
    ccNewTicket: "نیا ٹکٹ",
    ccOutstandingRemarks: "بقایا نوٹس",
    ccShowingTicketsCount: "{total} میں سے {from}-{to} ٹکٹس دکھا رہے ہیں",
    ccTicketFilters: "ٹکٹ فلٹرز",
    ccShowingPage: "صفحہ دکھا رہا ہے",
    ccOf: "کا",
    ccTotalTicket: "کل ٹکٹ",
    ccTotalTickets: "کل ٹکٹس",
    ccHideAdvancedFilters: "اعلیٰ فلٹرز چھپائیں",
    ccShowAdvancedFilters: "اعلیٰ فلٹرز دکھائیں",
    ccSearchAccountPlaceholder: "اکاؤنٹ نمبر تلاش کریں...",
    ccGlobalSearchPlaceholder: "ٹکٹس، ایجنٹس، قرضدار تلاش کریں...",
    ccCalled: "کال کی گئی",
    ccCalledOnly: "صرف کال شدہ",
    ccNotCalled: "کال نہیں کی گئی",
    ccAssignee: "تفویض کردہ",
    ccUnassigned: "غیر تفویض",
    ccAssignTicket: "ٹکٹ تفویض کریں",
    ccReassignTicket: "ٹکٹ دوبارہ تفویض کریں",
    ccAssignSelectedTickets: "منتخب تفویض کریں",
    ccTicketAssignModalTitle: "ٹکٹ تفویض کریں",
    ccTicketReassignModalTitle: "ٹکٹ دوبارہ تفویض کریں",
    ccTicketBulkAssignModalTitle: "منتخب ٹکٹس تفویض کریں",
    ccTicketAssignModalDescription: "اس ٹکٹ کے لیے call agent منتخب کریں۔",
    ccTicketBulkAssignModalDescription: "تمام منتخب ٹکٹس کے لیے call agent منتخب کریں۔",
    ccTicketAssignSelectAgent: "Call agent",
    ccTicketAssignSelectAgentPlaceholder: "ایجنٹ منتخب کریں",
    ccTicketAssignConfirm: "تفویض محفوظ کریں",
    ccTicketUnassign: "تفویض ہٹائیں",
    ccTicketAssignSuccess: "ٹکٹ تفویض اپ ڈیٹ ہو گئی۔",
    ccTicketUnassignSuccess: "ٹکٹ کی تفویض ہٹا دی گئی۔",
    ccTicketAssignFailed: "ٹکٹ تفویض اپ ڈیٹ نہیں ہو سکی۔",
    ccTicketUnassignFailed: "ٹکٹ کی تفویض نہیں ہٹ سکی۔",
    ccTicketsBulkAssignSuccess: "منتخب ٹکٹس تفویض ہو گئیں۔",
    ccTicketAssignNoAgents: "اس تنظیم کے لیے کوئی eligible call agent نہیں ملا۔",
    ccLoadingUsers: "ایجنٹس لوڈ ہو رہے ہیں…",
    ccAllAssignees: "تمام تفویض شدہ",
    ccScopedToCurrentAgent: "موجودہ ایجنٹ تک محدود",
    ccSortBy: "ترتیب دیں",
    ccSortOutstandingAsc: "بقایا: کم سے زیادہ",
    ccSortOutstandingDesc: "بقایا: زیادہ سے کم",
    ccSortPaymentDateAsc: "ادائیگی کی تاریخ: پرانی پہلے",
    ccSortPaymentDateDesc: "ادائیگی کی تاریخ: نئی پہلے",
    ccSortCalledAtAsc: "کال کا وقت: پرانا پہلے",
    ccSortCalledAtDesc: "کال کا وقت: نیا پہلے",
    ccSortRecentlyUpdated: "حال ہی میں اپ ڈیٹ",
    ccStatusIds: "اسٹیٹس IDs",
    ccStatusFiltersSelected: "اسٹیٹس فلٹر(ز) منتخب",
    ccMultiSelectTip: "ٹپ: کثیر انتخاب کے لیے Ctrl/Cmd دبائے رکھیں",
    ccMinOutstanding: "کم از کم بقایا",
    ccMaxOutstanding: "زیادہ سے زیادہ بقایا",
    ccPaymentDateFrom: "ادائیگی کی تاریخ سے",
    ccPaymentDateTo: "ادائیگی کی تاریخ تک",
    ccApplyFilters: "فلٹرز لگائیں",
    ccDeleteSelected: "منتخب حذف کریں",
    ccNoTicketsFound: "کوئی ٹکٹ نہیں ملا",
    ccNoTicketsHint: "اپنے فلٹرز ایڈجسٹ کرنے کی کوشش کریں۔",
    ccNoTicketsEmpty: "ابھی کوئی ٹکٹ نہیں۔ ٹکٹ انٹری سے بنائیں یا Excel فائل اپ لوڈ کریں۔",
    ccNoTicketsFiltered: "موجودہ فلٹرز سے کوئی ٹکٹ نہیں ملا۔",
    ccConfirmBulkDeleteTickets: "{count} منتخب ٹکٹ(s) حذف کریں؟ اسے واپس نہیں کیا جا سکتا۔",
    ccTicketsListLoadFailed: "ٹکٹس لوڈ کرنے میں ناکامی",
    ccSelectAllTickets: "تمام ٹکٹس منتخب کریں",
    ccSelectTicket: "ٹکٹ منتخب کریں",
    ccPage: "صفحہ",
    ccResults: "نتائج",
    ccTicket: "ٹکٹ",
    ccTicketId: "ٹکٹ ID",
    ccTicketOverview: "ٹکٹ کا جائزہ",
    ccTargetOutstanding: "ہدف بقایا",
    ccPaymentDate: "ادائیگی کی تاریخ",
    ccUpdateTicket: "ٹکٹ اپ ڈیٹ کریں",
    ccTargetPaymentDate: "ہدف ادائیگی کی تاریخ",
    ccCalledAt: "کال کا وقت",
    ccAdditionalComments: "اضافی تبصرے",
    ccAddRemarkNote: "ریمارک نوٹ شامل کریں",
    ccAddAdditionalComments: "اضافی تبصرے شامل کریں",
    ccSaveStatusUpdate: "اسٹیٹس اپ ڈیٹ محفوظ کریں",
    ccRefreshLiveOutstanding: "لائیو بقایا تازہ کریں",
    ccToastTicketStatusUpdated: "ٹکٹ اسٹیٹس اپ ڈیٹ ہو گیا",
    ccToastTicketNotFound: "ٹکٹ نہیں ملا۔ ہو سکتا ہے حذف کر دیا گیا ہو۔",
    ccToastTicketStatusUpdateFailed: "ٹکٹ اسٹیٹس اپ ڈیٹ کرنے میں ناکام",
    ccToastOutstandingRefreshedPrefix: "بقایا تازہ ہوا۔ پچھلا:",
    ccToastOutstandingRefreshedLive: "لائیو:",
    ccToastOutstandingRefreshedAt: "وقت:",
    ccToastRefreshOutstandingFailed: "بقایا تازہ کرنے میں ناکام",
    ccToastSelectTicketToDelete: "حذف کرنے کے لیے کم از کم ایک ٹکٹ منتخب کریں۔",
    ccToastDeleteProcessedPrefix: "حذف پروسیس ہوا۔ حذف شدہ:",
    ccToastDeleteAlreadyDeleted: "پہلے سے حذف:",
    ccToastDeleteNotFound: "نہیں ملا:",
    ccToastBulkDeleteFailed: "بلک میں ٹکٹس حذف کرنے میں ناکام",
    ccToastLoadTicketsFailed: "ٹکٹس لوڈ کرنے میں ناکام",
    ccToastLoadStatusOptionsFailed: "اسٹیٹس کے اختیارات لوڈ کرنے میں ناکام",
    ccToastLoadAssigneesFailed: "تفویض کی فہرست لوڈ کرنے میں ناکام",
    ccAgentWorkspaceTitle: "ٹکٹ انٹری",
    ccAgentWorkspaceSubtitle: "ترتیب شدہ فیلڈز کے ساتھ دستی طور پر ایک ٹکٹ بنائیں۔",
    ccTicketBulkUploadTitle: "بلک ٹکٹ اپ لوڈ",
    ccTicketBulkUploadSubtitle: "Excel یا CSV سے ٹکٹس اپ لوڈ کریں اور ماضی کے نتائج دیکھیں۔",
    ccUploadHistoryResultsLabel: "اپ لوڈز",
    ccUploadHistoryFile: "فائل",
    ccUploadHistoryDate: "اپ لوڈ کی تاریخ",
    ccUploadHistoryEmptyTitle: "ابھی کوئی اپ لوڈ نہیں",
    ccUploadHistoryNoRowDetails: "اس اپ لوڈ کے لیے قطار کی تفصیلات دستیاب نہیں۔",
    ccBulkUploadFormHint: "اسپریڈشیٹ اپ لوڈ کریں، کالم میپ کریں، پھر قطاروں کو ٹکٹس میں تبدیل کریں۔",
    ccBulkUploadStepFile: "فائل منتخب کریں",
    ccBulkUploadStepMapping: "کالم میپنگ",
    ccBulkUploadStepProcess: "اپ لوڈ پروسیس کریں",
    ccBulkUploadParsing: "فائل ہیڈرز پڑھے جا رہے ہیں…",
    ccBulkUploadValidationFailed: "توثیق ناکام",
    ccBulkUploadValidationPending: "میپنگ جاری ہے",
    ccBulkUploadFooterHint: "فعال payment plan والے قطار نظرانداز ہوتے ہیں، ناکام نہیں۔",
    ccBulkUploadClearFile: "فائل ہٹائیں",
    ccBulkUploadClearConfirm: "منتخب فائل ہٹائیں اور کالم میپنگ ری سیٹ کریں؟",
    ccBulkUploadSubmitConfirm: "یہ فائل پروسیس کریں اور تمام درست قطاروں کے لیے ٹکٹس بنائیں؟",
    ccBulkUploadSuccessTitle: "اپ لوڈ مکمل",
    ccBulkUploadRequiredColumns: "لازمی کالم",
    ccUploadHistoryPageTitle: "اپ لوڈ ہسٹری",
    ccUploadHistoryPageSubtitle: "ماضی کی Excel اپ لوڈز، قطار کے نتائج، اور ناکامی کی وجوہات دیکھیں۔",
    ccUploadHistoryRecentTitle: "حالیہ اپ لوڈز",
    ccUploadHistoryViewAll: "مکمل ہسٹری دیکھیں",
    ccUploadHistoryStatUploads: "کل اپ لوڈز",
    ccUploadHistorySuccessRate: "کامیابی کی شرح",
    ccUploadHistorySearchPlaceholder: "فائل نام سے تلاش…",
    ccUploadHistoryOutcomeFilter: "اپ لوڈ نتیجہ",
    ccUploadHistoryOutcome_ALL: "تمام اپ لوڈز",
    ccUploadHistoryOutcome_HAS_FAILURES: "ناکامیوں والے",
    ccUploadHistoryOutcome_HAS_IGNORED: "نظرانداز قطاروں والے",
    ccUploadHistoryOutcome_CLEAN: "مکمل کامیاب",
    ccUploadHistoryEmptyFiltered: "موجودہ فلٹرز سے کوئی اپ لوڈ نہیں ملا۔",
    ccUploadHistoryLoadFailed: "اپ لوڈ ہسٹری لوڈ کرنے میں ناکامی",
    ccUploadHistoryDetailTitle: "اپ لوڈ کی تفصیل",
    ccUploadHistoryTab_ALL: "تمام قطاریں",
    ccUploadHistoryTab_CREATED: "بنائے گئے",
    ccUploadHistoryTab_IGNORED: "نظرانداز",
    ccUploadHistoryTab_FAILED: "ناکام",
    ccUploadHistoryTab_REASSIGNED: "Reassigned",
    ccUploadHistoryTab_UNCHANGED: "Unchanged",
    ccUploadRowReassigned: "Reassigned",
    ccUploadRowUnchanged: "Unchanged",
    ccBulkUploadReassignExisting: "Reassign existing open tickets when account already exists",
    ccBulkUploadReassignExistingHint:
      "Map an agent column in the file. Matching accounts update assignee instead of creating duplicate tickets.",
    ccBulkUploadReassignRequiresAgent:
      "Enable agent column mapping when reassigning existing tickets from Excel upload.",
    ccTicketAssignmentHistoryTitle: "Assignment history",
    ccTicketAssignmentHistoryEmpty: "No assignment changes recorded yet.",
    ccTicketAssignmentHistoryLoadFailed: "Failed to load assignment history",
    ccTicketAssignmentEventAssigned: "Assigned to {assignee}",
    ccTicketAssignmentEventUnassigned: "Unassigned from {previous}",
    ccTicketAssignmentEventReassigned: "Reassigned from {previous} to {next}",
    ccTicketAssignmentSourceExcelUpload: "Excel upload",
    ccTicketAssignmentSourceExcelReassign: "Excel reassign",
    ccTicketAssignmentSourceManualAssign: "Manual assign",
    ccTicketAssignmentSourceManualUnassign: "Manual unassign",
    ccTicketAssignmentSourceBulkList: "Bulk list",
    ccTicketAssignmentSourceBulkApi: "Bulk API",
    ccTicketAssignmentSourceTicketCreate: "Ticket create",
    ccTicketAssignmentSourceUnknown: "System",
    ccTicketColumnConfigPageTitle: "ٹکٹ کالم سیٹنگز",
    ccTicketColumnConfigPageSubtitle: "لازمی ٹکٹ کالمز اور ان کے ظاہر ہونے کی جگہیں منظم کریں۔",
    ccManualTicketEntry: "دستی ٹکٹ انٹری",
    ccTicketEntryFormHint: "نیچے مطلوبہ فیلڈز بھریں۔ * والے فیلڈز لازمی ہیں۔",
    ccTicketEntrySectionAccount: "اکاؤنٹ کی تفصیل",
    ccTicketEntrySectionAccountHint: "فعال ٹکٹس کے لیے اکاؤنٹ نمبر منفرد ہونا چاہیے۔",
    ccTicketEntrySectionContact: "رابطہ نمبر",
    ccTicketEntrySectionContactHint: "رابطے کے لیے ایک یا زیادہ موبائل نمبر شامل کریں۔",
    ccTicketEntrySectionAssignment: "تفویض اور ترجیح",
    ccTicketEntrySectionPayment: "ادائیگی کی تفصیل",
    ccTicketEntrySectionNotes: "ریمارکس",
    ccTicketEntrySectionNotesHint: "تفاعل کے نوٹس ٹکٹ ٹائم لائن پر دکھائی دیتے ہیں۔",
    ccTicketEntrySectionAdditional: "اضافی فیلڈز",
    ccTicketEntryQuickLinks: "فوری لنکس",
    ccTicketEntryRequiredFields: "لازمی فیلڈز",
    ccTicketEntryRequiredHint: "لازمی فیلڈز ستارے سے نشان زد ہیں۔",
    ccTicketEntryViewTickets: "تمام ٹکٹس دیکھیں",
    ccTicketEntryClearConfirm: "غیر محفوظ تبدیلیاں مسترد کریں اور فارم صاف کریں؟",
    ccTicketEntryAccountPlanTitle: "Payment plans",
    ccTicketEntryAccountPlanNote: "فعال payment plan والے accounts کے لیے manual ticket نہیں بن سکتا۔ Payment Plans میں manage کریں۔",
    ccAssignedTo: "تفویض شدہ",
    ccLoadingUsers: "صارفین لوڈ ہو رہے ہیں...",
    ccSelectUserOptional: "صارف منتخب کریں (اختیاری)",
    ccRemarksInteractionNotes: "ریمارکس / تعامل کے نوٹس",
    ccCallCommentThread: "کال کی تاریخ اور تبصرے",
    ccNoCallsOnTicket: "اس ٹکٹ پر ابھی تک کوئی کال ریکارڈ نہیں ہوئی۔",
    ccNoCommentsForCall: "اس کال کے لیے ابھی تک کوئی تبصرہ نہیں۔",
    ccAddCallComment: "تبصرہ شامل کریں",
    ccCallCommentPlaceholder: "اس کال کے بارے میں نوٹ لکھیں...",
    ccCallDirectionInbound: "ان باؤنڈ",
    ccCallDirectionOutbound: "آؤٹ باؤنڈ",
    ccCallAgent: "ایجنٹ",
    ccCallAt: "کال کا وقت",
    ccSavingComment: "محفوظ ہو رہا ہے...",
    ccCommentAdded: "تبصرہ شامل ہو گیا",
    ccCommentAddFailed: "تبصرہ شامل کرنے میں ناکامی",
    ccCallThreadLoadFailed: "کال کی تاریخ لوڈ کرنے میں ناکامی",
    ccAiGenerated: "AI سے تیار شدہ",
    ccCreateTicket: "ٹکٹ بنائیں",
    ccClear: "صاف کریں",
    ccAccountNoExample: "مثلاً 112015128",
    ccRemarksPlaceholder: "واضح تعامل نوٹس شامل کریں...",
    ccBulkImport: "بلک امپورٹ",
    ccBulkImportRequiredColumns: "CSV یا Excel کے ذریعے متعدد ٹکٹس اپ لوڈ کریں۔ درکار کالم:",
    ccBulkImportPriorityNote: "اپ لوڈ کی ہر قطار پر لاگو ہوتا ہے۔",
    ccBulkImportAgentNameNote: "Agent Name کالم آپ کی تنظیم کے موجودہ کال ایجنٹ سے بالکل مماثل ہونا چاہیے۔",
    ccBulkImportOptional: "اختیاری:",
    ccBulkImportDynamicHint: "آپ کی فائل کے تمام کالم خودکار طور پر capture ہو جاتے ہیں۔ ذیل میں لازمی اور visible فیلڈز ترتیب دیں۔",
    ccBulkImportMultiMobileNote: "ایک سے زیادہ موبائل نمبروں کے لیے، کئی کالم Mobile Number سے map کریں یا ایک سیل میں comma سے الگ نمبر لکھیں۔ ملک کے کوڈ کے بغیر مقامی نمبرز ڈیفالٹ عمان (+968) سمجھے جائیں گے۔",
    ccAddMobileNumber: "موبائل نمبر شامل کریں",
    ccRemoveMobileNumber: "موبائل نمبر ہٹائیں",
    ccMobileNumberPlaceholder: "موبائل نمبر درج کریں",
    ccMobileNumberHint: "ملک کا کوڈ ڈیفالٹ عمان (+968) ہے۔ دوسرے ممالک کے لیے جھنڈا بدلیں۔ موجودہ مقامی نمبرز Omani سمجھے جائیں گے۔",
    ccMobileNumberInvalid: "ملک کے کوڈ کے ساتھ درست موبائل نمبر درج کریں۔",
    ccMobileNumberInvalidOman: "Omani موبائل نمبر +968 کے بعد 7–8 ہندسے ہونے چاہئیں۔",
    ccMobileNumberDuplicate: "یہ موبائل نمبر پہلے سے موجود ہے۔",
    ccMobileNumberMax: "زیادہ سے زیادہ {max} موبائل نمبرز۔",
    ccMobileNumberRequired: "کم از کم ایک موبائل نمبر درکار ہے",
    ccBulkPriority: "بلک ترجیح",
    ccUploadHistoryTitle: "اپ لوڈ تاریخ",
    ccUploadHistoryEmpty: "ابھی تک کوئی Excel اپ لوڈ نہیں۔",
    ccUploadSuccessCount: "بنائے گئے",
    ccUploadIgnoredCount: "نظر انداز",
    ccUploadFailedCount: "ناکام",
    ccUploadTotalRows: "کل قطاریں",
    ccUploadViewDetails: "تفصیل دیکھیں",
    ccUploadHideDetails: "تفصیل چھپائیں",
    ccUploadRowCreated: "بنایا گیا",
    ccUploadRowIgnored: "نظر انداز",
    ccUploadRowFailed: "ناکام",
    ccUploadSummaryToast: "اپ لوڈ مکمل: {success} بنائے گئے، {ignored} نظر انداز، {failed} ناکام۔",
    ccToastAccountPlanTicketBlocked:
      "اس اکاؤنٹ کے لیے دستی ٹکٹ نہیں بنایا جا سکتا کیونکہ اس کا فعال ادائیگی پلان موجود ہے",
    ccDropFileOrBrowse: "فائل یہاں چھوڑیں یا براؤز کرنے کے لیے کلک کریں",
    ccFileTypesCsvXls: "CSV, XLS, XLSX",
    ccValidationResult: "توثیق کا نتیجہ",
    ccMissingHeaders: "غائب:",
    ccHeadersValidated: "ہیڈرز کامیابی سے توثیق ہوئے",
    ccFoundHeaders: "ملے:",
    ccProcessBulkUpload: "بلک اپ لوڈ پروسیس کریں",
    ccAgentTipTitle: "ایجنٹ ٹپ",
    ccColumnConfigTitle: "ٹکٹ کالم کنفیگریشن",
    ccColumnConfigSubtitle: "capture شدہ کالموں کو لازمی بنائیں اور ظاہر ہونے ki جگہ منتخب کریں۔",
    ccSaveColumnConfig: "کالم سیٹنگز محفوظ کریں",
    ccSaving: "محفوظ ہو رہا ہے...",
    ccColumnLabel: "کالم",
    ccColumnType: "قسم",
    ccMandatory: "لازمی",
    ccShowInList: "فہرست",
    ccShowInForm: "فارم",
    ccShowInUpdate: "اپ ڈیٹ",
    ccShowInFilter: "فلٹر",
    ccPredefinedColumn: "Predefined",
    ccCustomColumn: "حسب ضرورت",
    ccInvalidNumber: "درست نمبر درج کریں",
    ccToastLoadColumnConfigFailed: "ٹکٹ کالم کنفیگریشن لوڈ کرنے میں ناکام",
    ccToastColumnConfigSaved: "کالم کنفیگریشن محفوظ ہو گئی",
    ccToastColumnConfigSaveFailed: "کالم کنفیگریشن محفوظ کرنے میں ناکام",
    ccToastTicketCreated: "ٹکٹ کامیابی سے بن گیا",
    ccToastCreateTicketFailed: "ٹکٹ بنانے میں ناکام",
    ccToastInvalidFileType: "غلط فائل کی قسم۔ براہ کرم CSV/XLS/XLSX اپ لوڈ کریں",
    ccToastParseFileFailed: "فائل کی ساخت پارس کرنے میں ناکام",
    ccToastNoFileSelected: "کوئی فائل منتخب نہیں",
    ccToastSelectBulkPriority: "براہ کرم بلک ترجیح منتخب کریں",
    ccToastHeaderValidationFailed: "ہیڈر توثیق ناکام",
    ccToastBulkUploadProcessed: "بلک اپ لوڈ پروسیس ہوا",
    ccToastBulkUploadFailed: "بلک اپ لوڈ ناکام",
    ccToastLoadOfficeUsersFailed: "آفس کے صارفین لوڈ کرنے میں ناکام",
    ccCampaignManagerTitle: "مہم منیجر",
    ccCampaignManagerSubtitle: "آؤٹ باؤنڈ کال کے بہاؤ کو خودکار اور مانیٹر کریں",
    ccCreateNewCampaign: "نئی مہم بنائیں",
    ccCampaignName: "مہم کا نام",
    ccCampaignNamePlaceholder: "مثال: Q4 قرض کی وصولی",
    ccCampaignNameRequired: "مہم کا نام درکار ہے",
    ccExecutionScheduleCron: "عملدرآمد کا شیڈول (Cron)",
    ccCallTypeRequired: "کال کی قسم درکار ہے",
    ccScheduleCronRequired: "شیڈول cron درکار ہے",
    ccManualAgent: "دستی ایجنٹ",
    ccMaxRetries: "زیادہ سے زیادہ دوبارہ کوشش",
    ccDelayMin: "تاخیر (منٹ)",
    ccTriggerConditions: "ٹرگر کی شرائط",
    ccAddRule: "+ قاعدہ شامل کریں",
    ccFieldMode: "فیلڈ",
    ccExprMode: "EXPR",
    ccExpressionPlaceholder: "مثال: balance > 500",
    ccFieldName: "فیلڈ کا نام",
    ccValue: "قدر",
    ccSetAsActive: "فعال کے طور پر سیٹ کریں",
    ccCreateCampaign: "مہم بنائیں",
    ccExistingCampaigns: "موجودہ مہمات",
    ccLogicPreview: "منطق کا پیش نظارہ",
    ccToastCampaignLaunched: "مہم کامیابی سے شروع ہو گئی",
    ccToastCampaignCreateFailed: "مہم بنانے میں ناکام",
    ccToastLoadCampaignsFailed: "مہمات کی فہرست لوڈ کرنے میں ناکام",
    ccToastCampaignUpdated: "مہم کامیابی سے اپڈیٹ ہو گئی",
    ccToastCampaignDeleted: "مہم کامیابی سے حذف ہو گئی",
    ccToastCampaignDeleteFailed: "مہم حذف کرنے میں ناکام",
    ccCampaignTasksLoadFailed: "مہم کے ٹاسks لوڈ کرنے میں ناکام",
    ccCampaignFormSubtitle: "شیڈول، ٹargeting rules، اور retry policy ترتیب دیں۔",
    ccScheduleCronHint: "6-field Spring cron expression استعمال کریں، مثلاً 0 */10 * * * *",
    ccCampaignManualTypeHint: "دستی مہمات محفوظ ہوتی ہیں مگر خودکار ڈائل نہیں ہوتیں۔ شیڈuled calls کے لیے AI automated استعمال کریں۔",
    ccConditionLogic: "شرط کی منطق",
    ccTicketLimit: "ہر run میں ٹکٹ کی حد",
    ccTicketLimitPlaceholder: "اختیاری",
    ccRemove: "ہٹائیں",
    ccSaveChanges: "تبدیلیاں محفوظ کریں",
    ccAnsweredRate: "جواب کی شرح",
    ccSearch: "تلاش",
    ccCampaignSearchPlaceholder: "مہم کا نام تلاش کریں...",
    ccCampaignStatusActive: "فعال",
    ccCampaignStatusInactive: "غیر فعال",
    ccCampaignsEmptyTitle: "ابھی تک کوئی مہم نہیں",
    ccCampaignsEmptyHint: "شیڈuled outbound recovery calls کے لیے AI automated مہم بنائیں۔",
    ccCampaignResultsLabel: "مہمات",
    ccCampaignDeleteConfirm: "مہم \"{name}\" حذف کریں؟ اس مہم کے queued tasks بھی ہٹا دیے جائیں گے۔",
    ccCampaignDetailSubtitle: "شیڈول، حیثیت، اور حالیہ dial tasks",
    ccCampaignRecentTasks: "حالیہ dial tasks",
    ccCampaignTasksEmpty: "اس مہم کے لیے ابھی تک کوئی dial task queue میں نہیں۔",
    ccCampaignTaskAiOutcome: "AI نتیجہ",
    ccCallTaskStatus_QUEUED: "Queue میں",
    ccCallTaskStatus_PROCESSING: "پروسیسنگ",
    ccCallTaskStatus_DIALING: "ڈائل ہو رہا ہے",
    ccCallTaskStatus_AWAITING_COMPLETION: "مکمل ہونے کا انتظار",
    ccCallTaskStatus_COMPLETED: "مکمل",
    ccCallTaskStatus_FAILED: "ناکام",
    ccCallTaskStatus_RETRY_WAIT: "دوبارہ کوشش شیڈول",
    ccAiCallIntent_PAID: "ادا شدہ",
    ccAiCallIntent_WILL_PAY: "ادا کرے گا",
    ccAiCallIntent_DISPUTE: "تنازع",
    ccAiCallIntent_CALLBACK_REQUIRED: "Callback درکار",
    ccAiCallIntent_NO_ANSWER: "جواب نہیں",
    ccAiCallIntent_UNKNOWN: "نامعلوم",
    ccEditCampaign: "مہم میں ترمیم",
    ccViewDetails: "تفصیل دیکھیں",
    ccUpdatedAt: "اپڈیٹ کا وقت",
    ccPhone: "فون",
    ccCampaignPromoTitle: "مہم شروع کریں",
    ccCampaignPromoSubtitle: "آؤٹ باؤنڈ recovery run شروع کریں اور live track کریں۔",
    ccCampaignPromoAction: "نئی مہم",
    ccCampaignPaymentReminderSubtitle:
      "غیر تفویض شدہ ٹکٹس پر AI کالز شیڈول کریں۔ کال کے نتائج سے ٹکٹس خودکار اپڈیٹ ہوتی ہیں۔",
    ccCampaignPaymentReminderPageSubtitle:
      "غیر تفویض شدہ ٹکٹس کے لیے AI payment reminder calls — نتائج سے ٹکٹ کی حیثیت خودکار اپڈیٹ ہوتی ہے۔",
    ccCampaignPaymentReminderPageIntro:
      "ہر run آپ کے rules سے میل کھانے والے open، unassigned tickets ڈھونڈتا ہے، AI reminder calls کرتا ہے، پھر customer response پر tickets اپڈیٹ کرتا ہے۔",
    ccCampaignFlowTitle: "Payment reminder campaigns کیسے کام کرتے ہیں",
    ccCampaignFlowHint: "Campaigns ہمیشہ AI automated calling استعمال کرتے ہیں۔",
    ccCampaignFlowStep1: "بقایا balance والے unassigned tickets تلاش کریں جو trigger rules سے match ہوں۔",
    ccCampaignFlowStep2: "configured schedule اور dial window میں AI reminder calls کریں۔",
    ccCampaignFlowStep3: "AI outcome سے ہر ticket اپڈیٹ کریں (status، promise، summary، recording)۔",
    ccCampaignUnassignedHint: "صرف unassigned tickets شامل ہوتے ہیں — یہ خودکار enforce ہوتا ہے۔",
    ccCampaignResetDefaults: "Payment reminder defaults ری سیٹ کریں",
    ccCampaignTypeLabel: "Campaign type",
    ccCampaignTypeAiPaymentReminder: "AI payment reminder",
    ccCampaignDetailPaymentReminderSubtitle: "AI dial tasks اور customer responses سے automatic ticket updates",
    ccCampaignOutcomeTitle: "AI outcomes سے ticket updates",
    ccCampaignOutcomeHint:
      "Paid → PAID · Will pay / No answer → Follow up · Callback → Callback required · Dispute → Failed · Promise date/amount save ہوتی ہے جب ملے۔",
    ccCampaignKpiAiScopeHint: "نیچے کے اعداد صرف AI automated payment-reminder calls کے ہیں — manual agent calls شامل نہیں۔",
    ccCampaignKpiAiCalls: "AI calls placed",
    ccCampaignKpiAiAnsweredRate: "AI answered rate",
    ccCampaignKpiAiSuccessRate: "Paid after AI call",
    ccCampaignKpiAiRecovery: "Recovery from AI calls",
    ccAnalyticsTitle: "ڈیش بورڈ",
    ccAnalyticsSubtitle: "ٹیم کے اہداف، مہم کی صحت، اور فوری رسائی ایک نظر میں۔",
    ccDashboardLastUpdated: "آخری اپ ڈیٹ {time} ({timezone})",
    ccRefreshAnalytics: "سنک",
    ccExportDashboard: "ایکسپورٹ",
    ccLast7Days: "آخری 7 دن",
    ccLast30Days: "آخری 30 دن",
    ccLast90Days: "آخری 90 دن",
    ccDashboardExported: "ڈیش بورڈ ایکسپورٹ ڈاؤن لوڈ ہو گئی",
    ccDashboardQuickActions: "فوری اقدامات",
    ccDashboardLastFetched: "آخری اپ ڈیٹ",
    ccDashboardNotFetchedYet: "ابھی لوڈ نہیں ہوا",
    ccDashboardOpen: "کھولیں",
    ccTeamProgress: "ٹیم کی پیشرفت",
    ccTeamProgressSubtitle: "رکن کے لحاظ سے ٹکٹ کے ہدف کی عملدرآمد",
    ccTotalTargets: "کل اہداف",
    ccCalledTargets: "کال شدہ اہداف",
    ccPaidTargets: "ادا شدہ اہداف",
    ccRecovery: "وصولی",
    ccCalledCoverage: "کال کوریج",
    ccPaidConversion: "ادائیگی کی تبدیلی",
    ccMember: "رکن",
    ccAssigned: "تفویض",
    ccCalledCol: "کال",
    ccPaidCol: "ادا",
    ccPending: "زیر التواء",
    ccNoTeamProgressData: "ٹیم کی پیشرفت کا ڈیٹا دستیاب نہیں۔",
    ccCampaignAnalytics: "مہم کے تجزیات",
    ccCampaignAnalyticsSubtitle: "لائیو مہم کی تھروپٹ اور نتیجے کا معیار",
    ccTotalCalls: "کل کالیں",
    ccAnswerRate: "جواب کی شرح",
    ccSuccessRate: "کامیابی کی شرح",
    ccCampaignPerformanceSnapshot: "مہم کی کارکردگی کا مختصر جائزہ",
    ccAnsweredPercent: "جواب دیا %",
    ccSuccessPercent: "کامیابی %",
    ccNoCampaignAnalyticsData: "مہم کے تجزیات کا ڈیٹا دستیاب نہیں۔",
    ccToastLoadAnalyticsFailed: "تجزیات لوڈ کرنے میں ناکام",
    ccTotalRecovery: "کل وصولی",
    ccPerfectTiming: "کال کرنے کا بہترین وقت",
    ccPerfectTimingSubtitle: "گاہک عام طور پر کب جواب دیتے ہیں، گھنٹے کے لحاظ سے",
    ccPerfectTimingHelp: "کالز گاہک کے جواب دینے کے گھنٹے (یا کال شروع ہونے کے وقت) کے حساب سے گروپ کی جاتی ہیں۔ جواب کی شرح = جواب دی گئی کالز ÷ اس گھنٹے میں کل کالز۔",
    ccPerfectTimingHeroTitle: "تجویز کردہ کال کا وقت",
    ccPerfectTimingHeroStats: "{rate}% جواب کی شرح · {answered} جوابات {total} کالز میں سے",
    ccPerfectTimingRecommended: "بھی تجویز کردہ",
    ccPerfectTimingRecommendedShort: "اچھا",
    ccPerfectTimingBest: "بہترین",
    ccPerfectTimingBestHour: "کال کرنے کا بہترین وقت: {hour}",
    ccPerfectTimingAnswerRate: "جواب کی شرح",
    ccPerfectTimingAnsweredCalls: "جواب دی گئی کالز",
    ccPerfectTimingPeriodTotalCalls: "مدت میں کل کالز",
    ccPerfectTimingOverallRate: "مجموعی جواب کی شرح",
    ccPerfectTimingByHour: "گھنٹے کے لحاظ سے تفصیل",
    ccPerfectTimingShowingHours: "{count} گھنٹے جن میں کال سرگرمی تھی",
    ccPerfectTimingChartHint: "بار کی اونچائی = اس گھنٹے کی جواب کی شرح",
    ccPerfectTimingTableTime: "وقت",
    ccPerfectTimingTableCalls: "کل کالز",
    ccPerfectTimingTableAnswered: "جواب دی گئی",
    ccPerfectTimingTableRate: "جواب کی شرح",
    ccPerfectTimingNoData: "ٹائمинг بصیرت کے لیے کافی کال ہسٹری نہیں",
    ccPerfectTimingSample: "منتخب مدت میں {count} کالز کی بنیاد پر",
    ccVsLastPeriodShort: "گزشتہ مدت کے مقابلے",
    ccDebtorSegments: "قرض دار طبقات",
    ccIndividuals: "افراد",
    ccSMEs: "چھوٹے کاروبار",
    ccCorporates: "کارپوریٹس",
    ccBestPerformingAgents: "بہترین ایجنٹس",
    ccAgentRatingHelp:
      "منتخب مدت کے لیے ستارہ ریٹنگ (0–5) میں شامل ہے: 35% کال کوریج (کال شدہ ÷ تفویض شدہ)، 35% ادا شدہ تبدیلی (ادا شدہ ÷ کال شدہ)، اور 30% ٹیم کے بہترین ایجنٹ کے مقابلے میں ریکوری۔ اس مدت میں بغیر سرگرمی والے ایجنٹس 0.0 دکھائیں گے۔",
    ccAgentRatingHelpLabel: "ایجنٹ ریٹنگ کیسے حساب ہوتی ہے",
    ccAgentTableShowing: "{total} میں سے {shown} ایجنٹس دکھائے جا رہے ہیں",
    ccAgentTableSearchPlaceholder: "ایجنٹس کو نام سے تلاش کریں...",
    ccAgentTableShowAll: "تمام ایجنٹس دکھائیں",
    ccAgentTableShowTop: "ٹاپ {count} دکھائیں",
    ccAgentTableMoreHidden: "{count} مزید ایجنٹس دکھائیں",
    ccAgentTableNoMatches: "آپ کی تلاش سے کوئی ایجنٹ نہیں ملا",
    ccAgent: "ایجنٹ",
    ccCalls: "کالز",
    ccRating: "ریٹنگ",
    ccShowDetails: "تفصیل دکھائیں",
    ccOnTrackCoverage: "80% کوریج ہدف کے مطابق",
    ccCallOutcomes: "کال نتائج",
    ccOutcomeAns: "جواب",
    ccOutcomeNoAns: "کوئی جواب نہیں",
    ccOutcomeBusy: "مصروف",
    ccOutcomeCallback: "کال بیک",
    ccOutcomePaid: "ادا شدہ",
    ccOutcomeWrong: "غلط",
    ccOutcomeDnc: "DNC",
    ccWeeklyTrackerTitle: "ہفتہ وار کال ٹریکر",
    ccMenuWeeklyTracker: "ہفتہ وار ٹریکر",
    ccWeeklyTrackerDownloadExcel: "Excel ڈاؤن لوڈ",
    ccWorkflowSettingsTitle: "ٹکٹ اسٹیٹس سیٹنگز",
    ccWorkflowSettingsSubtitle: "اپنے ورک اسپیس کے لیے حسب ضرورت ٹکٹ اسٹیٹس شامل کریں یا بلٹ ان کو دوبارہ نام دیں۔",
    ccAddNewStatus: "نیا اسٹیٹس شامل کریں",
    ccStatusKeyLabel: "اسٹیٹس کی کلید",
    ccStatusKeyPlaceholder: "مثال AWAITING_DOCS",
    ccStatusKeyHint: "APIs اور فلٹرز میں استعمال ہونے والا منفرد کوڈ۔ ڈسپلے نام سے خود بنتا ہے۔",
    ccStatusKeyInvalid: "حروف، نمبر اور انڈر سکور استعمال کریں (حرف سے شروع)۔",
    ccStatusSettingsViewOnly: "آپ یہاں اسٹیٹس دیکھ سکتے ہیں۔ شامل یا ترمیم کے لیے Settings رسائی والے ایڈمن سے رابطہ کریں۔",
    ccStatusManageFromTickets: "محفوظ کرنے کے بعد Tickets صفحے پر یہ اسٹیٹس استعمال کریں۔",
    ccConfiguration: "ترتیب",
    ccBaseMapping: "بنیادی نقشہ",
    ccDisplayName: "ظاہری نام",
    ccDisplayNamePlaceholder: "مثال: گاہک کا انتظار",
    ccColorTheme: "رنگ کی تھیم",
    ccLiveUiPreview: "لائیو UI پیش نظارہ",
    ccPreviewStatus: "پیش نظارہ اسٹیٹس",
    ccPreviewTicketLedger: "ٹکٹ لیجر میں ظاہر ہوگا",
    ccFinalStatus: "حتمی اسٹیٹس",
    ccFinalStatusHint: "اس ٹکٹ کو بند کرنے سے خودکار دوبارہ کوششیں رک جاتی ہیں۔",
    ccEnabled: "فعال",
    ccSaveWorkflowStatus: "ورک فلو اسٹیٹس محفوظ کریں",
    ccActiveStatusLibrary: "فعال اسٹیٹس لائبریری",
    ccSyncStatuses: "اسٹیٹس ہم آہنگ کریں",
    ccNoCustomStatuses: "ابھی تک کوئی حسب ضرورت اسٹیٹس ترتیب نہیں۔",
    ccFinal: "حتمی",
    ccActive: "فعال",
    ccInactive: "غیر فعال",
    ccStatusProTipTitle: "اسٹیٹس پرو-ٹپ",
    ccStatusKeyRequired: "اسٹیٹس کی کلید درکار ہے",
    ccNameRequired: "نام درکار ہے",
    ccInvalidHexColor: "غلط Hex رنگ",
    ccToastStatusSaved: "اسٹیٹس کی ترتیب محفوظ ہو گئی",
    ccToastStatusSaveFailed: "اسٹیٹس محفوظ کرنے میں ناکام",
    ccToastLoadStatusesFailed: "اسٹیٹس لوڈ کرنے میں ناکام",
    ccStatusLibrary: "اسٹیٹس لائبریری",
    ccEditStatus: "اسٹیٹس میں ترمیم کریں",
    ccUpdateWorkflowStatus: "اسٹیٹس اپ ڈیٹ کریں",
    ccCancelEdit: "منسوخ کریں",
    ccStatusKeyLockedHint: "بنانے کے بعد اسٹیٹس کی کلید تبدیل نہیں کی جا سکتی۔",
    ccEdit: "ترمیم",
    ccDelete: "حذف کریں",
    ccActivate: "فعال کریں",
    ccDeactivate: "غیر فعال کریں",
    ccBuiltInStatus: "بلٹ ان",
    ccToastStatusUpdated: "اسٹیٹس اپ ڈیٹ ہو گیا",
    ccToastStatusUpdateFailed: "اسٹیٹس اپ ڈیٹ کرنے میں ناکام",
    ccToastStatusDeleted: "اسٹیٹس حذف ہو گیا",
    ccToastStatusDeleteFailed: "اسٹیٹس حذف کرنے میں ناکام",
    ccConfirmDeleteStatus: "یہ حسب ضرورت اسٹیٹس حذف کریں؟ اسے واپس نہیں کیا جا سکتا۔",
    ccStatusProTipBody:
      'ایسی اسٹیٹس کے لیے اعلیٰ کنٹراسٹ رنگ (جیسے سرخ یا نارنجی) استعمال کریں جن پر ایجنٹ کی فوری توجہ درکار ہو، اور حتمی اسٹیٹس جیسے "مکمل" کے لیے ہلکے رنگ۔',
    ccAckNotePlaceholder: "تسلیم کا نوٹ (اختیاری)",
    ccAcknowledge: "تسلیم کریں",
    ccAcknowledgeAlert: "الرٹ تسلیم کریں",
    ccAlertAckFailed: "الرٹ تسلیم کرنے میں ناکامی",
    ccAlertAcknowledged: "الرٹ تسلیم ہو گیا",
    ccAlertAgent: "ایجنٹ",
    ccAlertDuration: "مدت",
    ccAlertNotificationEscalatedBody: "جواب SLA الرٹ آپ کی ٹیم کو ایسکلیٹ کر دیا گیا۔",
    ccAlertNotificationEscalatedTitle: "ایجنٹ الرٹ ایسکلیٹ ہو گیا",
    ccAlertNotificationWarningBody: "تفویض شدہ ٹکٹ یا مسڈ کال کو توجہ درکار ہے۔",
    ccAlertNotificationWarningTitle: "ایجنٹ جواب SLA انتباہ",
    ccAlertOpenFor: "کھلا ہے",
    ccAlertPending: "زیر التواء",
    ccAlertPriority: "ترجیح",
    ccAlertReason: "وجہ",
    ccAlertReference: "حوالہ",
    ccAlertReportEmpty: "ایکسپورٹ کے لیے رپورٹ ڈیٹا نہیں",
    ccAlertReportFailed: "الرٹ رپورٹ لوڈ کرنے میں ناکامی",
    ccAlertReportTitle: "الرٹ آڈٹ رپورٹ",
    ccAlertResolveFailed: "الرٹ حل کرنے میں ناکامی",
    ccAlertResolved: "الرٹ حل ہو گیا",
    ccAlertResponseTime: "جواب کا وقت",
    ccAlertStatus: "اسٹیٹس",
    ccAlertTimeline: "ایونٹ ٹائم لائن",
    ccAlertTimelineEmpty: "اس الرٹ کے لیے کوئی ایونٹ ریکارڈ نہیں۔",
    ccAlertsAccessDenied: "آپ کو الرٹس مانیٹر تک رسائی نہیں ہے۔",
    ccAlertsEmpty: "موجودہ فلٹرز سے کوئی الرٹ میل نہیں کھاتا۔",
    ccAlertsLoadFailed: "الرٹس لوڈ کرنے میں ناکامی",
    ccAlertsLoading: "الرٹس لوڈ ہو رہے ہیں…",
    ccAlertsPageSubtitle: "SLA انتباہات، ایسکلیشنز، اور ٹیم کے جواب کے اوقات مانیٹر کریں۔",
    ccAlertsPageTitle: "ایجنٹ جواب الرٹس",
    ccAllAgents: "تمام ایجنٹس",
    ccAllStatuses: "تمام اسٹیٹس",
    ccBrowserPhoneFlagDisabled: "براؤزر فون بند ہے۔ بیک اینڈ پر TELEPHONY_BROWSER_PHONE_ENABLED=true سیٹ کریں۔",
    ccBrowserPhoneLinkusConnectionFailed: "D2C Cloud PBX سے منسلک نہیں ہو سکا۔ انٹرنیٹ کنکشن چیک کریں، صفحہ ریفریش کریں، اور دوبارہ کوشش کریں۔",
    ccBrowserPhoneMissingEmail: "براؤزر فون کو D2C PBX ایکسٹینشن سے ملانے کے لیے آپ کا ای میل درکار ہے۔ ایڈمن سے اپنا ای میل سیٹ کروائیں۔",
    ccBrowserPhoneMissingExtension: "براؤزر فون کو PBX ایکسٹینشن درکار ہے۔ ایڈمن سے اپنے پروفائل پر ایجنٹ ایکسٹینشن سیٹ کروائیں۔",
    ccBrowserPhoneNotEnabled: "براؤزر فون دستیاب نہیں۔ اپنا ایجنٹ ایکسٹینشن، ای میل، اور D2C Linkus SDK credentials ترتیب دیں۔",
    ccBrowserPhonePbxCredentialsRejected: "D2C نے Linkus SDK Access ID/Key مسترد کر دیے۔ PBX → Integrations → Linkus SDK میں دوبارہ بنائیں، D2C_PBX_ACCESS_ID / D2C_PBX_ACCESS_KEY اپ ڈیٹ کریں، اور بیک اینڈ ری اسٹارٹ کریں۔",
    ccBrowserPhonePbxIpBlocked: "D2C PBX نے OpenAPI کے لیے اس براؤزر IP کو بلاک کر دیا (errcode 70004)۔ D2C سے اپنا عوامی IP whitelist کروائیں۔",
    ccBrowserPhonePbxSslError: "براؤزر D2C PBX HTTPS سرٹیفکیٹ کی تصدیق نہیں کر سکا۔ نیٹ ورک/پراکسی سیٹنگز چیک کریں یا D2C سے رابطہ کریں۔",
    ccBrowserPhoneSdkNotConfigured: "سرور پر D2C Linkus SDK credentials غائب ہیں۔ D2C_PBX_ACCESS_ID اور D2C_PBX_ACCESS_KEY سیٹ کریں، پھر بیک اینڈ ری اسٹارٹ کریں۔",
    ccCallingDisabled: "اس تنظیم کے لیے کالنگ غیر فعال ہے۔",
    ccCancel: "منسوخ کریں",
    ccConfirmDialogTitle: "کارروائی کی تصدیق",
    ccConfirmDialogProceed: "آگے بڑھیں",
    ccConfirmDialogConfirm: "تصدیق کریں",
    ccDownloadLinkusDesktop: "Linkus Desktop ڈاؤن لوڈ کریں",
    ccDownloadLinkusMobile: "Linkus Mobile ڈاؤن لوڈ کریں",
    ccDuplicateUploadMappingTitle: "یہ ہیڈرز ایک ہی ٹکٹ فیلڈ سے منسلک ہیں:",
    ccExportCsv: "CSV ایکسپورٹ",
    ccHideTimeline: "ٹائم لائن چھپائیں",
    ccLinkusExtensionLabel: "آپ کا ایکسٹینشن",
    ccLinkusSoftphoneHint: "کالز سرور کے ذریعے کی جاتی ہیں۔ click-to-call اور آنے والی کالوں کا جواب دینے کے لیے Linkus Desktop، Mobile، یا Web Client میں سائن ان رہیں۔",
    ccLinkusSoftphoneTitle: "Linkus پر جواب دیں",
    ccLoadReport: "رپورٹ لوڈ کریں",
    ccMenuAgentAlerts: "ایجنٹ الرٹس",
    ccMenuAgentResponseAlerts: "ایجنٹ جواب الرٹس",
    ccMenuPaymentConfirmationSettings: "ادائیگی کی تصدیق",
    ccMenuSettings: "سیٹنگز",
    ccMenuSlaSettings: "جواب SLA",
    ccNoReassign: "دوبارہ تفویض نہ کریں",
    ccNoSupervisor: "کوئی سپروائزر نہیں",
    ccNotificationsSectionUnreadMany: "{count} غیر پڑھے",
    ccNotificationsSectionUnreadOne: "1 غیر پڑھا",
    ccOpenLinkusWebClient: "Linkus Web Client کھولیں",
    ccOperationalAlerts: "آپریشنل الرٹس",
    ccOperationalAlertsSubtitle: "{open} کھلے · {escalated} ایسکلیٹ",
    ccPageOf: "صفحہ {page} از {total}",
    ccPaymentConfirmationEnabled: "اس تنظیم کے لیے ادائیگی کی تصدیق فعال کریں",
    ccPaymentConfirmationFieldMapping: "پے لوڈ فیلڈ میپنگ",
    ccPaymentConfirmationInfoAmount: "رقم",
    ccPaymentConfirmationInfoConfirmedAt: "تصدیق کا وقت",
    ccPaymentConfirmationInfoReference: "حوالہ",
    ccPaymentConfirmationInfoSource: "ماخذ",
    ccPaymentConfirmationInfoTitle: "ادائیگی کی تصدیق ہو گئی",
    ccPaymentConfirmationInfoBadge: "تصدیق شدہ",
    ccPaymentConfirmationSourceWebhook: "Webhook",
    ccPaymentConfirmationSourceRestPoll: "REST poll",
    ccPaymentConfirmationSourceMessageQueue: "Message queue",
    ccSecretConfiguredHint: "پہلے سے ترتیب شدہ — موجودہ قدر رکھنے کے لیے خالی چھوڑیں",
    ccPaymentConfirmationDisableConfirm:
      "ادائیگی کی تصدیق بند کرنے سے خودکار ٹکٹ اپڈیٹ رک جائیں گے۔ جاری رکھیں؟",
    ccPaymentConfirmationProviderChangeConfirm:
      "انٹیگریشن موڈ بدلنے پر آپ کے بیرونی سسٹم میں تبدیلی درکار ہو سکتی ہے۔ جاری رکھیں؟",
    ccPaymentConfirmationInvalidMqJson: "MQ connection JSON درست نہیں",
    ccPaymentConfirmationPollBaseUrlRequired: "فعال ہونے پر poll base URL درکار ہے",
    ccPaymentConfirmationPollBaseUrlInvalid: "درست poll base URL درج کریں (https://...)",
    ccPaymentConfirmationPollPathRequired: "فعال ہونے پر poll path template درکار ہے",
    ccPaymentConfirmationPollIntervalInvalid: "Poll interval 1 سے 1440 منٹ کے درمیان ہونا چاہیے",
    ccPaymentConfirmationWebhookSecretRequired: "فعال ہونے پر webhook secret درکار ہے",
    ccPaymentConfirmationMqConfigRequired: "فعال ہونے پر MQ connection JSON درکار ہے",
    ccPaymentConfirmationFieldAccountNo: "اکاؤنٹ نمبر فیلڈ",
    ccPaymentConfirmationFieldAmount: "رقم فیلڈ",
    ccPaymentConfirmationFieldReference: "حوالہ فیلڈ",
    ccPaymentConfirmationFieldPaidAt: "ادائیگی کی تاریخ فیلڈ",
    ccPaymentConfirmationFieldIsPaid: "ادائیگی کی حالت فیلڈ",
    ccPaymentConfirmationFieldMappingHint:
      "اپنے payment payload کے JSON keys کو call center کی متوقع فیلڈز سے map کریں۔",
    ccPaymentConfirmationPayloadExampleTitle: "Webhook payload کی مثال",
    ccPaymentConfirmationPayloadExampleHint:
      "آپ کا سسٹم اس طرح JSON بھیجے۔ فیلڈ نام اوپر map کیے گئے ہوں تو مختلف ہو سکتے ہیں۔",
    ccPaymentConfirmationCopyUrl: "URL کاپی کریں",
    ccPaymentConfirmationUrlCopied: "URL کلپ بورڈ پر کاپی ہو گیا",
    ccPaymentConfirmationCopyUrlFailed: "URL کاپی نہیں ہو سکا",
    ccPaymentConfirmationEventLogTitle: "حالیہ integration events",
    ccPaymentConfirmationEventLogSubtitle:
      "Payment confirmation attempts کا audit trail — ناکام یا unmatched payments debug کرنے کے لیے.",
    ccPaymentConfirmationEventLogEmpty: "ابھی تک کوئی payment confirmation event نہیں",
    ccPaymentConfirmationEventLogEmptyHint:
      "جب آپ کا سسٹم payment notifications بھیجے یا polling چلے تو events یہاں دکھائی دیں گے.",
    ccPaymentConfirmationEventLogLoadFailed: "Payment confirmation events لوڈ کرنے میں ناکام",
    ccPaymentConfirmationEventProcessed: "لاگو",
    ccPaymentConfirmationEventFailed: "ناکام",
    ccPaymentConfirmationEventSkipped: "چھوڑ دیا",
    ccPaymentConfirmationEventAccountNo: "اکاؤنٹ نمبر",
    ccPaymentConfirmationEventReference: "حوالہ",
    ccPaymentConfirmationEventProvider: "Provider",
    ccPaymentConfirmationEventCreatedAt: "موصول وقت",
    ccPaymentConfirmationEventError: "تفصیل",
    ccPaymentConfirmationEventResultsLabel: "events",
    ccPaymentConfirmationLoadFailed: "ادائیگی کی تصدیق کی سیٹنگز لوڈ کرنے میں ناکامی",
    ccPaymentConfirmationMqBrokerType: "میسج بروکر کی قسم",
    ccPaymentConfirmationMqConfigHint: "host، port، username، password، virtualHost، اور queue JSON میں فراہم کریں۔",
    ccPaymentConfirmationMqConfigJson: "MQ کنکشن JSON",
    ccPaymentConfirmationPollAuthSecret: "پول auth secret",
    ccPaymentConfirmationPollAuthType: "پول auth کی قسم",
    ccPaymentConfirmationPollBaseUrl: "پول API base URL",
    ccPaymentConfirmationPollInterval: "پول وقفہ (منٹ)",
    ccPaymentConfirmationPollPathTemplate: "پول path template",
    ccPaymentConfirmationProvider: "انٹیگریشن موڈ",
    ccPaymentConfirmationProviderMessageQueue: "میسج کیو (تنظیم کا بروکر)",
    ccPaymentConfirmationProviderRestPoll: "REST API poll (ہم اکاؤنٹ نمبر سے org API چیک کرتے ہیں)",
    ccPaymentConfirmationProviderWebhook: "Webhook (تنظیم ادائیگی کے ایونٹس بھیجتی ہے)",
    ccPaymentConfirmationSave: "ادائیگی کی تصدیق کی سیٹنگز محفوظ کریں",
    ccPaymentConfirmationSaveFailed: "ادائیگی کی تصدیق کی سیٹنگز محفوظ کرنے میں ناکامی",
    ccPaymentConfirmationSaveSuccess: "ادائیگی کی تصدیق کی سیٹنگز محفوظ ہو گئیں",
    ccPaymentConfirmationSettingsSubtitle: "ترتیب دیں کہ آپ کی تنظیم کسٹمرز کے ادائیگی پر کال سینٹر کو کیسے مطلع کرتی ہے (webhook، REST poll، یا message queue)۔",
    ccPaymentConfirmationSettingsTitle: "ادائیگی کی تصدیق",
    ccPaymentConfirmationTestConnection: "کنکشن ٹیسٹ",
    ccPaymentConfirmationTestFailed: "کنکشن ٹیسٹ ناکام",
    ccPaymentConfirmationTestSuccess: "کنکشن ٹیسٹ کامیاب",
    ccPaymentConfirmationWebhookHint: "کسٹمر کے ادائیگی پر اکاؤنٹ نمبر اور ادائیگی کی تفصیلات کے ساتھ signed JSON POST کریں۔",
    ccPaymentConfirmationWebhookSecret: "Webhook secret",
    ccPaymentConfirmationWebhookUrlLabel: "یہ webhook URL اپنی ادائیگی کے نظام میں رجسٹر کریں",
    ccPhoneOptionalPlaceholder: "اختیاری",
    ccReassignToAgent: "ٹکٹ دوبارہ تفویض کریں",
    ccRefresh: "تازہ کریں",
    ccReportAcknowledged: "تسلیم شدہ",
    ccReportAutoResolved: "خودکار حل",
    ccReportCreated: "بنایا گیا",
    ccReportEscalatedL1: "L1 ایسکلیٹ",
    ccReportEscalatedL2: "L2 ایسکلیٹ",
    ccReportResolved: "حل شدہ",
    ccResolutionNotePlaceholder: "حل کا نوٹ (اختیاری)",
    ccResolve: "حل کریں",
    ccResolveAlert: "الرٹ حل کریں",
    ccSaveSlaSettings: "SLA سیٹنگز محفوظ کریں",
    ccSlaAckGraceMinutes: "تسلیم کی مہلت (منٹ)",
    ccSlaAckGraceNote: "L2 سے پہلے +{duration} تسلیم کی مہلت",
    ccSlaCopiedFrom: "{source} سے thresholds کاپی ہو گئے",
    ccSlaCopyFrom: "سے کاپی کریں",
    ccSlaCopyFromPlaceholder: "قسم منتخب کریں",
    ccSlaDescriptionColumn: "تفصیل",
    ccSlaDisabledTimeline: "جب یہ الرٹ قسم غیر فعال ہو تو ایسکلیشن روکی ہے۔",
    ccSlaDurationColumn: "مدت",
    ccSlaEnabled: "فعال",
    ccSlaEscalationPreview: "ایسکلیشن ٹائم لائن",
    ccSlaImmediate: "فوری",
    ccSlaL1Minutes: "L1 ایسکلیشن (منٹ)",
    ccSlaL2Minutes: "L2 ایسکلیشن (منٹ)",
    ccSlaPriorityHint: "اہم اور اعلیٰ ترجیحی الرٹس تیزی سے ایسکلیٹ ہوتے ہیں۔ نئی الرٹس اور ایسکلیشن روکنے کے لیے قسم غیر فعال کریں۔",
    ccSlaResetDefaults: "ڈیفالٹ ری سیٹ",
    ccSlaResetToDefaults: "ڈیفالٹ thresholds پر ری سیٹ",
    ccSlaSaveHint: "محفوظ کرنے پر تمام الرٹ اقسام پر تبدیلیاں لاگو ہوتی ہیں۔",
    ccSlaSettingsLink: "SLA سیٹنگز",
    ccSlaSettingsLoadFailed: "SLA سیٹنگز لوڈ کرنے میں ناکامی",
    ccSlaSettingsLoading: "SLA سیٹنگز لوڈ ہو رہی ہیں…",
    ccSlaSettingsPageSubtitle: "تفویض شدہ ٹکٹس اور مسڈ کالز کے لیے انتباہ اور ایسکلیشن ٹائمرز ترتیب دیں۔",
    ccSlaSettingsPageTitle: "جواب SLA",
    ccSlaSettingsSaveFailed: "SLA سیٹنگز محفوظ کرنے میں ناکامی",
    ccSlaSettingsSaved: "SLA سیٹنگز محفوظ ہو گئیں",
    ccSlaStatusOff: "بند",
    ccSlaStatusOn: "چالو",
    ccSlaStepPending: "تفویض شدہ",
    ccSlaThresholdColumn: "حد",
    ccSlaThresholdsSubtitle: "ہر الرٹ قسم کے لیے انتباہ اور ایسکلیشن ٹائمرز ترتیب دیں۔ وقت منٹ، گھنٹے، یا دن میں ہو سکتا ہے۔",
    ccSlaThresholdsTitle: "جواب SLA thresholds",
    ccSlaTypeColumn: "قسم",
    ccSlaUnitDay: "دن",
    ccSlaUnitDayShort: "د",
    ccSlaUnitDays: "دن",
    ccSlaUnitHour: "گھنٹہ",
    ccSlaUnitHourShort: "گ",
    ccSlaUnitHours: "گھنٹے",
    ccSlaUnitMinute: "منٹ",
    ccSlaUnitMinuteShort: "م",
    ccSlaUnitMinutes: "منٹ",
    ccSlaWarningMinutes: "انتباہ (منٹ)",
    ccSlaItemType_TICKET: "ٹکٹ",
    ccSlaItemType_MISSED_CALL: "مسڈ کال",
    ccSlaItemType_BULK_ASSIGNMENT: "بلک تفویض",
    ccSlaItemDesc_TICKET: "نئی تفویض شدہ یا کال بیک درکار ٹکٹس کے لیے جواب SLA۔",
    ccSlaItemDesc_MISSED_CALL: "مسڈ ان باؤنڈ کال کے بعد کال بیک SLA۔",
    ccSlaItemDesc_BULK_ASSIGNMENT: "بڑے بلک ٹکٹ اپ لوڈ کے بعد کام شروع کرنے کا SLA۔",
    ccSlaField_warning: "انتباہ",
    ccSlaField_escalationL1: "ایسکلیشن L1",
    ccSlaField_escalationL2: "ایسکلیشن L2",
    ccSlaField_acknowledgementGrace: "تسلیم کی مہلت",
    ccSlaFieldDesc_warning: "الرٹ کے انتباہی حالت میں داخل ہونے سے پہلے کا وقت",
    ccSlaFieldDesc_escalationL1: "ایجنٹ کے سپروائزر تک ایسکلیشن",
    ccSlaFieldDesc_escalationL2: "کال سینٹر منتظمین تک ایسکلیشن",
    ccSlaFieldDesc_acknowledgementGrace: "L2 ایسکلیشن سے پہلے تسلیم کے بعد مہلت",
    ccSlaUnitLabel_MINUTES: "منٹ",
    ccSlaUnitLabel_HOURS: "گھنٹے",
    ccSlaUnitLabel_DAYS: "دن",
    ccSupervisor: "سپروائزر",
    ccSupervisorMappingSubtitle: "ہر کال ایجنٹ کو L1 ایسکلیشن کے لیے سپروائزر سے منسلک کریں۔",
    ccSupervisorMappingTitle: "سپروائزر میپنگ",
    ccToastDuplicateUploadMapping: "ہر ٹکٹ فیلڈ صرف ایک اپ لوڈ کالم سے میپ ہو سکتی ہے۔",
    ccUploadColumnMapping: "کالم میپنگ",
    ccUploadKeepAsNew: "نئے فیلڈ کے طور پر رکھیں:",
    ccUploadMappedTo: "منسلک:",
    ccViewAllAlerts: "تمام الرٹس دیکھیں",
    ccViewTimeline: "ٹائم لائن",
    ccWeeklyTrackerAgents: "کال ایجنٹس",
    ccWeeklyTrackerApplyHint: "ایجنٹس منتخب کریں، پھر ڈیٹا لوڈ کرنے کے لیے ڈیش بورڈ سنک پر کلک کریں۔",
    ccWeeklyTrackerCallType: "کال کی قسم (اسٹیٹس)",
    ccWeeklyTrackerCallsCompleted: "مکمل کالز",
    ccWeeklyTrackerCallsThisWeek: "اس ہفتے کی کالز",
    ccWeeklyTrackerClearAll: "صاف کریں",
    ccWeeklyTrackerCombinedCalls: "مجموعی کالز (تمام ایجنٹس)",
    ccWeeklyTrackerCommitments: "درج شدہ وعدے",
    ccWeeklyTrackerDailyCalls: "روزانہ کالز",
    ccWeeklyTrackerExportFailed: "Excel رپورٹ ڈاؤن لوڈ کرنے میں ناکامی",
    ccWeeklyTrackerExportSuccess: "ہفتہ وار ٹریکر Excel ڈاؤن لوڈ ہو گیا",
    ccWeeklyTrackerExporting: "ایکسپورٹ ہو رہا ہے…",
    ccWeeklyTrackerFollowupsDone: "مکمل فالو اپس",
    ccWeeklyTrackerKpiCollected: "وصول شدہ رقم (OMR)",
    ccWeeklyTrackerKpiMissed: "چھوٹے ہوئے فالو اپس",
    ccWeeklyTrackerKpiPromise: "ادائیگی کا وعدہ پورا",
    ccWeeklyTrackerLoadFailed: "ہفتہ وار ٹریکر لوڈ کرنے میں ناکامی",
    ccWeeklyTrackerNeedsAttention: "توجہ درکار",
    ccWeeklyTrackerNoData: "منتخب فلٹرز کے لیے ہفتہ وار ٹریکر ڈیٹا نہیں۔",
    ccWeeklyTrackerQuickDesc: "اسٹیٹس کے لحاظ سے ایجنٹ کے اہداف Excel ایکسپورٹ کے ساتھ",
    ccWeeklyTrackerRecoveredThisWeek: "اس ہفتے وصول شدہ",
    ccWeeklyTrackerSearchAgents: "ایجنٹس تلاش کریں اور منتخب کریں…",
    ccWeeklyTrackerSelectAgents: "کم از کم ایک کال ایجنٹ منتخب کریں۔",
    ccWeeklyTrackerSelectAll: "سب منتخب کریں",
    ccWeeklyTrackerSelectedCount: "{total} میں سے {count} ایجنٹ منتخب",
    ccWeeklyTrackerSubtitle: "منتخب ایجنٹس کے لیے ٹکٹ اسٹیٹس (کال کی قسم) کے لحاظ سے روزانہ کال کی تعداد۔ اتوار–جمعرات کام کا ہفتہ۔",
    ccWeeklyTrackerTargetCallsPlaced: "اس ہفتے {target} میں سے {done} ہدف کالز",
    ccWeeklyTrackerWeekOf: "ہفتہ (اتوار)",
    ccWeeklyTrackerWeeklyTarget: "ہفتہ وار ہدف",
    ccWeeklyTrackerWeeklyTotal: "ہفتہ وار کل",
  },
};

export const getCallCenterT = (langMode = "en") => {
  const bundle = callCenterTranslations[langMode] || callCenterTranslations.en;
  return (key) => {
    const value = bundle[key];
    if (value) return value;
    return callCenterTranslations.en[key] || key;
  };
};

export default callCenterTranslations;
